import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { combineLatest, forkJoin, Subscription } from "rxjs";
import { switchMap } from "rxjs/operators";
import { DashboardService } from "src/app/services/dashboard.service";
import { NavigationService } from "src/app/services/navigation.service";
import { SessionService } from "src/app/services/session.service";
import { FilterService } from "src/app/services/filter.service";
import { UserActivityService } from "src/app/services/useractivity.service";
import { NoDataNotifierService } from "src/app/services/no-data-notifier.service";
// Reuse the real status-overview popups so the static screen has the exact same
// single-click (comments) and double-click (control chart) behaviour. The DEM
// variants, not DPM's: they render <app-status-cell-info-dem>, whose panel names
// the extract monitor's own counts (sent / failures / errors / on hold) where
// DPM's names received / success / failures.
//
// The double click opens the detail page itself, through PagePopupComponent —
// imported where it is used (openControlChart) rather than here, because it pulls
// in WidgetModule. See that method.
import { Modal as DemCommentModal } from "../../../widget/status-overview/dem/modal";
import {
  AngularResizeElementDirection,
  AngularResizeElementEvent,
} from "../../../../directives/resize-element";
import { AppCustomDatepickerHeaderComponent } from "../../../shared/custom-input-type/timecycle-with-range/app-custom-datepicker-header";
import moment from "moment";
import { CellClickedEvent, ColDef } from "ag-grid-community";
import {
  DEM_DETAIL_PAGE,
  DEM_DYNAMIC_FILTERS,
  DEM_EXECUTION_API_KEY,
  DPM_REPORT_TYP,
  DpmDynamicFilterDef,
  DpmDynamicFilterState,
  DpmFilterOption,
  DpmStaticContext,
  DpmStaticService,
  dynamicFilterSnapshot,
  HelpEntry,
  isDetailPageConfigured,
  makeHelpLookup,
  sortFilterValues,
  StaticBookmarkFilter,
  StaticBookmarkState,
  StaticTreeOrder,
  // Cross-filtering: the Qlik/Power BI style visual interaction both static
  // screens share. The model is in the service so the two cannot drift; the
  // column names behind each kind are this screen's and stay here — see
  // selectionTest().
  combineSelections,
  pathMatches,
  StaticPendingSelection,
  StaticSelection,

} from "../dpm-static/dpm-static.service";
// This screen's per-widget help text. Imported as a module rather than fetched: it
// is static content that ships with the build, so there is no request to fail and no
// loading state to hold. resolveJsonModule is on for exactly this — see
// icon.service.ts, which reads its icon manifests the same way.
import HELP_INFO_JSON from "src/assets/help_info/dem-static.json";
import { consumeStaticDashboardEntry } from "../static-dashboard-entry";
import { fitTier } from "../short-count";
// ===========================================================================
// DEM-Static — Data Extract Monitor
//
// The static twin of the dynamic Data Extract Monitor widget set, built on
// DPM-Static's structure deliberately: same filter bar, same date machinery, same
// Live-strip / History-calendar heat map, same shared <icon-chart> headers, same
// ag-Grid panel underneath. A user moving between the two screens should not have
// to relearn either, and a fix to one shape is a fix to a shape the other also
// has.
//
// What differs is the data, and it differs all the way down: the extract feed
// names four statuses where DPM names five, counts `sent_records` where DPM counts
// `no_of_records`, badges on a different set of flags (three of which it derives
// rather than reads — see stampTrends), and builds a two-level tree where DPM
// builds five. Every one of those decisions below is taken from the live widget's
// own config (status-overview/dem/config.ts) rather than invented, and says so, so
// a static tile can never grade a record differently from the real one.
// ===========================================================================

/**
 * Status palette for the KPI cards, the donut, the heat cells, the heat legend
 * and the grid's header dots — so every label on this screen carries the same
 * colour as the count behind it.
 *
 * `failed` / `inProcess` / `sent` are dem/config.ts's `colorScheme`
 * (['#D11044', '#645DD7', '#3AC97C']) verbatim, so a tile here is the colour the
 * live widget would have painted it.
 *
 * `notStarted` has no entry there — the library falls through to its own neutral
 * for a status the scheme does not cover — so it is the one colour in this file
 * matched off the rendered screen rather than copied from config. `delayed` and
 * `expected` are not file statuses at all: they colour KPI counts, and are the
 * amber and teal the reference cards use.
 *
 * The $sent…$not-started SCSS vars mirror these for the parts that can only be
 * expressed in CSS.
 */
const COLORS = {
  failed: "#d11044",
  inProcess: "#645dd7",
  sent: "#3ac97c",
  notStarted: "#94abb8",
  delayed: "#ffcd4a",
  expected: "#2bbcd1",
};

/**
 * Colour bands for the grid's Processing Status column.
 *
 * Taken from the production widget config (DPM_S3_History_expected_dt.json),
 * so the static screen grades a percentage exactly the way the real dashboard
 * does. Deliberately not COLORS: that palette names file statuses, and these
 * are thresholds on a rate — the two agree on the red and part ways on the
 * other two, which is the config's call to make, not the palette's.
 *
 * Bands are inclusive at both ends and read in order, so they must stay sorted
 * and non-overlapping.
 */
const PCT_COLOR_BANDS: { color: string; start: number; end: number }[] = [
  { color: "#d11044", start: 0, end: 74.99 },
  { color: "#2bbcd1", start: 75, end: 99.99 },
  { color: "#66b366", start: 100, end: 100 },
];

// Status legend entries, in the order the reference dashboard renders them
// (NOT STARTED, FAILED, IN PROCESS, SENT). `label` doubles as the cell `status`
// key, which is what lets a legend click filter cells without a separate lookup
// table.
//
// Failed carries no rows in today's feed; it stays so the legend still explains
// the colour if the data grows one.
const STATUS_LEGEND: { label: string; color: string }[] = [
  { label: "Not Started", color: COLORS.notStarted },
  { label: "Failed", color: COLORS.failed },
  { label: "In Process", color: COLORS.inProcess },
  { label: "Sent", color: COLORS.sent },
];

// Statuses drawn as an outlined "nothing here" slot instead of a solid fill.
//
// Empty for the extract monitor, deliberately: every status it reports is a real
// state a file reached, including Not Started — which the reference paints as a
// solid grey tile carrying 0 rather than as an outline. DPM's equivalent list
// holds Expected, which is that screen's "this day was due and nothing came"
// placeholder; the extract feed has no such row. Kept as a named list rather than
// deleted so statusFill() stays the one place a fill is decided.
const OUTLINED_STATUSES: string[] = [];

// Statuses that carry no sent records, so a count would always read 0.
//
// Also empty, and for a related reason: dem/config.ts's cellValueAccessor prints
// a value for every cell it is given, and a Not Started record's null
// `sent_records` sums to 0 — which is the 0 the reference shows on those tiles.
// Suppressing it here would blank a number the real screen prints.
const STATUSES_WITHOUT_COUNT: string[] = [];

/**
 * The JSON does not always spell a status the way the legend does, and every
 * lookup downstream keys off that label: the fill, the priority table, the
 * legend's hidden set, the pale-text list. Left alone, a differently-cased cell
 * renders as an empty slot and its legend entry does nothing.
 *
 * So resolve a raw value to the legend's spelling once, where a cell picks its
 * status, and everything after it agrees. An unrecognised status passes through
 * untouched rather than being dropped.
 */
const STATUS_BY_KEY = new Map(
  STATUS_LEGEND.map((s) => [s.label.toLowerCase(), s.label])
);
const canonicalStatus = (status: string): string =>
  STATUS_BY_KEY.get(status.trim().toLowerCase()) || status;

/** Cell fill for a status. '' for outlined and unrecognised statuses, which is
 *  the signal callers use to render the empty cell state. */
const statusFill = (status: string): string =>
  OUTLINED_STATUSES.includes(status)
    ? ""
    : STATUS_LEGEND.find((s) => s.label === status)?.color || "";

// Priority used to collapse many files on a (target, day) into one cell status.
// Ordered by how much it wants attention rather than by lifecycle: a branch with
// one failure among fifty sent files is a failure first.
const STATUS_PRIORITY: Record<string, number> = {
  Failed: 5,
  "In Process": 4,
  "Not Started": 3,
  Sent: 2,
};

interface CellBadge {
  name: string;
  url: string;
  /** The legend's wording for this indicator, named in the overflow popover. */
  label: string;
}

/**
 * One status band inside a tile. A (node, day) can hold records of several
 * statuses at once — a parent row aggregates its whole branch, and even a leaf
 * can have two files land differently on the same day — so the tile is divided
 * into one band per status present rather than collapsing to the single
 * highest-priority one. That is what lets a parent tile say "some of this
 * branch processed, some is missing" at a glance, without expanding it.
 *
 * Bands are equal width: they answer "which statuses are here", not "how much
 * of each", and the exact per-status counts are printed on the leaf tiles and
 * spelled out in the hover panel.
 */
interface CellSegment {
  status: string;
  /** '' for outlined statuses (Expected), which renders as an empty band. */
  color: string;
  count: number;
  /** Pre-formatted count ('' when the status carries no records). */
  countLabel: string;
  /** True when the fill is too pale to carry white text, so the count flips to
   *  ink. Decided at build time from the status rather than sniffed from the
   *  serialised inline style, which varies by browser. */
  onPale: boolean;
  /**
   * Badge icons for THIS status's records only — defect / notes / delay …
   *
   * Indicators are a property of a file status, not of a day: a tile holding a
   * delayed file and a sent one carries its DELAY badge because of the late one,
   * so the badge belongs on that status's band. Drawn per band for that reason, in
   * both views.
   *
   * INTERIM is not here. It is the tile's marching outline rather than a corner
   * badge, and it wraps the whole tile — see HeatCell.interim.
   */
  indicators: CellBadge[];
  /** `indicators` minus whatever the indicator legend has switched off. Stamped
   *  by applyLegendVisibility() so the template renders a ready-made array
   *  instead of filtering per band on every change-detection pass. */
  shownIndicators: CellBadge[];
  /** This band's own records — the bucket cellSegments() grouped it from.
   *  Held so a cross-highlight can ask whether the band has anything the
   *  highlighted selection matches; nothing else reads it. */
  rows: any[];
  /** Dimmed by an active cross-highlight: the band holds records, and none of
   *  them match what is highlighted. Stamped by applyHighlight() rather than
   *  tested in the template, for the same reason shownIndicators is. */
  dim: boolean;
}

interface HeatCell {
  /** The cell's dominant status (highest priority present). Still what the
   *  popups and the hover panel key off — the bands are how it is drawn. */
  status: string;
  color: string;
  count: number;
  iso: string; // ISO day this cell belongs to (drives the popup xKey)
  rows: any[]; // underlying JSON records for this (node, day) — feeds the popups
  // Corner badges are NOT held here: an indicator belongs to a file status, so
  // each band carries its own set — see CellSegment.indicators.
  //
  // INTERIM is the exception, and stays on the cell: dem/config.ts marks it with a
  // border on the tile (ducharts' borderBadgeAccessor -> .borderInd) rather than a
  // corner icon, and that outline wraps the whole tile — an interim extract is a
  // second delivery of the day's file, a property of the cell's records as a whole
  // rather than of one band inside it.
  interim: boolean;
  // `interim` after the indicator legend has had its say. The reference legend
  // offers no INTERIM entry, so today this only ever mirrors `interim` — kept as
  // its own field so adding that entry needs no change here.
  showInterim: boolean;
  // Every status present in this cell, in legend order — each band with its own
  // count and its own badges. Computed once at build time so the template never
  // runs a pipe or method per tile.
  segments: CellSegment[];
  // `segments` minus whatever the status legend has switched off — what the
  // tile actually draws. Stamped by applyLegendVisibility().
  shownSegments: CellSegment[];
  // Set by applyLegendVisibility() when every status this cell holds is
  // switched off in the status legend — the tile then renders as an empty slot,
  // matching the Status Overview widget, where a disabled legend entry drops
  // those records out of the cell entirely. A cell that simply has no records
  // is not "hidden"; it is an empty slot to begin with.
  hidden: boolean;
  // Dimmed by an active cross-highlight — the whole tile, when nothing in it
  // matches. Distinct from every band being dimmed: a tile can hold records
  // whose status the legend has switched off, so the two counts differ.
  // Stamped by applyHighlight().
  dim: boolean;
}

/**
 * One arc of the donut ring. The ring is drawn as concentric arcs — one per
 * series — so the circle itself says how much was expected and how much of it
 * was sent, instead of encoding a single share.
 *
 * `r` is in the SVG's own 90x90 user space, and the arcs are spaced so their
 * 7-wide strokes clear each other: the outer band covers 28.5..35.5 from the
 * centre and the inner 18.5..25.5, leaving a 3-unit gutter between them and a
 * hole in the middle the same size as the reference card's.
 *
 * This is DPM's geometry, and deliberately so. Both series used to share ONE
 * band — Expected as the track with Sent painted straight over it, same r, same
 * width — so two saturated legend colours occupied the same annulus and the ring
 * read as two arcs stacked on each other rather than as one clean band. The
 * hover made it plain: growing whichever part was hovered to stroke-width 9 bled
 * it out past the other on both edges, inside the same band. Concentric bands on
 * their own grey tracks cannot do that.
 */
interface DonutArc {
  /** Series name; also the arc's native tooltip. */
  name: string;
  color: string;
  r: number;
  /**
   * This arc's share as a stroke-dasharray (drawn length first, then a gap of
   * the whole circumference so the pattern cannot repeat) — or null at a full
   * share, which is drawn as a plain uninterrupted circle instead.
   *
   * The null case is not an optimisation. A dash equal to the circumference
   * still begins and ends at the same point, and with stroke-linecap: round the
   * two caps meet there over a rounding-error gap and leave a visible seam — so
   * a ring at 100% read as "nearly complete" instead of complete. Dropping the
   * attribute closes it.
   */
  dasharray: string | null;
  /** Hover label: the series and its value, e.g. "Sent: 28". */
  tooltip: string;
  /** What clicking this band cross-filters by, or null for a band that is the
   *  whole rather than a share of it — see DONUT_SEL. */
  sel: StaticSelection | null;
}

// Ring geometry, outermost first. Two entries, because the ring plots Sent and
// Expected; a third series would need another radius here (and 10 units of
// room, which the 90-unit viewBox has).
const DONUT_RADII = [32, 22];

// ------------------------------------------------------- cross-filter sources
/**
 * A selection on a record flag — the 'Y'/'N' columns the cards count.
 *
 * Its own group per column, because two flags are two independent questions: a
 * file can be both delayed and an interim delivery, so picking Delayed and then
 * Interim should narrow to the files that are both, not widen to either. Statuses
 * share one group for the opposite reason — a file has exactly one, so picking
 * two can only ever mean "either".
 */
const flagSel = (
  column: string,
  label: string,
  color: string
): StaticSelection => ({
  id: `flag:${column}`,
  group: `flag:${column}`,
  kind: "flag",
  value: column,
  // No chip is keyed by a flag column, so these are the selections that stay
  // standalone chips in the strip — see StaticSelection.field.
  field: column,
  label,
  color,
});

/** A selection on a file status — see flagSel for why these share a group. */
const statusSel = (status: string, color: string): StaticSelection => ({
  id: `status:${status}`,
  group: "status",
  kind: "status",
  value: status,
  // The Status chip's key, so clicking a status figure narrows that badge
  // instead of adding a chip beside it.
  field: "file_status",
  label: status,
  color,
});

/**
 * The KPI cards this screen always shows, chrome first — heading, icon, colour
 * and the sub-line's label are constant, so the row renders its cards with a
 * loader in place of each number before any data has arrived and buildKpis() only
 * fills in values. Nothing shifts when they land.
 *
 * Two entries, not DPM's four: the extract monitor's remaining two cards are not
 * heading-and-number at all. Extracts is three counts side by side and the donut
 * is a ring, so both have markup of their own in the template rather than being
 * bent into this shape.
 *
 * `key` is what buildKpis() looks its value up by — a name rather than an array
 * position, so reordering the row cannot silently swap two numbers.
 */
const KPI_CARD_DEFS: {
  key: "delayed" | "inProcess";
  heading: string;
  icon: string;
  color: string;
  /** Sub-line caption, or '' for a card that has no second line. */
  subHeading: string;
  /**
   * The `info_header` this card's Help icon answers with, out of
   * assets/help_info/dem-static.json.
   *
   * Named rather than derived from `heading`: it happens to match on both of these
   * cards, but an entry describes a card as a whole — the In Process one covers its
   * Interim sub-line too — so which entry belongs to which card is the JSON's call
   * and not something a string transform should be guessing at.
   */
  help: string;
  /**
   * The cross-filter this card's headline figure stands for — ctrl/cmd+click picks
   * it, which highlights it across the page, and the popup's Apply commits it as a
   * filter chip.
   *
   * Named per card rather than derived from `heading`, because the two cards are
   * not the same kind of measure: In Process is a file STATUS, Delayed is a FLAG
   * that cuts across the statuses (a delayed file is also a sent one). That
   * distinction is the whole reason aggregate() counts them separately, and it is
   * what selectionTest() needs in order to match the right records.
   */
  sel: StaticSelection;
  /** The same for the sub-line's figure, where it names a different set. Null on
   *  a card with no second line. */
  subSel: StaticSelection | null;
}[] = [
  {
    key: "delayed",
    heading: "Delayed",
    icon: "question_circle",
    color: COLORS.delayed,
    subHeading: "",
    help: "Delayed",
    sel: flagSel("delay_ind", "Delayed", COLORS.delayed),
    subSel: null,
  },
  {
    key: "inProcess",
    heading: "In Process",
    icon: "refresh_circle",
    color: COLORS.inProcess,
    subHeading: "Interim",
    help: "In Process",
    sel: statusSel("In Process", COLORS.inProcess),
    subSel: flagSel("interim_extract_flg", "Interim", COLORS.inProcess),
  },
];

/**
 * The Extracts card's three counts as cross-filter sources.
 *
 * Expected is deliberately absent: it is every row in the current view (see
 * aggregate(), where `expected` is simply rows.length), so selecting it would
 * narrow the screen to what it already shows. It stays a plain figure — the
 * denominator the other two are read against — rather than a click that appears
 * to do nothing.
 */
const EXTRACTS_SEL = {
  sent: statusSel("Sent", COLORS.sent),
  interim: flagSel("interim_extract_flg", "Interim Sent", COLORS.sent),
};

/**
 * The donut's clickable series.
 *
 * Only Sent, for the same reason the Extracts card offers no Expected: the inner
 * band IS the whole, so there is nothing to narrow to. The band still draws and
 * still answers its hover tooltip; it just is not a filter source.
 */
const DONUT_SEL: Record<string, StaticSelection> = {
  Sent: statusSel("Sent", COLORS.sent),
};

/**
 * The donut LEGEND's clickable series, which is a longer list than the ring's.
 *
 * The legend reports four figures where the ring draws two bands — In Process and
 * Delayed have no arc, but they are real measures of the same dataset, and a
 * legend entry is as good a place to select one from as a KPI card. Selecting
 * either here produces the very selection its card produces, so the two light up
 * together.
 *
 * Expected is absent, as it is from the ring: it is every row in view.
 */
const DONUT_LEGEND_SEL: Record<string, StaticSelection> = {
  ...DONUT_SEL,
  "In Process": statusSel("In Process", COLORS.inProcess),
  Delayed: flagSel("delay_ind", "Delayed", COLORS.delayed),
};

// -------------------------------------------------------------------- help text
/** This screen's help lookup — see makeHelpLookup() in dpm-static.service.ts. */
const helpFor = makeHelpLookup(HELP_INFO_JSON as HelpEntry[]);

/**
 * The cards with each `help` header resolved to its entry, once, at module load —
 * so `help` is the popover's payload everywhere downstream rather than a name still
 * to be looked up.
 *
 * Resolved HERE rather than where the cards are rendered because both the initial
 * seed of `kpis` and every rebuild in buildKpis() map straight off this array: a
 * lookup at either of those sites is one the other can forget, and the one that
 * forgot would hand the template a string where it expects an entry.
 */
const KPI_CARDS = KPI_CARD_DEFS.map((c) => ({ ...c, help: helpFor(c.help) }));

// Statuses whose fill is too light for white text.
//
// Empty: the only candidate was Not Started, whose 0 then printed in ink while
// every other tile's count printed white — one tile in a row reading a different
// colour from its neighbours for a reason nothing on screen explains. #94abb8 is
// dark enough to carry white text, so the count is left the same colour on every
// tile. Kept as a named list so a genuinely pale fill has somewhere to go.
const PALE_STATUSES: string[] = [];
// The one indicator the reference widget draws as a tile outline rather than a
// corner badge, via ducharts' borderBadgeAccessor. Kept as a name so the legend
// entry, the cell flag and the badge exclusion can never drift apart.
//
// INTERIM for the extract monitor, where DPM has ANOMALY: dem/config.ts's
// borderBadgeAccessor reads `interim_extract_flg`, and an interim extract is a
// property of the whole cell — a second delivery of that day's file — rather than
// of one status inside it, which is exactly what an outline says and a corner
// badge does not.
const BORDER_INDICATOR = "interim";

// ------------------------------------------------------------------ indicators
/** Null-safe text read, so predicates never trip over null/undefined cells. */
const asText = (v: any): string =>
  v === null || v === undefined ? "" : String(v).trim();

/** The 'Y'/'N' test every flag indicator uses. */
const isY = (v: any): boolean => asText(v).toUpperCase() === "Y";

/**
 * Is this field "set"? Covers both shapes DEFECT is fed: a 'Y'/'N' flag and a
 * plain count. Deliberately not a bare `!!(...)` — that treats every non-empty
 * string as true, so an explicit 'N', which is how every flag in this dataset
 * spells "off", would light the badge on every record.
 */
const isSet = (v: any): boolean => {
  const t = asText(v).toUpperCase();
  if (!t) return false;
  // Numeric first, so a count is judged by its value — '0', '0.0' and 0 are all
  // off, where a string comparison would only have caught the first.
  const n = Number(t);
  if (Number.isFinite(n)) return n !== 0;
  return t !== "N" && t !== "NO" && t !== "FALSE";
};

interface IndicatorDef {
  /** Machine name; also the svg filename under assets/indicators_svg. */
  name: string;
  /** Legend caption. */
  label: string;
  /** Resolved asset path — the same file the cell badges use. */
  url: string;
  /** True when a record carries this indicator. */
  test: (record: any) => boolean;
}

const indicatorUrl = (name: string) => `assets/indicators_svg/${name}.svg`;

/**
 * Every indicator this screen knows about — dem/config.ts's `badgeIndicators`,
 * entry for entry and in its order, each reading the column its counterpart reads
 * there.
 *
 * The three trend entries read columns the API does NOT send: `sent_records_up`,
 * `sent_records_down` and `sent_records_nochange` are derived on the client, and
 * stampTrends() is what puts them on the records before any of this is consulted.
 * Everything else is a flag straight off the row.
 *
 * INTERIM is last because it is not a corner badge at all — it is drawn as an
 * outline round the tile. cellIndicators() filters it out by name, so its
 * position here is free.
 */
const INDICATORS: IndicatorDef[] = [
  {
    name: "defect",
    label: "DEFECT",
    url: indicatorUrl("defect"),
    // The extract feed's three count columns, and only those: it has no
    // failure_record_count / error_record_count the way DPM's does.
    test: (r) => isSet(r.failures) || isSet(r.errors) || isSet(r.on_hold),
  },
  { name: "notes", label: "NOTES", url: indicatorUrl("notes"), test: (r) => isY(r.comment_flg) },
  { name: "delay", label: "DELAY", url: indicatorUrl("delay"), test: (r) => isY(r.delay_ind) },
  { name: "ahead", label: "AHEAD", url: indicatorUrl("ahead"), test: (r) => isY(r.ahead_flg) },
  { name: "on_time", label: "ON TIME", url: indicatorUrl("on_time"), test: (r) => isY(r.on_time_flg) },
  // These two names carry their file's odd casing on purpose — indicatorUrl()
  // builds the path from `name`, and the assets are delayLT1hr.svg / delayGT1hr.svg.
  { name: "delayLT1hr", label: "DELAY < 1HR", url: indicatorUrl("delayLT1hr"), test: (r) => isY(r.delay_lt_1hr_flg) },
  { name: "delayGT1hr", label: "DELAY > 1HR", url: indicatorUrl("delayGT1hr"), test: (r) => isY(r.delay_gt_1hr_flg) },
  { name: "partial", label: "PARTIAL", url: indicatorUrl("partial"), test: (r) => isY(r.partial_flg) },
  // Derived, not sent — see stampTrends().
  { name: "up_trend", label: "UP TREND", url: indicatorUrl("up_trend"), test: (r) => isY(r.sent_records_up) },
  { name: "down_trend", label: "DOWN TREND", url: indicatorUrl("down_trend"), test: (r) => isY(r.sent_records_down) },
  { name: "no_change", label: "NO CHANGE", url: indicatorUrl("no_change"), test: (r) => isY(r.sent_records_nochange) },
  // A second delivery of the day's file. Drawn as the tile's outline.
  {
    name: BORDER_INDICATOR,
    label: "INTERIM",
    url: indicatorUrl("interim"),
    test: (r) => isY(r.interim_extract_flg),
  },
];

/**
 * Which indicators the legend surfaces, and therefore which can appear on a tile
 * — NO CHANGE, UP TREND, DOWN TREND, AHEAD, ON TIME, DELAY < 1HR, DELAY > 1HR,
 * NOTES.
 *
 * An ordering/visibility list; the icons and labels still resolve through
 * INDICATORS above. The rest stay defined because the columns behind them are
 * real and the server config decides per client which are on — adding one here is
 * the whole change.
 *
 * DELAY is deliberately absent, replaced by the four timing indicators after the
 * trends. `delay_ind` only says a file was late; ahead_flg / on_time_flg /
 * delay_lt_1hr_flg / delay_gt_1hr_flg say by how much, and one of them is set on
 * nearly every record — so DELAY was both the coarsest of the five and redundant
 * beside the two it overlaps.
 *
 * NOTES stays last: it is the one entry here that is not about timing, and a note
 * is added from a tile's own popup, so it reads as the end of the row rather than
 * as a fifth timing state.
 *
 * INTERIM is deliberately absent too: the reference legend does not offer it, so
 * the outline is not something this screen lets you switch off.
 */
const LEGEND_INDICATORS: IndicatorDef[] = [
  "no_change",
  "up_trend",
  "down_trend",
  "ahead",
  "on_time",
  "delayLT1hr",
  "delayGT1hr",
  "notes",
]
  .map((name) => INDICATORS.find((i) => i.name === name)!)
  .filter(Boolean);
interface HeatRow {
  id: string; // full path, e.g. "SP/Commercial/Biologics/Status"
  label: string; // this level's label (last path segment)
  // The dimension this node represents (a HEAT_LEVELS key). `level` cannot stand
  // in for it: blank levels are dropped from a path, so the same dimension can
  // sit at different depths in different branches. Mirrors the reference
  // widget's `XVariable`.
  dim: string;
  level: number; // 0 = top … deepest = leaf
  expanded: boolean;
  hasChildren: boolean;
  parentLabels: string[]; // ancestor labels, used to build the popup heading
  /**
   * The schedule and cutoff pills the reference prints beside a row's label —
   * dem/config.ts's `itemAccessorsTimeSeries: ['file_freq_desc']` and its
   * `itemAccessorsSuffix: ['file_receive_cutoff_time']`.
   *
   * Both are properties of a file type rather than of a day, which is why they
   * live on the row and not on a cell. Blank on an aggregate row: there they would
   * have to summarise several schedules at once, which is not something a pill can
   * say — see the fill in buildHeatmap().
   */
  freq: string;
  cutoff: string;
  rows: any[]; // every record under this node (aggregates descendants)
  cells: HeatCell[];
  children: HeatRow[];
  /** Dimmed by an active cross-highlight — nothing under this node matches what
   *  is highlighted. Stamped by applyHighlight().
   *
   *  `dimmed`, not `dim`: that name is already this node's DIMENSION, a few
   *  fields up. GridRow spells it the same way for the same reason. */
  dimmed: boolean;
  /**
   * Dropped from the strip by the OPEN selection: nothing under this node matches
   * the picks, so the heat map filters it out instead of fading it.
   *
   * The same pairing GridRow.hidden describes, and for the same reason. This panel
   * DIMS when the popup is anchored to it, because it has to go on showing the rows
   * you did not pick for a second pick to be possible; it HIDES when the selection
   * was made in the grid, a KPI card or the donut, because then the question is
   * "what does this narrow to". Stamped by refreshHeatFigures().
   */
  hidden: boolean;
}
// Upper bound on the days either view will lay out — see buildDateColumns().
// Live is a horizontally-scrolling strip of day columns; History is month
// grids, which hold a year in the space the strip gives a fortnight.
const LIVE_MAX_DAYS = 400; // ~13 months
const HISTORY_MAX_DAYS = 1100; // ~3 years

// Month labels for both views: the History calendars print them in full
// ("July - 2026") and the Live date strip takes the first three characters, so
// the two can never disagree about a month's name.
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * One day of the loaded range.
 *
 * The Live strip renders these as its columns, and the History calendars take
 * their months from the same list, so neither view can show a month the other
 * has no data for. Within a month the calendar then draws every day, so a day
 * outside this list is a tile with no cell rather than a gap in the grid — see
 * buildCalendar().
 */
interface DateColumn {
  iso: string;
  dd: string;
  mon: string;
  wd: string;
}

/** One day cell of the History view's month calendars. */
interface CalDay {
  iso: string;
  dom: string; // day of month, "01".."31"
  /** Padding day outside the month — holds its column, draws nothing. */
  offset: boolean;
  /**
   * The selected node's cell for this day, or null when the loaded range does not
   * reach it — the month is always drawn whole, so its first and last weeks can
   * run past the data. A null cell renders the outlined empty slot and answers
   * nothing; every calendar handler gates on it.
   */
  cell: HeatCell | null;
}
/**
 * One Sunday-to-Saturday row of a month grid.
 *
 * The month is modelled as weeks rather than the reference duc-calendar's flat
 * day list because its date labels are absolutely positioned out into a margin,
 * which does not stay put — the label ends up over the tiles. A week row gives
 * the date a real grid column of its own, so it cannot overlap anything.
 */
interface CalWeek {
  /** The row's date, from its first day inside the month. */
  label: string;
  /** Always 7, Sunday first, padded at both ends. */
  days: CalDay[];
}
interface CalMonth {
  key: string; // "2026-07"
  label: string; // "July - 2026"
  weeks: CalWeek[];
}

interface GridRow {
  label: string;
  dim: string; // the HEAT_LEVELS key this row represents — see HeatRow.dim
  level: number;
  expanded: boolean;
  hasChildren: boolean;
  /**
   * The heat-map node this row was mapped from — its full path.
   *
   * Carried so a click here means the same branch a click on the tree above does:
   * the grid is the same tree over the same levels, so both produce the one
   * `path:` selection and clicking either lights up both.
   */
  nodeId: string;
  /** Every record under this node. A reference to the heat node's own array, so
   *  it costs a pointer per row; read only by the cross-highlight. */
  rows: any[];
  /** Dimmed by an active cross-highlight — see HeatRow.dim. `dim` was already
   *  taken here by the dimension name, hence the longer spelling. */
  dimmed: boolean;
  /**
   * Dropped from the visible list by the OPEN selection: nothing under this node
   * matches the picks, so the grid filters it out rather than showing it at zero.
   *
   * Distinct from `dimmed`, which is the same test with a different answer. The
   * grid dims when it is the panel the popup is anchored to — it has to go on
   * listing what you did not pick, or there is nothing left to add a second pick
   * from — and hides when the selection was made anywhere else, because then the
   * question the panel is answering is "what does this narrow to". See
   * refreshGridFigures().
   */
  hidden: boolean;
  /**
   * Where this row sat among its siblings when the tree was BUILT — i.e. the order
   * the backend's tree order (or the record order) put it in.
   *
   * Carried so clearing the grid's sort can put every branch back exactly as it was
   * without rebuilding the tree: a rebuild would also reset which nodes are open,
   * and collapsing the grid because the user un-sorted a column would be a strange
   * thing to do. See sortGridTree().
   */
  ord: number;
  // Every count aggregate() produces, so a grid row and the KPI row can never
  // disagree about the same branch. The grid shows three of them today; the rest
  // are carried because the Excel export names them and adding a column is then
  // one entry in gridColumnDefs.
  pct: number;
  expected: number;
  sent: number;
  failed: number;
  inProcess: number;
  notStarted: number;
  delayed: number;
  interim: number;
  children: GridRow[];
}
// Interactive filter badge backed by the distinct values in the JSON.
interface FilterDef {
  label: string;
  key: string;
  single: boolean; // single-select (like Brand) vs multi-select
  options: string[];
  applied: Set<string>; // values currently filtering the data
  checked: string[]; // working selection (ng-select model + badge checkboxes)
  search: string;
  /**
   * "Add current selection to filter" — the checkbox the reference badge dropdown
   * and multi-select both carry (filter-badges.selectAddClick,
   * multi-select.selectAddClick).
   *
   * Off, a search narrows what Include can apply: only the ticked values still on
   * screen are sent, so searching and including REPLACES the filter. On, the values
   * already applied are carried along with them, so the same action ADDS to it.
   * That is the whole difference between the two, and why it resets to off every
   * time the list is opened or closed — it describes one interaction, not a
   * remembered preference.
   */
  addCurrent: boolean;
  // What a fresh visit to this screen opens the chip on, which is what Reset puts
  // it back to and what "off default" is measured against (isPartial). Recorded
  // per chip rather than assumed to be "every option", because a single-select
  // chip's default is one value — "all" is not a state it can be in. Rebuilt with
  // the chip on every load, so it always describes the dataset on screen.
  defaultApplied: string[];
}

/**
 * Every filter this screen offers, in strip order — the one list the badge
 * strip and the right-side panel are both built from.
 *
 * Declared outside the component so the list exists before anything has been
 * fetched: buildFilterSkeleton() builds an entry per name from it, valueless,
 * which is what the rest of the screen filters and fills against.
 * buildFilters() then walks the same list again once the data is in and fills
 * each entry's options.
 *
 * A valueless entry is not drawn — the strip and the panel both render a filter
 * only once it has values to offer — so a filter appears the moment its own
 * source answers and never before.
 *
 * An entry whose `key` matches a DEM_DYNAMIC_FILTERS entry takes its options
 * from that API; the rest are the distinct values of the loaded records.
 *
 * Destination is the extract's target — `source_name`, the value the extract grid
 * labels "Target Name" — and is derived from the records rather than fetched,
 * because every value it can take is already in them.
 */
/**
 * Row order per tree level — dem/config.ts's `itemsOrder`, brought across rather
 * than imported.
 *
 * Names listed here lead, in this order; anything else follows alphabetically (see
 * orderNodes). The four below are the order the reference screen shows, which leads
 * with the core SP files rather than sorting the whole list — left as data rather
 * than logic, so matching another client's screen is an edit here.
 *
 * The config's own list names the Rezurock demo's file types ('SP Status', 'SP
 * Dispense', …); these are the same intent against this client's names.
 */
const ITEMS_ORDER: Record<string, string[]> = {
  file_type: ["Patient View", "Status", "Dispense", "Inventory"],
};

const DEM_FILTER_DEFS: { label: string; key: string; single?: boolean }[] = [
  { label: "Brand", key: "brand_name", single: true },
  { label: "Destination", key: "source_name" },
  { label: "File Type", key: "file_type" },
  { label: "Status", key: "file_status" },
];

// Static "DEM-Static" dashboard. Renders the Data Extract Monitor UI from
// kpi/get-execution-details using the same palette, icons (mat svgIcons) and
// Poppins font as the real widgets.
//
// Brand is a live filter (kpi/get-brand-filter) and is the screen's first
// dependency — see loadDashboard(). Every other filter badge is still derived
// from the loaded records: distinct values drive the options and each selection
// re-aggregates the KPI / donut / heatmap / grid client-side, without a refetch.
@Component({
    selector: "app-dem-static",
    templateUrl: "./dem-static.component.html",
    styleUrls: ["./dem-static.component.scss"],
    // Screen-scoped: the request context it builds is this route's, so it should
    // not outlive the component or be shared with another static dashboard.
    providers: [DpmStaticService],
    standalone: false
})
export class DemStaticComponent implements OnInit, AfterViewInit, OnDestroy {
  // Raises the page's "no data" toast. Injected rather than taken through the
  // constructor, which is how every other widget that reports an empty state
  // reaches this service.
  private readonly noDataNotifier = inject(NoDataNotifierService);

  clientLogo: any;
  newColor: any;
  moduleName: any;
  clientKey: any;
  moduleKey: any;
  dashboardKey: any;

  // Full dataset (the last kpi/get-execution-details response) + the currently
  // filtered view used for every aggregation.
  data: any[] = [];
  viewData: any[] = [];

  /**
   * `viewData` narrowed by the OPEN selection — what the page would hold if the
   * popup's Apply were pressed right now. Equal to viewData when nothing is open.
   *
   * This is the difference between cross-HIGHLIGHTING and Qlik's actual
   * behaviour, and it was the bug: a pick used to stamp dim flags and
   * nothing else, so clicking a branch of the heat tree faded the tiles that did
   * not match while the KPI cards and the Extracts figures above went on
   * reporting the unfiltered totals. The screen showed a selection and its
   * numbers denied it. In Qlik the tentative selection is live everywhere the
   * moment it is made — only the object being selected IN keeps its full list,
   * so there is still something left to add to the selection with a second click.
   *
   * So the summary widgets read this (see widgetRows), and the object that owns
   * the selection keeps reading viewData. Committing changes nothing about what
   * is on screen, which is exactly right: Apply only makes it permanent.
   */
  private previewData: any[] = [];

  /**
   * "Does anything under the open selection match this?", per selection id.
   *
   * Memoised because it answers a template binding (isDimmed) on visuals that
   * are re-checked every change-detection pass, and the answer is a scan of
   * previewData. Cleared whenever the preview is rebuilt, which is the only
   * thing that can change it.
   */
  private readonly assocCache = new Map<string, boolean>();

  /** The days still represented in `previewData`, for isDayDimmed(). Built on
   *  demand and dropped with the preview, same as assocCache. */
  private previewDays: Set<string> | null = null;

  /** Counts the data loads issued, so a superseded one can be recognised and
   *  dropped when it answers — see loadExecutionData(). */
  private loadSeq = 0;

  // ---- API-driven filters --------------------------------------------------
  // Request context for both calls, and what each dynamic filter resolved to.
  // Both are settled before any data is fetched — see loadDashboard().
  private apiContext!: DpmStaticContext;
  private dynamicFilterStates: DpmDynamicFilterState[] = [];

  // The order the tree's levels are drawn in, as the backend gave it (empty until
  // the first load answers, and whenever it has nothing to say — see
  // DpmStaticService.getTreeOrder). Fetched with the data because it is scoped by
  // the same brand, and read by orderNodes() on every rebuild, so a filter change
  // re-sorts rather than falling back to the shipped ITEMS_ORDER.
  private treeOrder: StaticTreeOrder = {};

  // True from the first request until the whole screen has been rebuilt from
  // the response — one flag for every widget, because they all read the same
  // dataset and there is nothing useful to show while part of it is missing.
  // Starts true so the panels open as loading rather than flashing "no data".
  loading = true;
  // Nothing came back for the current selection + date range. Distinct from
  // `loading`, so an empty answer reads as an empty answer instead of a
  // spinner that never stops.
  get noData(): boolean {
    return !this.loading && !this.viewData.length;
  }

  // Header
  dashboardTitle = "Data Extract Monitor";
  brandName = "";
  /**
   * The topnav's page-level Help icon. The dynamic pages build this from page
   * config key 555555; a static dashboard has no page config, so it is stated here.
   *
   * `pageInfoChecks` switches the icon on. `pageInfoVar` is the one field topnav
   * actually reads afterwards — it goes out as `file_nm` on both calls its
   * getInfoDetail() makes, and that is what resolves this dashboard's user manual:
   * the help text comes back with a `??um_url` placeholder in it, and topnav swaps
   * that for the export URL of the named PDF. Left blank, the placeholder is
   * printed literally, which is what this screen did before.
   *
   * This is the PAGE-level help and it still fetches, unlike the per-widget
   * popovers on this screen (see helpFor and the panels' [staticInfo]) — because
   * the manual link can only come from the API.
   *
   * pageInfoApiKey / pageInfoWidgetKey stay empty: topnav sends api_key and
   * widget_key as null for a page-level lookup and never reads either field.
   */
  readonly topnavHelpInfo = {
    pageInfoChecks: true,
    pageInfoVar: "DEM_Internal_User_Manual.pdf",
    pageInfoApiKey: "",
    pageInfoWidgetKey: "",
  };

  // Filter chips
  filters: FilterDef[] = [];
  itemsShowLimit = 2; // chips shown before "N more..." in the ng-select label
  openKey: string | null = null;
  menuPos = { left: 0, top: 0 };

  // ---- cross-filtering (click a visual to narrow the page) -----------------
  /**
   * Committed cross-filters — the picks a selection popup's Apply sent here. These
   * are real filters: applyFilters() narrows `viewData` by them alongside the
   * chips, so every widget re-aggregates, and each one shows as a removable chip
   * in the filter bar. Persisted with the rest of the filter state.
   *
   * Combined OR-within-group, AND-across-groups — see combineSelections().
   */
  selections: StaticSelection[] = [];

  /**
   * The selection being built right now — which widget owns it, and what has been
   * picked in it. Null when nothing is open.
   *
   * Filters nothing on its own. While it is up, every widget keeps all of its data
   * and dims whatever the picks do not match, so the part is read against the
   * whole; the widget that owns it draws an outline and wears the selection popup,
   * which names the picked values as chips you can take back out one at a time.
   * Only the popup's Apply turns any of it into an actual filter.
   *
   * Built by ctrl/cmd+click and by nothing else — see onVisualClick(). Resolved by
   * exactly three things: the popup's Apply (commit), its Clear button, and Escape
   * (both discard). Nothing else ends it. Clicking away used to confirm it and a
   * pick in another widget used to as well; both were removed because they applied
   * filters the user had not asked for — see onDocumentClick() and pick().
   */
  pending: StaticPendingSelection | null = null;

  /**
   * Where the selection toolbar sits, in VIEWPORT coordinates.
   *
   * The pill is position: fixed and rendered once at the root of the component
   * rather than inside the widget it belongs to. It has to be: every widget on
   * this screen is a %card, and %card is `overflow: hidden` — so a pill
   * absolutely positioned to straddle a panel's top edge is simply clipped away
   * at that edge, which is exactly what it did. Dropping the card's overflow is
   * not an option either; it is what keeps the Extracts card's captions from
   * spilling out of it.
   *
   * So it escapes the card the same way the heat tile's hover panel does (see
   * .cell-tip, which is fixed for the same reason) and is placed against the
   * widget's measured rect instead — see positionToolbar().
   */
  toolbarPos = { left: 0, top: 0 };

  // date badge -> app-calendar popup (same UX as filter-badges)
  dateMenuOpen = false;
  datePos = { left: 0, top: 0 };
  dateConfig: any;
  dateSelectedData: any;
  // Right-side filter panel (opened from the sticky filter icon)
  filterPanelOpen = false;
  panelWidth = 380;
  readonly AngularResizeElementDirection = AngularResizeElementDirection;
  dateRangeLabel = "";

  // Date range (From / To) — ISO strings that drive the aggregation.
  dataMin = "";
  dataMax = "";
  fromDate = "";
  toDate = "";
  // default range (data period) used to detect changes + reset
  private defaultFrom = "";
  private defaultTo = "";

  // ---- date pickers (this screen is Daily-only; selector hidden) ----------
  // Add more cycles here to re-enable the D/W/M/Q/Y selector.
  timeCycles: string[] = ["D"];
  selectedCycle = "D";
  cycleFormats = [
    { value: "D", label: "Daily" },
    { value: "W", label: "Weekly" },
    { value: "M", label: "Monthly" },
    { value: "Q", label: "Quarterly" },
    { value: "Y", label: "Yearly" },
  ];
  exampleHeader = AppCustomDatepickerHeaderComponent;

  // picker model (Date objects) + bounds
  fromDateObj!: Date;
  toDateObj!: Date;
  windowStart!: Date;
  windowEnd!: Date;
  minFromDate!: Date;
  maxFromDate!: Date;
  minToDate!: Date;
  maxToDate!: Date;

  // quarter (Q) options
  fromOption: any[] = [];
  toOption: any[] = [];
  selectedFromOption: any;
  selectedToOption: any;
  /**
   * The filter bar's note — one sentence per view, because the two views are asked
   * different questions about the range.
   *
   * Live opens on a fixed default window, so it states that default. History is
   * opened precisely because the span is long, where "previous 15 days" would be a
   * plainly wrong statement — so it reports the span actually loaded, which is the
   * one figure that says how far back the calendars reach without the reader adding
   * the months up. See updateNote() for the wording and the arithmetic.
   *
   * Starts blank rather than on either sentence: `isHistory` comes off the route,
   * which the constructor reads AFTER every field initialiser here has run, so
   * there is no correct value to seed it with. ngOnInit settles it and
   * buildDateColumns() keeps it current.
   */
  noteText = "";
  private readonly LIVE_NOTE =
    "The data shown by default is from previous 15 days from the current date";

  @ViewChild("badgeScroller") badgeScroller!: ElementRef<HTMLElement>;

  // scroll arrows only show when the badge strip actually overflows
  badgesOverflow = false;

  // KPI cards. Seeded with the constant chrome so the row is on screen from the
  // first frame — each card shows its own loader where the number will go, so
  // the layout does not shift when the values arrive.
  kpis: {
    /** KPI_CARD_DEFS' own key. Always been on these objects (KPI_CARDS spreads
     *  the def), and now declared: buildKpis() looks each value up by it and the
     *  row's trackBy identifies each card by it. */
    key: string;
    heading: string;
    icon: string;
    color: string;
    value: number;
    subHeading: string;
    subValue: number;
    /** This card's help entry, already resolved by KPI_CARDS. Null hides the
     *  card's Help icon rather than opening an empty popover. */
    help: HelpEntry | null;
    /** What clicking this card's figures cross-filters by — see KPI_CARD_DEFS. */
    sel: StaticSelection;
    subSel: StaticSelection | null;
  }[] = KPI_CARDS.map((c) => ({ ...c, value: 0, subValue: 0 }));

  // The Extracts card: three counts rather than a heading and a number, so it
  // does not go through KPI_CARDS. Zeroed rather than left undefined, so the card
  // renders its labels from the first frame and only the figures are pending.
  extracts = { sent: 0, expected: 0, interim: 0 };
  /** What the Extracts card's Sent and Interim Sent figures cross-filter by.
   *  Expected has no entry — see EXTRACTS_SEL. */
  readonly extractsSel = EXTRACTS_SEL;
  /**
   * The one green "sent" wears across the three widgets that report it: the
   * Extracts card, the donut's arc and Sent swatch, and every heat tile's Sent
   * band.
   *
   * Exposed off COLORS rather than hard-coded in the template so all three keep
   * agreeing — and so they keep agreeing with dem/config.ts's `colorScheme`, which
   * is where the value comes from.
   */
  readonly sentColor = COLORS.sent;

  // Donut
  donut!: {
    percentage: string;
    ratio: string;
    // The concentric arcs the ring draws, outermost first — Sent and Expected,
    // so the circle carries both series in bands of their own rather than
    // stacking them in one. Each arc knows its own radius, so the geometry lives
    // in one place instead of being split between the template and the builder.
    arcs: DonutArc[];
    legend: { name: string; value: number; color: string }[];
  };

  /**
   * How far the ring card's type has to come down for its longest line to fit —
   * 0 (not at all) to 3. Read by the template as `data-fit`, which the
   * stylesheet turns into a scale factor for every figure and label on the card
   * (.donut-inner[data-fit]).
   *
   * The card's own words are fixed — "Processing Status" is the widest of them —
   * but its figures are not, and %card clips rather than wraps. A brand with a
   * million files makes "Expected: 1000000" half again as wide as the legend was
   * laid out for, and the row it belongs to is pinned to one line so nothing can
   * fall to a second. So the card measures what it is about to draw and shrinks
   * its own type to hold it.
   *
   * The alternative was abbreviating these figures the way the KPI cards now do
   * (short-count.ts). Deliberately not taken: a ring is a card about
   * proportions, and "24/28" rounded to two significant figures stops being
   * checkable against the percentage printed beside it.
   */
  donutFit = 0;

  // ---- Live vs History ------------------------------------------------------
  // Set from the `hist_flg` query param the dashboard card put there, so the
  // screen knows which of its two buttons opened it before the first request
  // goes out. It picks the report type (D / H) AND which view the receipt panel
  // renders: Live is the day-by-day date strip, History the month calendars.
  //
  // Both views are the same tree over the same cells — only the layout differs,
  // so the tiles, their click popups and the legends are shared rather than
  // reimplemented per view.
  isHistory = false;

  // ---- History (calendar) view ---------------------------------------------
  // The month grids for whichever tree node is selected. The date strip gives
  // every node a row of its own; a calendar is a whole month per node, so the
  // History view shows one node at a time and the tree is how you choose it.
  calMonths: CalMonth[] = [];
  calNode: HeatRow | null = null;
  // Column headings of every month grid. Sunday first, matching the order
  // buildCalendar() pads and flows the days in.
  readonly calWeekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  /**
   * Which way the month grids run, flipped by the shared header's Sort icon.
   *
   * 'desc' (newest first) to start with, the order the reference view opens on:
   * with a long history the month being looked for is nearly always the most
   * recent one. The same two states, and the same starting point, as the real
   * widget's `calenderSortOrder` (status-overview.ts -> monthSortOrder).
   */
  calSortOrder: "asc" | "desc" = "desc";

  // ------------------------------------------------------------------ help text
  /**
   * The help entry behind each widget's Help icon, out of
   * assets/help_info/dem-static.json.
   *
   * Resolved once as fields rather than called from the template: helpFor() would
   * otherwise run on every change-detection pass, and an `info_header` this file
   * spells wrong shows as a missing icon on the first render instead of
   * intermittently.
   *
   * The two panel entries are handed straight to <icon-chart>'s `staticInfo`, which
   * takes exactly this shape — so the panels answer through the shared header's own
   * Help icon and popover rather than growing a second one.
   *
   * `gridHelp` is null today: dem-static.json has no "Data Extract Status" entry, so
   * that header's icon falls back to the API fetch it already does. Bound anyway, so
   * adding the entry to the JSON is the whole change.
   *
   * Spelled as the JSON's own headers rather than read off heatTitle / gridTitle,
   * which these two happen to match: those are declared further down the class, and
   * a field initialiser referencing a later field reads undefined. Independent
   * anyway — the header is the JSON's key, and renaming a panel on screen should not
   * quietly take its help text away.
   */
  readonly extractsHelp = helpFor("Extracts");
  readonly donutHelp = helpFor("Processing Status (Expected Vs Sent)");
  readonly heatHelp = helpFor("Data Load Status");
  readonly gridHelp = helpFor("Data Extract Status");

  /**
   * The entry the shared card popover is showing.
   *
   * One popover and one field for the whole KPI row rather than one per card: the
   * cards render from an *ngFor, so a mat-menu inside it would be a menu per card.
   * The trigger anchors to whichever icon was clicked and showCardHelp() loads that
   * card's entry on the same click.
   */
  cardHelp: HelpEntry | null = null;

  // Heatmap
  dateColumns: DateColumn[] = [];
  heatRows: HeatRow[] = [];
  // Flattened view of heatRows honouring each node's expanded state — lets the
  // template render an arbitrarily deep tree as a simple indented row list.
  visibleHeatRows: HeatRow[] = [];
  // Tree hierarchy (top → leaf), shared by BOTH panels: the Data Load Status heat
  // map and the Data Extract Status grid are the same tree over these levels (buildGrid()
  // maps the heat map's own nodes), so this is the single place the hierarchy is
  // defined. The last level a branch reaches is where individual files live, so
  // it is the only level that answers a hover or a click — see
  // isCellInteractive(). Indicator icons and the status fill still apply at every
  // level, aggregating downward. A level that is blank on a record is dropped
  // from its path, so branches can end at different depths.
  //
  // Three levels for the extract monitor, where DPM has five.
  // dem/config.ts ships `itemAccessors: ['extract_type', 'file_type']` for its
  // Rezurock demo, but the extract monitor as configured for this client roots the
  // tree at the destination: the screen shows "Sanofi" over its file types, and
  // the extract grid below labels that same value "Target Name". So the first
  // level is source_name; file_type is the second either way.
  //
  // sub_file_type is the third, and is OPTIONAL by construction rather than by a
  // flag: recordPath() drops a level that is blank on a record, so a file type
  // whose records carry a sub type gains a level under it and one whose records
  // do not stays the leaf it already was. Nothing else has to know — the grid, the
  // exports, the tree order and the cell interactions are all written over
  // HEAT_LEVELS and against `hasChildren` rather than a fixed depth. This is the
  // only screen it is added to: DPM's hierarchy is its own list above.
  private readonly HEAT_LEVELS = ["source_name", "file_type", "sub_file_type"];
  // Excel header per level. Kept beside HEAT_LEVELS so adding a level there
  // cannot leave either export with an unnamed column — levelExcelCols() builds
  // both panels' level columns from these three.
  private readonly LEVEL_LABELS: Record<string, string> = {
    source_name: "Target Name",
    file_type: "File Type",
    sub_file_type: "Sub File Type",
  };
  // Status + indicator legends both come straight from the shared config, so
  // they always match the Status Overview widget's swatches and badge icons.
  heatLegend = STATUS_LEGEND;
  indicatorLegend: IndicatorDef[] = LEGEND_INDICATORS;

  // ---- heat legend interaction -------------------------------------------
  // Click-only show/hide, exactly like the Status Overview widget's legend: no
  // hover behaviour and nothing is "selected". Clicking an entry switches it off
  // — the status legend blanks those cells (ducharts drops those records from the
  // cell), and the indicator legend drops that badge from every tile. So these
  // sets hold what is HIDDEN, and empty means "everything shown". The swatch
  // itself carries the state: filled dot vs hollow ring for a status, icon vs
  // empty circle for an indicator.
  hiddenStatuses = new Set<string>();
  // Nothing seeded as hidden, unlike DPM — which starts ANOMALY off because its
  // outline animates on every anomalous cell the moment the screen opens. The
  // extract legend offers no INTERIM entry at all (LEGEND_INDICATORS), so hiding
  // it here would only make the outline unreachable rather than optional.
  hiddenIndicators = new Set<string>();

  // ---- heat map widget actions (shared <icon-chart>) ----------------------
  // The panel hosts the same shared header component every dynamic widget uses,
  // so Fullscreen / Expand / the export menu behave identically here.
  //
  // #heatFsEl (the node icon-chart calls requestFullscreen on) and #heatExportEl
  // (the subtree it rasterises for "Export as Image") are handed over
  // straight from the template, and deliberately are NOT @ViewChild properties:
  // openFullscreen() does `this.expand.requestFullscreen()` on the raw node, and
  // a template reference variable on a native element already resolves to the
  // HTMLElement. A ViewChild would supply an ElementRef instead — and would
  // shadow-clash with the identically named template ref.
  @ViewChild("heatScrollEl") heatScrollEl!: ElementRef<HTMLElement>;

  // ---- heat tile hover tooltip --------------------------------------------
  // The panel itself is the reused Status Overview cell component
  // (<app-status-cell-info-dpm>), fed by buildCellPayload() — the same payload
  // the single- and double-click popups get — so hover, comments and the control
  // chart can never disagree about what a cell holds. `tipData` non-null is what
  // renders it; `tipPos` / `tipRadius` are what placeCellTip() settles on.
  //
  // Placement is reimplemented here rather than delegated to ducharts'
  // duc-tooltip because that component is driven by the d3 chart's own mouse
  // handlers and these tiles are plain DOM — but it follows the library's
  // geometry exactly. See placeCellTip().
  tipData: any = null;
  tipPos = { left: 0, top: 0 };
  // border-radius with the corner that touches the tile squared off — the
  // library's "caret corner". Set by placeCellTip(); this is duc-tooltip's own
  // default (all four corners at its borderRadius of 16).
  tipRadius = "16px";
  @ViewChild("cellTipEl") cellTipEl?: ElementRef<HTMLElement>;

  // Open indicator-overflow popover: what the ⋯ disc on a tile stands for. A
  // separate panel from the hover tooltip because both can be open at once —
  // reaching the disc means the pointer is already on the tile — and this one
  // names icons rather than describing the cell.
  badgePop: { indicators: CellBadge[]; left: number; top: number } | null = null;
  // duc-tooltip's borderRadius, and the margin status-overview.updateTooltip()
  // asks for — it passes none, so the panel's squared corner sits directly on
  // the tile's corner.
  private readonly TIP_RADIUS = 16;
  private readonly TIP_MARGIN = 0;

  heatFullscreen = false;
  heatExpanded = false;
  readonly heatTitle = "Data Load Status";

  // Leaf-tile count sizing. `heatTableMinPx` guarantees every date column is
  // wide enough to print its widest value at a legible size — the strip then
  // scrolls horizontally rather than squeezing digits into an unreadable tile.
  // `heatCountFontPx` adapts within that guarantee to the width the column
  // actually got. Both are applied once on the table (a style binding and a CSS
  // custom property) instead of per cell, so they cost nothing per tile.
  heatCountFontPx = 10;
  heatTableMinPx = 760;
  private heatMaxCountChars = 0;

  // Tree column width, bound to the column's header cell. Recomputed from the
  // rows currently on screen (updateHeatTreeWidth), so the column tracks the
  // tree: expanding a branch widens it just enough for the deeper indent, and
  // collapsing gives the space straight back to the tiles. Replaces a flat 190px,
  // which was both dead space beside short labels and too narrow for the deeper
  // levels, where the text ran on over the first tiles.
  heatTreeColPx = 190;
  private readonly HEAT_TREE_MIN_PX = 96;
  /**
   * Ceiling on a NODE LABEL, so one very long name cannot squeeze the tiles out;
   * anything past it ellipsizes and answers with a tooltip on hover.
   *
   * On the label rather than on the row's total, which is what it used to cap.
   * A leaf row here is a label plus two pills, and the pills do not shrink — so a
   * total ceiling made the two compete for the same budget and, once the label
   * had taken its share, clipped the pills instead. A schedule reading
   * "M, T, W, TH ,…" is not a shorter way of saying the same thing, so the pills
   * are never the ones to give. See updateHeatTreeWidth().
   */
  private readonly HEAT_LABEL_MAX_PX = 200;
  // The column's own left padding (.heat-tree) plus the per-level indent, which
  // the template binds as [style.margin-left.px]="row.level * 16" on .tree-cell —
  // a margin rather than padding so the row's cross-filter ring starts at the
  // node instead of back at the column edge. Both take their space out of the
  // same fixed-width cell, so the arithmetic below is the same either way.
  private readonly HEAT_TREE_PAD_PX = 8;
  private readonly HEAT_TREE_INDENT_PX = 16;
  // .fold is 20px wide with a 6px right margin.
  private readonly HEAT_FOLD_PX = 26;
  // Just enough that a label stops short of the gutter instead of touching the
  // first tile.
  private readonly HEAT_TREE_TAIL_PX = 4;
  // .node-label's font, for measuring a label's real width.
  private readonly HEAT_LABEL_FONT_PX = 12;
  // .row-pill's font, and what one pill costs beyond its text: 6px of padding a
  // side and a 1px border a side.
  private readonly PILL_FONT_PX = 10;
  private readonly PILL_PAD_PX = 14;
  // .tree-cell's gap, which sits in front of each pill.
  private readonly PILL_GAP_PX = 6;
  /**
   * Width of each pill column, fitted to the widest value on screen.
   *
   * Fixed per column rather than per pill, because the point is that they line up:
   * a "TUE" schedule and an "M, T, W, TH , F" one are different widths, so sizing
   * each pill to its own text left the cutoff pill at a different x on every row
   * and the tree read as ragged. With one width per column the two form columns
   * down the tree, which is what the reference shows.
   */
  freqColPx = 0;
  cutoffColPx = 0;
  private labelCtx: CanvasRenderingContext2D | null | undefined;
  // Must match $heat-gap in dpm-static.component.scss — that is the table's
  // border-spacing, so it is part of the width every column actually consumes.
  private readonly HEAT_COL_SPACING_PX = 5;
  private readonly HEAT_COUNT_MIN_FONT = 8;
  private readonly HEAT_COUNT_MAX_FONT = 12;
  // Poppins 700 advance width as a fraction of font size; digits and the
  // thousands comma both sit close enough to this for sizing purposes.
  private readonly HEAT_CHAR_RATIO = 0.62;

  // ---- indicator badge geometry -------------------------------------------
  // Must match .cell-badge / .cell-badges in the stylesheet: the disc's size, the
  // gutter between two of them, and how far the strip is allowed to hang off the
  // tile's right edge (.cell-badges right: -4px). measureTileBadgeCap() divides a
  // tile's measured width by these to decide how many badges it can carry.
  private readonly BADGE_PX = 12;
  private readonly BADGE_GAP_PX = 1;
  private readonly BADGE_OVERHANG_PX = 4;
  // ...and .badge-pop's padding and gap, which showBadgePop() needs to size the
  // unstacked column before it can place it.
  private readonly BADGE_POP_PAD_PX = 4;
  private readonly BADGE_POP_GAP_PX = 4;
  // The gap between the disc and the column rising out of it.
  private readonly BADGE_POP_OFFSET_PX = 3;

  /**
   * The rendered width of one tile, measured by measureTileWidth(). bandBadgeCap()
   * divides it by the number of bands to decide how many badges a band can carry.
   *
   * 0 until the first measurement lands, which bandBadgeCap() reads as "one badge
   * a band" — the worst case is a ⋯ disc where the badges would in fact have
   * fitted, which the scheduled pass then corrects.
   */
  private tileWidthPx = 0;
  // bandBadgeCap() keyed by band count. The template asks per band, but the answer
  // only depends on how many bands the tile has — every two-band tile on screen
  // gets the same number — so it is worth not re-dividing thousands of times a
  // change-detection pass. Cleared whenever the measured tile width moves.
  private bandCapCache = new Map<number, number>();

  /**
   * The header's Sort icon — it flips the month order of the History calendars.
   *
   * History only, so it starts switched off: the Live view is one continuous
   * chronological strip with no months to reorder, and icon-chart's own gate for
   * this icon is keyed to two hard-coded dashboard keys that are not ours (see the
   * `icon.name =='sort'` case in icon-chart.component.html), so it would otherwise
   * show in both views. ngOnInit turns it on, because `isHistory` arrives with the
   * route — which the constructor reads, by which point every field initialiser
   * here has already run.
   *
   * Held by reference rather than looked up out of heatHeaderConfig below, so the
   * one line that switches it on cannot go hunting through the config and quietly
   * miss.
   */
  private readonly heatSortIcon = {
    name: "sort",
    value: "notes_sorting.svg",
    activated: false,
  };

  // Mirrors widget 100709's `widget_header_config` in
  // assets/page_json/DPM_S3_History_expected_dt.json — the same icon set and
  // export-menu wiring the real Data Load Status widget ships with — all three
  // menu entries it offers, Excel / Image / Copy to Clipboard, each of which
  // this screen can honour as-is.
  readonly heatHeaderConfig: any[] = [
    {
      name: "Page Header",
      value: "page_header",
      add_header: true,
      header_name_isNeed: true,
      page_header_name: this.heatTitle,
    },
    {
      name: "Icons On Right side",
      value: "icons",
      add_header: true,
      icon_selected: true,
      iconList: [
        { name: "info_circle", value: "info_circle.svg", activated: true },
        { name: "expand_circle", value: "expand_circle.svg", activated: true },
        { name: "gridload_circle", value: "gridload_circle.svg", activated: true },
        // Last of the view controls, immediately before the export menu, so the
        // icons that were already here keep the positions they had.
        this.heatSortIcon,
        {
          name: "dotmenu_circle",
          value: "dotmenu_circle.svg",
          activated: true,
          is_Event: true,
          event: [
            {
              name: "Excel",
              value: "excel",
              is_true: true,
              dispPlayName: "Export as Excel",
            },
            {
              name: "PNG",
              value: "png",
              is_true: true,
              dispPlayName: "Export as Image",
            },
            // The third entry the real widget config ships and this screen was
            // already able to honour: icon-chart's copyImageToClipboard()
            // rasterises the very element exportToPNG() does — the panel's
            // #…ExportEl, handed over as [config] — so switching it on is the
            // whole change. Named exactly as the reference config names it
            // (control-chart-dialog), so the entry reads the same wherever it
            // appears in the portal.
            {
              name: "Copy to Clipboard",
              value: "copy_image",
              is_true: true,
              dispPlayName: "Copy to Clipboard",
            },
          ],
        },
      ],
    },
  ];

  // icon-chart's excelExport() reads item.config for the workbook name before
  // short-circuiting on excelConfig, so this is the minimum it needs.
  // `widgetKey` is what icon-chart filters filterService.expendTree by, which is
  // the channel syncHeatExpandIcon() uses to keep the expand/collapse icon in
  // step with the tree. It has to be a real value, not undefined: some widgets
  // push a bare boolean onto that subject, and `undefined == undefined` would
  // let those through to this header.
  readonly heatItem: any = {
    key: 100709,
    config: { report_heading: this.heatTitle, widgetKey: 100709 },
  };

  // Column defs for "Export as Excel". Passing excelConfig makes
  // icon-chart delegate straight to ExcelService.customExportExcel, bypassing
  // its per-chart-name branching.
  readonly heatExcelConfig: any[] = [
    ...this.levelExcelCols(),
    // `heatExcelRows` is the raw record list, so every key here has to be a real
    // response column — `cal_day` (DPM's) does not exist on these rows and the
    // column would export blank.
    { header: "Date", key: "cycle_dt", type: "string" },
    { header: "Status", key: "file_status", type: "string" },
    { header: "Sent Records", key: "sent_records", type: "number" },
    { header: "Failures", key: "failures", type: "number" },
    { header: "Errors", key: "errors", type: "number" },
    { header: "On Hold", key: "on_hold", type: "number" },
    { header: "Schedule", key: "file_freq_desc", type: "string" },
    { header: "Cutoff", key: "file_receive_cutoff_time", type: "string" },
    { header: "File Name", key: "file_nm", type: "string" },
  ];

  // One Excel column per level of the shared hierarchy, in tree order. A method
  // rather than a field so both excelConfigs can spread it; safe to call from a
  // field initializer because HEAT_LEVELS and LEVEL_LABELS are declared above it.
  private levelExcelCols(): any[] {
    return this.HEAT_LEVELS.map((key) => ({
      header: this.LEVEL_LABELS[key] || key,
      key,
      type: "string",
    }));
  }

  // Grid
  gridRows: GridRow[] = [];
  // Flattened view of gridRows honouring each node's expanded state, exactly as
  // visibleHeatRows is for the heat map.
  visibleGridRows: GridRow[] = [];
  // Dots beside the grid's count headers. Resolved from the shared palette, so
  // each header wears the same colour as the status it counts.
  headerDots = {
    expected: COLORS.expected,
    sent: COLORS.sent,
    inProcess: COLORS.inProcess,
    delayed: COLORS.delayed,
    failed: COLORS.failed,
  };

  // ---- ag-Grid (Data Processing Status) ------------------------------------
  // The panel is an ag-Grid rather than a hand-rolled table, skinned back to the
  // look it had (see the .dpm-ag-grid block in the stylesheet).
  //
  // It is fed `visibleGridRows` — the expansion-aware flat list — instead of
  // ag-Grid tree data. The hierarchy on this screen is shared with the heat map
  // above: one HEAT_LEVELS definition, one +/- control, and one expand-all that
  // icon-chart drives through onGridExpandToggle(). Native tree data would put
  // ag-Grid in charge of all three, so the two panels could disagree about what
  // is open. Flattening here keeps them the same tree, rendered twice.
  readonly gridDefaultColDef: ColDef = {
    // Sorting is offered, but ag-Grid does not carry it out: the rows are a
    // flattened tree, so sorting the flat list would lift children away from the
    // parent they are indented under and the indent would stop meaning anything.
    //
    // So the header is sortable while the comparator below reports "no difference"
    // for every pair — ag-Grid's sort is stable, so that leaves the rows in the order
    // they were handed over. onGridSortChanged() then sorts the TREE instead,
    // reordering each node's children inside their own branch, and hands back a
    // freshly flattened list.
    //
    // No unSortIcon: only the ascending and descending arrows are shown, so an arrow
    // in a header always means "this is the column the grid is sorted by" rather than
    // being a permanent decoration on every column.
    sortable: true,
    comparator: () => 0,
    // Column filters stay off: the filter bar above is this screen's filtering, and
    // a second, per-column one would be a different answer to the same question.
    // The menu goes with them — there is nothing left in it this screen wants to
    // offer, so the header carries sorting and resizing and nothing else.
    filter: false,
    suppressMenu: true,
    resizable: true,
    suppressMovable: true,
  };

  /**
   * Cross-filter state on a grid row, as ag-Grid row classes.
   *
   * Row classes rather than a cellClass so the whole row answers, which is what
   * the click selects — a branch, not a cell. ag-Grid only re-evaluates these
   * when its row data is re-set, so applyHighlight() hands it a fresh
   * `visibleGridRows` array; see the note there.
   *
   * Dimming is all that is left here. A committed filter used to mark its rows
   * too, and that mark is gone from the whole screen on purpose: once Apply has
   * run, the chips in the filter strip are what say what is filtering, and a page
   * that ALSO outlined every figure, row and column the filter touched read as
   * though a selection were still open. See .xf-lit in the stylesheet.
   */
  readonly gridRowClassRules: Record<string, (p: any) => boolean> = {
    "dem-row-dim": (p) => !!p.data?.dimmed,
  };

  readonly gridColumnDefs: ColDef[] = [
    {
      // Holds every level of the hierarchy, indented — the same single column
      // the table had, for the same reason (a column per level would be as deep
      // as the deepest branch and mostly empty).
      // "Target Name" is what the reference grid calls the extract's destination,
      // which is this tree's first level (source_name).
      headerName: "Target Name",
      colId: "tree",
      field: "label",
      minWidth: 260,
      flex: 2,
      headerClass: "dpm-ag-left",
      cellClass: "dpm-ag-tree",
      cellRenderer: (p: any) => this.gridTreeCell(p.data),
    },
    {
      headerName: "Processing Status",
      colId: "pct",
      field: "pct",
      minWidth: 150,
      flex: 1,
      cellRenderer: (p: any) => {
        const pct = p.data?.pct ?? 0;
        return `<span class="pct" style="color:${this.pctColor(
          pct
        )}">${pct.toFixed(2)}%</span>`;
      },
    },
    // The three the reference grid shows, in its order. Expected and Failed are
    // deliberately not here: Expected is the denominator the Processing Status
    // column beside them already grades against, and Failed carries no rows in
    // this feed — adding either is one entry in this list.
    ...[
      { header: "In Process", key: "inProcess" },
      { header: "Sent", key: "sent" },
      { header: "Delayed", key: "delayed" },
    ].map(
      (c): ColDef => ({
        headerName: c.header,
        colId: c.key,
        field: c.key,
        minWidth: 130,
        flex: 1,
        headerComponentParams: {
          template: this.dotHeaderTemplate(
            (this.headerDots as any)[c.key] || COLORS.expected
          ),
        },
      })
    ),
  ];

  // ---- data processing grid widget (shared <icon-chart>) --------------------
  // The same header component, icon set and export menu as the heat map above,
  // so both panels behave identically. #gridFsEl / #gridExportEl come straight
  // from the template and are deliberately not @ViewChild, for the same reason
  // the heat map's refs are not — see the note on heatScrollEl above.
  gridFullscreen = false;
  gridExpanded = false;
  readonly gridTitle = "Data Extract Status";
  // Stable array reference for icon-chart's [data] — see buildGridExcelRows().
  gridExcelRows: any[] = [];

  // Mirrors heatHeaderConfig entry for entry, which is what makes the two panel
  // headers offer the same icons. The static `setting` icon this panel used to
  // show is dropped: the heat map does not have one, and there is nothing on
  // this screen for it to configure.
  readonly gridHeaderConfig: any[] = [
    {
      name: "Page Header",
      value: "page_header",
      add_header: true,
      header_name_isNeed: true,
      page_header_name: this.gridTitle,
    },
    {
      name: "Icons On Right side",
      value: "icons",
      add_header: true,
      icon_selected: true,
      iconList: [
        // { name: "info_circle", value: "info_circle.svg", activated: true },
        { name: "expand_circle", value: "expand_circle.svg", activated: true },
        { name: "gridload_circle", value: "gridload_circle.svg", activated: true },
        {
          name: "dotmenu_circle",
          value: "dotmenu_circle.svg",
          activated: true,
          is_Event: true,
          event: [
            {
              name: "Excel",
              value: "excel",
              is_true: true,
              dispPlayName: "Export as Excel",
            },
            {
              name: "PNG",
              value: "png",
              is_true: true,
              dispPlayName: "Export as Image",
            },
            // The third entry the real widget config ships and this screen was
            // already able to honour: icon-chart's copyImageToClipboard()
            // rasterises the very element exportToPNG() does — the panel's
            // #…ExportEl, handed over as [config] — so switching it on is the
            // whole change. Named exactly as the reference config names it
            // (control-chart-dialog), so the entry reads the same wherever it
            // appears in the portal.
            {
              name: "Copy to Clipboard",
              value: "copy_image",
              is_true: true,
              dispPlayName: "Copy to Clipboard",
            },
          ],
        },
      ],
    },
  ];

  // Distinct key from heatItem on purpose: icon-chart filters
  // filterService.expendTree by item.config.widgetKey, so a shared key would let
  // each panel's expand icon answer the other panel's tree.
  readonly gridItem: any = {
    key: 100710,
    config: { report_heading: this.gridTitle, widgetKey: 100710 },
  };

  // This panel is an aggregate view, so its Excel is the rows on screen rather
  // than the raw records the heat map exports. One column per level, so a row's
  // full branch survives the export — the screen's single indented column could
  // not say which parent a row belongs to once flattened.
  readonly gridExcelConfig: any[] = [
    ...this.levelExcelCols(),
    { header: "Processing Status", key: "processing_status", type: "string" },
    { header: "Expected", key: "expected", type: "number" },
    { header: "Sent", key: "sent", type: "number" },
    { header: "In Process", key: "inProcess", type: "number" },
    { header: "Not Started", key: "notStarted", type: "number" },
    { header: "Delayed", key: "delayed", type: "number" },
    { header: "Interim Sent", key: "interim", type: "number" },
    { header: "Failed", key: "failed", type: "number" },
  ];

  private reqSubcription: Subscription[] = [];

  // ---- persisted filter/date state (survives a page refresh) ----------------
  // Guards saving until the initial load + restore has finished, so seeding the
  // defaults during init never overwrites a previously saved selection.
  private stateReady = false;
  // The persisted selection for this dashboard, refreshed on every load —
  // pickDefaultSelection() needs it before the filters resolve, and
  // restoreFilterState() after the data arrives.
  private savedState: any = null;
  /**
   * The DYNAMIC filters' remembered selection — the portal's user state
   * (UserActivityService), not the localStorage blob above.
   *
   * They are split because they answer different questions. The chips derived
   * from the loaded records are this dashboard's own and survive a browser
   * restart, so they belong in localStorage keyed by dashboard. A dynamic
   * filter scopes the FETCH — pick a different brand and it is a different
   * dataset, a different date period and a different tree — so the portal
   * already treats it as user state and already clears it wherever the user
   * says "start here": a module click, or a dashboard card click. Keeping it in
   * the same place means this screen is cleared by those two gestures without
   * needing to know they happened.
   *
   * Read once per load, like savedState, because pickDefaultSelection() is
   * called per filter as its options land and would otherwise re-parse
   * sessionStorage for each one.
   */
  private userFilterState: Record<string, StaticBookmarkFilter> = {};
  private get storageKey(): string {
    return `dem-static-filter-state:${this.dashboardKey || "default"}`;
  }

  // ---- bookmarks ------------------------------------------------------------
  // `bk_mrk_key` off the route: present only when the Bookmarks panel opened this
  // screen (":module/static/:bk_mrk_key" — dashboard.routing.module.ts).
  private bookmarkKey: any;
  // What that bookmark saved, once fetched — the highest-priority source in
  // pickDefaultSelection(), above both the persisted selection and the portal's.
  // Null when there is no bookmark, or it carried nothing this screen can apply.
  private bookmarkFilters: StaticBookmarkState | null = null;
  // ngOnInit has issued its load, so a bk_mrk_key arriving after this point is a
  // second bookmark being opened on the screen already up — see the route
  // subscription.
  private loadIssued = false;

  // ---- status-overview popups (single click = comments, dbl click = chart) --
  // Distinguishes a single click from a double click on a heat cell.
  private clickTimer: any = null;

  // Resolved once: the definition behind the tile outline, so cellInterim() does
  // not re-scan INDICATORS for every cell it builds.
  private readonly BORDER_DEF = INDICATORS.find(
    (i) => i.name === BORDER_INDICATOR
  );

  // Minimal `item` the reused Modal / Comments components read from.
  private readonly commentItem: any = {
    key: 100709,
    config: {
      // 'dem', not 'dpm': comments.ts branches on this to decide which bucket a
      // note is filed in, so a DPM value here would put extract notes where the
      // production DEM widget can never read them.
      type_of_page: { name: "DEM", value: "dem" },
      tool_tip_type: { name: "DEM", value: "dem" },
      max_comment_length: 250,
      // The columns a note is keyed by. `cycle_dt` is the extract feed's date
      // column — DPM's `cal_day` does not exist on these records, and comments.ts
      // silently falls back to `file_type` for a column it cannot find, which
      // would file every day's note under one key.
      notes_selected_key: [
        { api_resp_column_nm: "source_name" },
        { api_resp_column_nm: "file_type" },
        { api_resp_column_nm: "cycle_dt" },
      ],
    },
  };

  constructor(
    private sessionService: SessionService,
    private router: ActivatedRoute,
    private dashboardService: DashboardService,
    private navigationService: NavigationService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private filterService: FilterService,
    private dpmService: DpmStaticService,
    // Where this screen's dynamic filters (Brand) are remembered across a
    // navigation — the portal's own user state, which a module click or a
    // dashboard card click clears. See pickDefaultSelection / seedFilterState.
    private userActivityService: UserActivityService,
    // The component's own element, used to reach the History calendar's tiles —
    // those live outside #heatScrollEl (the strip's ref), and a tile's rendered
    // width is what measureTileBadgeCap() needs in both views.
    private host: ElementRef<HTMLElement>,
    // Registers the selection pill's drag listeners outside Angular — see
    // onToolbarDragStart() for why a mousemove must not run change detection here.
    private zone: NgZone
  ) {
    this.reqSubcription.push(
      combineLatest([this.router.params, this.router.queryParams]).subscribe(
        ([p, params]: [any, any]) => {
          this.moduleName = p["module"] || "";
          let decryptedParams = this.navigationService.decryptData(params);
          this.clientKey = decryptedParams["cl_key"];
          this.moduleKey = decryptedParams["md_key"];
          this.dashboardKey = decryptedParams["ds_key"];
          // Which of the dashboard card's two buttons opened this screen. Only
          // History sends the flag, so its absence is Live — the same reading
          // the dynamic page flow gives it.
          this.isHistory = String(decryptedParams["hist_flg"] || "") === "H";
          this.dashboardService.moduleKey = this.moduleKey;

          // Opening one of this screen's bookmarks lands on the same route with
          // the bookmark key appended, so this is where a bookmarked selection
          // announces itself.
          //
          // Compared rather than just assigned because the key can change while
          // this component stays alive: opening a second bookmark of the same
          // static dashboard is the same route with a different param, which
          // Angular answers by reusing the instance — no new construction, no
          // second ngOnInit. Without the reload below, the panel would look like
          // it had navigated while the screen kept the first bookmark's filters.
          const bookmarkKey = p["bk_mrk_key"];
          if (String(bookmarkKey ?? "") !== String(this.bookmarkKey ?? "")) {
            this.bookmarkKey = bookmarkKey;
            this.bookmarkFilters = null; // re-fetched for the new key
            // Not on the first emission: both route observables replay on
            // subscribe, so this runs inside the constructor, where ngOnInit's own
            // loadDashboard() is the one that reads it.
            if (this.loadIssued) this.loadDashboard();
          }

          // markForCheck, NOT detectChanges: both route observables replay on
          // subscribe, so this callback runs synchronously inside the
          // constructor — before the view exists. detectChanges() there throws
          // "ASSERTION ERROR: Should be run in update mode", because Angular is
          // still in creation mode for this component. Marking is enough either
          // way: the first emission is followed by the view's own first pass,
          // and a later one (switching static dashboards without leaving the
          // route) is picked up by the pass that emission is already inside.
          this.cdr.markForCheck();
        }
      )
    );
  }

  ngOnInit(): void {
    // The client's own mark, and the fallback for a brand that has none — the brand
    // logo replaces it as soon as the Brand chip resolves (applyBrandLogo).
    this.clientLogo = this.sessionService.getClintLogo();
    this.newColor = this.sessionService.getColorCodes();

    // Brand -> topnav logo. FilterService publishes here from getBrandLogo(), which
    // applyBrandLogo() calls on every load and every brand change; subscribed
    // before loadDashboard() below so the first brand is not missed. The empty case
    // is a brand the `logos` cache has no row for, which falls back to the client's
    // mark rather than leaving the previous brand's in the header.
    this.reqSubcription.push(
      this.filterService.logoChange.subscribe((res: any) => {
        this.clientLogo = res?.logo || this.sessionService.getClintLogo();
        this.cdr.markForCheck();
      })
    );

    // The topnav's Refresh button pushes a bare `true` onto filterQuery
    // (getRefreshData) — that is the signal to re-run this screen's query.
    // Anything else on that subject is a filter widget's emission from some
    // other page and is not ours to answer, so the value is checked rather
    // than just the notification.
    this.reqSubcription.push(
      this.filterService.filterQuery.subscribe((q: any) => {
        if (q === true && !this.loading) this.loadExecutionData();
      })
    );

    // The heat panel's Sort icon, now that the route has been read — see
    // heatSortIcon.
    this.heatSortIcon.activated = this.isHistory;

    // The filter bar's note, for the same reason and at the same point: it is
    // per-view, and the view is only known once the route has been read. This
    // settles the Live sentence, which is fixed; History's needs a range, so it is
    // left to buildDateColumns() and the box stays empty until the first load.
    this.updateNote();

    // This screen's page key, minted from the dashboard key — see staticPageKey().
    // Published before loadDashboard() below so it is in place for the very first
    // request: everything page-scoped reads it off FilterService, both the data calls
    // (through buildContext) and the topnav's Help icon, which is clickable from the
    // first frame and would otherwise post whatever page the portal last visited.
    //
    // Single-page screen, so the page number defaults to 1 and the key ends "11". A
    // second page would pass 2 here and get "21".
    this.dpmService.publishPageKey(this.dashboardKey);

    // icon-chart reports the Sort icon on filterService.triggerSort, and — unlike
    // the expand icon's expendTree — carries no widget key, so the guard is this
    // screen's own: only the History view has months to reorder, and only its
    // header offers the icon in the first place. That makes this a belt-and-braces
    // check rather than the thing keeping the Live view out of it.
    this.reqSubcription.push(
      this.filterService.triggerSort.subscribe(() => {
        if (this.isHistory) this.toggleCalSort();
      })
    );

    this.loadDashboard();
  }

  // ------------------------------------------------------------ load sequence
  /**
   * Everything this screen shows, in dependency order:
   *
   *   1. build the request context from the portal's own state (route keys,
   *      client / employee session, signed-in user) — DpmStaticService;
   *   2. if a bookmark opened this screen, read what it saved;
   *   3. load the dynamic filters (Brand today) and settle each one's
   *      selection — the bookmark's, if step 2 found one;
   *   4. only then fetch kpi/get-execution-details for those selections.
   *
   * Step 4 is deliberately inside step 3's callback: the execution call is
   * brand-scoped, so issuing it before a brand exists would either fail or
   * answer for the wrong one. Adding a dynamic filter changes no step —
   * the new entry resolves inside step 3 and its selection rides along into
   * step 4 through dynamicFilterParams().
   *
   * Step 2 is chained ahead of step 3 rather than run beside it for the same kind
   * of reason: pickDefaultSelection() is called as each filter's options land, so
   * a bookmark that arrived a moment later would be read after the selection it
   * was supposed to decide. It resolves to null immediately when there is no
   * bookmark, so an ordinary load still issues exactly the two requests it did.
   */
  private loadDashboard(): void {
    this.loading = true;
    // Before the first await of any kind: the route subscription reads this to
    // tell a bookmark arriving on the screen already up from the one that opened
    // it, and its own emission can land while the requests below are in flight.
    this.loadIssued = true;
    // Name the filters before step 2 has even been issued, so the strip and the
    // panel show what this screen filters by for the whole of the load rather
    // than appearing to have no filters. Only on the first load — a refresh
    // keeps the chips it already has, so their values don't blink away and come
    // back.
    if (!this.filters.length) this.buildFilterSkeleton();
    this.apiContext = this.dpmService.buildContext({
      dashboardKey: this.dashboardKey,
      clientKey: this.clientKey,
      moduleKey: this.moduleKey,
    });
    // Nothing of the previous page, and — for a fresh arrival — nothing of the
    // previous visit either.
    //
    // Arriving from this dashboard's own card (Live or History) means "start here",
    // so the selection the last visit left behind is dropped before it can be read: a
    // screen that comes up narrowed by filters the user does not remember setting —
    // Reset chip already lit, tiles already missing — reads as broken. Every OTHER
    // arrival keeps it, which is what the state is for: a browser refresh, and a trip
    // out to a detail page and back, return to the view the user had built. The flag
    // is consumed either way, so it cannot sit in sessionStorage waiting to fire at
    // the next load.
    //
    // The line sql-cache-static draws in the same place, and the one openPage() draws
    // for a dynamic dashboard. See static-dashboard-entry.ts.
    // Both halves of what this screen remembers: the localStorage blob its own
    // chips live in, and the user state its dynamic filters live in.
    // openPage() has already cleared the second on its way here, so this is the
    // belt to that braces — it also covers an arrival the card click did not
    // route (a restored tab, a pasted URL carrying the flag).
    if (consumeStaticDashboardEntry(this.dashboardKey)) {
      this.clearFilterState();
      this.userActivityService.clearStaticFilters();
    }
    this.resetSharedFilterState();
    // Both read before the filters resolve: pickDefaultSelection() needs to know
    // what this screen had selected last time.
    this.savedState = this.readSavedState();
    this.userFilterState = this.userActivityService.getStaticFilters(
      this.dashboardKey
    );

    this.reqSubcription.push(
      this.dpmService
        .loadBookmarkFilters(this.bookmarkKey)
        .pipe(
          switchMap((bookmarked) => {
            this.bookmarkFilters = bookmarked;
            return this.dpmService.loadDynamicFilters(
              this.apiContext,
              (def, options) => this.pickDefaultSelection(def, options),
              DEM_DYNAMIC_FILTERS
            );
          })
        )
        .subscribe((states) => {
          this.dynamicFilterStates = states;
          // Light the API-backed chips up now rather than after the (much slower)
          // execution call — see applyDynamicFilterOptions().
          this.applyDynamicFilterOptions();
          // A filter that came back empty (or could not be loaded) has nothing
          // to scope the data by, so the screen renders empty rather than
          // asking for every brand at once.
          if (!states.length || states.every((s) => s.selected.length)) {
            this.loadExecutionData();
          } else {
            this.data = [];
            this.onDataLoaded();
          }
        })
    );
  }

  /**
   * The dashboard data for the current filter selections. Also the refetch path
   * when a dynamic filter changes — see onFilterApplied().
   */
  private loadExecutionData(): void {
    // Which load this is. The Brand chip can now be opened while the screen is still
    // loading (see applyDynamicFilterOptions), so two of these can be in flight at
    // once — and the older one is describing a brand the user has already moved off.
    // Only the newest is allowed to land; the responses are not guaranteed to arrive
    // in the order they were asked for.
    const seq = ++this.loadSeq;
    // Runs on the initial load and after every brand change, which is exactly
    // when the header's brand label and refreshed-date need re-publishing.
    this.publishTopnavContext();
    // Suspend persistence while the screen is rebuilt from the response, so
    // the intermediate state never overwrites what the user actually chose.
    this.stateReady = false;
    this.loading = true;
    // The panels are about to be replaced wholesale, so a tooltip left open
    // over the old tiles would be pointing at nothing.
    this.hideCellTip();
    this.cdr.detectChanges();
    // The tree order rides along with the data: it is scoped by the same brand,
    // and it has to be in hand before the first buildHeatmap() — a tree drawn
    // first and re-sorted after would visibly reorder itself.
    this.reqSubcription.push(
      forkJoin([
        this.dpmService.getExecutionDetails(
          this.apiContext,
          this.dynamicFilterParams(),
          // D for daily, H for history — the same two the dashboard card's Live
          // and History buttons come down to on DPM.
          this.isHistory ? DPM_REPORT_TYP.history : DPM_REPORT_TYP.live,
          DEM_EXECUTION_API_KEY
        ),
        // Which monitor is asking, taken from the same field status-overview
        // reads it from (its item config's type_of_page.name) so the two screens
        // cannot disagree about what they are.
        this.dpmService.getTreeOrder(
          this.apiContext,
          this.HEAT_LEVELS,
          this.commentItem.config.type_of_page.name,
          this.dynamicFilterParams()
        ),
      ]).subscribe(([rows, order]) => {
        // A newer load has been issued since this one — its answer is the one the
        // screen is waiting for, so this reply is dropped rather than overwriting it.
        if (seq !== this.loadSeq) return;
        // Trends are stamped once, on the whole response and before any
        // filtering: they compare a file type's consecutive cycles, so a day a
        // chip drops would otherwise silently become "the previous day" for the
        // next one. See stampTrends().
        this.data = this.stampTrends(rows || []);
        this.treeOrder = order || {};
        this.onDataLoaded();
      })
    );
  }

  /** Build the screen from whatever `data` now holds. */
  private onDataLoaded(): void {
    this.brandName = this.selectedLabels("brand_name")[0] || "";
    const days = [...new Set(this.data.map((r) => this.day(r)))].sort();
    this.dataMin = days[0] || "";
    this.dataMax = days[days.length - 1] || "";
    // Re-read on every load: a refetch has just persisted the new selection,
    // so this carries the user's other filters across a brand change.
    this.savedState = this.readSavedState();
    this.buildFilters(); // dynamic options from the API, the rest from the data
    this.initTimeCycle(); // default cycle range -> fromDate/toDate
    this.restoreFilterState(); // override defaults with any saved selection
    // After the restore, not before: what was restored is what the chips below
    // are scoped by. buildFilters() gives every chip the full list because that
    // is right for a screen on its defaults; this narrows the children once the
    // parents are on whatever the visit actually opened with.
    this.cascadeFilters();
    // Cleared before applyFilters(), so the panels come out of the loading
    // state in the same change-detection pass that fills them.
    this.loading = false;
    // Before applyFilters(), not after: publishing on filterQuery makes every
    // icon-chart on the page clear its own expand flag, and the rebuild inside
    // applyFilters() ends with syncHeatExpandIcon() re-asserting it. This order
    // leaves the panel headers agreeing with the tree they sit over.
    this.publishPanelContext();
    this.applyFilters(); // reflect the restored state
    this.stateReady = true; // from here on, user actions persist
    this.cdr.detectChanges();
    // The tiles only exist now, so ngAfterViewInit's measurement pass had
    // nothing to measure — re-fit the leaf-count font and the badge cap against
    // the real layout.
    this.scheduleTileMetrics();
  }

  // ======================================================== derived trends
  /**
   * Stamp `sent_records_up` / `_down` / `_nochange` onto every record.
   *
   * These three columns do not come back from the API. The live widget derives
   * them, and this is that derivation — status-overview.ts, the block immediately
   * above monthSortOrder() — reproduced so a static tile carries the same trend
   * badge the real one would:
   *
   *   - records are grouped by their tree path and read in cycle order;
   *   - the first record of a group has no predecessor, so it gets no trend;
   *   - a null `sent_records` on either side gets no trend, rather than being
   *     read as a drop to zero;
   *   - otherwise it is a plain comparison with the previous cycle.
   *
   * Grouping is by tree path, not by file name: one cycle can hold several files
   * for a file type (the reference screen's 21 Jul PSS tile is two of them), and
   * the trend belongs to the row, so the comparison follows the row's own
   * sequence. Two records sharing a cycle are compared with each other, which is
   * what the widget does too.
   *
   * Mutates the records in place and returns the list unsorted, as the widget
   * does: the sort here only establishes the order the comparison walks in, while
   * the popups, the Excel export and the tiles all read these same objects and
   * should see the same columns.
   */
  private stampTrends(rows: any[]): any[] {
    const list = Array.isArray(rows) ? rows : [];
    const clear = (r: any, trend: string) => {
      r.sent_records_trend = trend;
      r.sent_records_up = "N";
      r.sent_records_down = "N";
      r.sent_records_nochange = "N";
    };
    // The on-hold trend is stamped alongside the sent one because the tooltip
    // prints an arrow for it too (dem/cell-info.ts reads `on_hold_trend`). No
    // badge reads it, which is why it has no up/down/nochange flags — the arrow is
    // driven by the trend word itself.
    const holdTrend = (r: any, trend: string) => {
      r.on_hold_trend = trend;
      r.on_hold_up = trend === "up" ? "Y" : "N";
      r.on_hold_down = trend === "down" ? "Y" : "N";
      r.on_hold_nochange = trend === "nochange" ? "Y" : "N";
    };

    const sorted = [...list].sort((a, b) => {
      for (const col of this.HEAT_LEVELS) {
        const av = this.n(a[col]);
        const bv = this.n(b[col]);
        if (av !== bv) return av.localeCompare(bv);
      }
      return this.day(a).localeCompare(this.day(b));
    });

    for (let i = 0; i < sorted.length; i++) {
      const current = sorted[i];
      const prev = sorted[i - 1];
      const sameGroup =
        i > 0 &&
        this.HEAT_LEVELS.every(
          (col) => this.n(current[col]) === this.n(prev[col])
        );

      if (!sameGroup) {
        clear(current, "no trend");
        holdTrend(current, "no trend");
        continue;
      }

      // The two trends are decided independently, exactly as the widget does it: a
      // null `sent_records` suppresses the sent arrow only, and the on-hold
      // comparison still runs (its column is a plain count that is 0 rather than
      // null on a file that never arrived).
      if (current.sent_records == null || prev.sent_records == null) {
        clear(current, "no trend");
      } else {
        const currSent = Number(current.sent_records) || 0;
        const prevSent = Number(prev.sent_records) || 0;
        if (currSent > prevSent) {
          clear(current, "up");
          current.sent_records_up = "Y";
        } else if (currSent < prevSent) {
          clear(current, "down");
          current.sent_records_down = "Y";
        } else {
          clear(current, "nochange");
          current.sent_records_nochange = "Y";
        }
      }

      const currHold = Number(current.on_hold) || 0;
      const prevHold = Number(prev.on_hold) || 0;
      holdTrend(
        current,
        currHold > prevHold ? "up" : currHold < prevHold ? "down" : "nochange"
      );
    }

    return list;
  }

  /**
   * The dynamic filters' selections as request parameters — what scopes
   * kpi/get-execution-details. Empty selections are left out rather than sent
   * as an empty list, which the backend would read as an explicit "none".
   */
  private dynamicFilterParams(): Record<string, any> {
    const params: Record<string, any> = {};
    this.dynamicFilterStates.forEach((s) => {
      if (!s.selected.length) return;
      params[s.def.requestField] = s.def.single ? s.selected[0] : s.selected;
    });
    return params;
  }

  /**
   * Which options a dynamic filter opens on, most specific first:
   *   1. what the bookmark that opened this screen saved (if one did);
   *   2. what this screen had selected last time (persisted per dashboard);
   *   3. the selection the rest of the portal is already on (Brand is chosen
   *      once per module and every dashboard follows it);
   *   4. the first option the API returned.
   *
   * The bookmark outranks the persisted selection deliberately: opening a bookmark
   * is a request for the state it was taken in, and the persisted copy is only
   * "where this screen was left", which is exactly what a bookmark is meant to
   * override. It stays the winner for that load alone — the moment the user changes
   * a filter, the normal save runs and their choice is what a refresh restores.
   */
  private pickDefaultSelection(
    def: DpmDynamicFilterDef,
    options: DpmFilterOption[]
  ): any[] {
    if (!options.length) return [];

    // 1. A bookmark, if one opened this screen. Nothing it saved being on offer
    //    any more (a brand the user lost access to, say) falls through to the
    //    sources below rather than showing an empty screen — the bookmark is
    //    stale, not the dashboard.
    const fromBookmark = this.resolveSaved(
      def,
      options,
      this.bookmarkFilters?.dynamic?.[def.key]
    );
    if (fromBookmark.length) return fromBookmark;

    // 2. What this filter was left on — the portal's user state, which lives
    //    exactly as long as the user has not said "start here" (see
    //    `userFilterState`). This used to read the localStorage blob instead,
    //    which outlived a module switch and a trip through another dashboard,
    //    so a brand chosen days ago could still be scoping this screen's fetch.
    const fromUserState = this.resolveSaved(
      def,
      options,
      this.userFilterState?.[def.key]
    );
    if (fromUserState.length) return fromUserState;

    const shared = this.dpmService.sharedSelection(def);
    if (shared !== null && shared !== undefined) {
      const fromShared = options.filter(
        (o) => String(o.key) === String(shared)
      );
      if (fromShared.length) return fromShared.map((o) => o.key);
    }

    return [options[0].key];
  }

  /**
   * Resolve a remembered selection against the options actually on offer.
   *
   * The one matching rule the bookmark and the user state share — they are the
   * same {keys, labels} pair (dynamicFilterSnapshot builds both), so they are
   * read back the same way. Request value first, display value as the fallback:
   * see StaticBookmarkFilter. String-compared because a key survives the JSON
   * round trip as whatever the API sent, number or string.
   *
   * Empty when nothing it names is still available, which is the caller's cue to
   * fall through to its next source rather than to select nothing.
   */
  private resolveSaved(
    def: DpmDynamicFilterDef,
    options: DpmFilterOption[],
    saved: StaticBookmarkFilter | null | undefined
  ): any[] {
    if (!saved) return [];
    const keys = (saved.keys || []).map((k: any) => String(k));
    const labels = saved.labels || [];
    const matched = options.filter(
      (o) => keys.includes(String(o.key)) || labels.includes(o.label)
    );
    if (!matched.length) return [];
    return (def.single ? matched.slice(0, 1) : matched).map((o) => o.key);
  }

  /**
   * Hand the shared topnav this screen's brand.
   *
   * FilterService.lastRefreshedDate is the channel a dynamic dashboard's filter
   * widget uses for the same purpose, and topnav does three things with it: it
   * labels the header with `brand_name`, it posts
   * kpi/get-last-refreshed-date for that brand (plus the dashboard and module
   * keys it already has from the route) and prints the answer as "Last
   * refreshed", and it switches that stamp on at all. So publishing here is
   * what makes the header's brand-dependent API call happen — and re-publishing
   * on a brand change is what keeps the date describing the brand on screen.
   *
   * It is also where the header's LOGO comes from, for the same reason and by the
   * same route pages.component takes: the mark beside the page name is the brand's,
   * so it has to follow the Brand chip rather than stay on whatever the client
   * logged in as. See applyBrandLogo().
   */
  private publishTopnavContext(): void {
    const state = this.dynamicFilterStates.find(
      (s) => s.def.key === "brand_name"
    );
    const brand = state?.options.find((o) =>
      state.selected.some((k) => String(k) === String(o.key))
    );
    if (!brand) return;
    this.applyBrandLogo(brand.key);
    this.filterService.lastRefreshedDate.next({
      brand_key: brand.key,
      brand_name: brand.label,
    });
  }

  /**
   * The selected brand's logo, into the topnav's [clientLogo].
   *
   * FilterService.getBrandLogo() is the portal's one answer to "which mark does this
   * brand wear" — it reads the `logos` session cache home.component fills on client
   * selection (api/get-logos) and returns that brand's logo_path, which is exactly
   * the value pages.component assigns [clientLogo] from for a
   * dynamic dashboard. Calling it also announces the same thing on
   * `logoChange` (subscribed in ngOnInit), which is what covers the two cases the
   * return value cannot: a brand the cache has no row for, where it returns nothing
   * and publishes an empty logo instead.
   *
   * The client logo stays the fallback, so a brand without a mark of its own leaves
   * the header as it was rather than blank.
   */
  private applyBrandLogo(brandKey: any): void {
    const logo = this.filterService.getBrandLogo({ brand_key: brandKey });
    if (logo) this.clientLogo = logo;
  }

  /**
   * Switch the topnav's Bookmarks and Notes icons on, and hand their panels the
   * context they answer with.
   *
   * Both icons render for any dashboard_key, so they are on this header
   * already — but they wear .du-disabled-filterloads (half opacity,
   * pointer-events: none) until topnav's own `filterFlag` flips, and the unread
   * badges beside them are fetched in the very same subscription. On a dynamic
   * dashboard both happen when the filter widget applies, because FilterService
   * publishes `baseQuery` on `filterQuery` as the last thing it does. This
   * screen has no filter widget, so it publishes the same value at the same
   * point in its own load — once its filters have settled, which is also when
   * the brand those panels report against is known. Without this the icons stay
   * greyed out until topnav's 25s fallback, and their badges never arrive at
   * all.
   *
   * `baseQuery` rather than `true` on purpose: `true` is the Refresh button's
   * signal, which this screen answers by reloading (see the ngOnInit
   * subscription), so publishing that here would loop.
   */
  private publishPanelContext(): void {
    // Seeded exactly as the cell popups seed it, so a note filed from the
    // topnav is filed against the same dashboard, page and brand as one filed
    // from a tile — and so the Bookmarks panel, which does nothing at all while
    // baseQuery is empty, has something to read.
    this.seedFilterState();
    this.filterService.filterQuery.next(this.filterService.baseQuery);
  }

  /** Display labels of a dynamic filter's current selection. */
  private selectedLabels(key: string): string[] {
    const state = this.dynamicFilterStates.find((s) => s.def.key === key);
    if (!state) return [];
    return state.options
      .filter((o) => state.selected.some((k) => String(k) === String(o.key)))
      .map((o) => o.label);
  }

  /** Set by ngOnDestroy, so work scheduled on a promise (see refitOnFontsReady)
   *  can tell that the view it would refresh is gone. */
  private destroyed = false;

  ngAfterViewInit(): void {
    this.updateBadgeOverflow();
    // First real measurement — the heat table only has a laid-out width now.
    this.scheduleTileMetrics();
    this.refitOnFontsReady();
  }

  /**
   * Re-fit the tree column once Poppins has actually loaded.
   *
   * measureLabel() sizes labels and pills on a canvas set to
   * "Poppins, sans-serif" — but a canvas does not wait for a webfont. If the face
   * is still in flight on the first pass, it silently measures the FALLBACK, whose
   * glyphs are narrower, and every width comes out short. The text then paints in
   * Poppins, wider than the box that was sized for it, and clips.
   *
   * That is invisible on a warm cache and reproducible on a cold one, which is
   * exactly the kind of bug worth spending one extra pass on. document.fonts.ready
   * settles immediately when the font is already there, so the common case costs
   * nothing.
   */
  private refitOnFontsReady(): void {
    const fonts = (document as any).fonts;
    if (!fonts?.ready?.then) return;
    fonts.ready.then(() => {
      // The view can be gone by the time a webfont settles — detectChanges() on a
      // destroyed view throws.
      if (this.destroyed) return;
      this.updateHeatTreeWidth();
      this.cdr.detectChanges();
    });
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    this.updateBadgeOverflow();
    // Tile widths track the viewport, so the leaf-count font and the badge cap
    // both have to re-fit.
    this.scheduleTileMetrics();
    // The selection pill is placed against a measured rect, so a resize moves
    // the widget out from under it.
    this.positionToolbar();
  }

  // Close the open filter-badge dropdown and the date-range popup on any click
  // outside them. The triggers and the menus themselves stopPropagation, so a
  // click only reaches the document when it is genuinely outside — this works
  // regardless of z-index stacking (the transparent backdrops can't, because
  // the filter bar sits above them).
  @HostListener("document:click")
  onDocumentClick(): void {
    if (this.openKey || this.dateMenuOpen) {
      this.openKey = null;
      this.dateMenuOpen = false;
    }
    // An open selection is deliberately NOT touched here. Clicking away used to
    // confirm it — Qlik's rule, and the right one back when a plain click was the
    // pick gesture, because then "clicking away" was the unambiguous end of a
    // gesture you had just been performing.
    //
    // It is the wrong rule now that picking takes ctrl/cmd. A plain click is
    // ordinary page interaction again: expanding a tree, folding a branch, going
    // fullscreen, opening a menu, reading a figure. None of those says anything
    // about a selection, and every one of them was applying it — the user got a
    // filter chip they never asked for and no obvious way to know which click
    // caused it. Guarding the chrome case by case could not keep up either — there
    // was a selector list for the panel headers, the card help icons and the CDK
    // overlay, plus a test for the widget that owned the selection, and things
    // still slipped through. The miss was structural, not a matter of one more
    // selector, so the list is gone with the rule it served.
    //
    // So the popup's Apply is the only thing that commits, and the only things
    // that end a selection are Apply, its Clear button, and Escape. A stray click
    // now costs nothing: the popup stays exactly where it was, still listing the
    // picks, still one click from either outcome.
  }

  /**
   * Escape closes an open selection WITHOUT applying it — the way out when the
   * picks were a mistake, and the counterpart to the popup's Clear button.
   *
   * Committed filters stay: they are undone from their badges, or by Reset.
   */
  @HostListener("document:keydown.escape")
  onEscape(): void {
    this.cancelPending();
  }

  // Show the scroll arrows only when the badges overflow the strip.
  private updateBadgeOverflow(): void {
    setTimeout(() => {
      const el = this.badgeScroller?.nativeElement;
      this.badgesOverflow = el ? el.scrollWidth > el.clientWidth + 2 : false;
    });
  }

  // ---------------------------------------------------------- time cycle
  get cycleList() {
    return this.cycleFormats.filter((c) => this.timeCycles.includes(c.value));
  }
  get showCycleSelector(): boolean {
    return this.cycleList.length > 1;
  }

  private initTimeCycle(): void {
    this.selectedCycle = this.timeCycles.includes("D")
      ? "D"
      : this.timeCycles[0] || "D";
    this.setDataPeriodRange(this.selectedCycle);
    // remember the default (data-period) range so we can detect changes / reset
    this.defaultFrom = this.fromDate;
    this.defaultTo = this.toDate;
  }

  // true once the user has moved the date range off the default data period
  get dateChanged(): boolean {
    return this.fromDate !== this.defaultFrom || this.toDate !== this.defaultTo;
  }

  changeCycle(cycle: string): void {
    if (cycle === this.selectedCycle) return;
    this.selectedCycle = cycle;
    this.setDataPeriodRange(cycle);
  }

  // The date range is the data period itself — anchored on the latest day the
  // loaded records cover, going back per cycle (D: the whole period, W: 52
  // weeks, M/Q/Y: 1 year).
  //
  // Deliberately no kpi/get-timecycle-date call: that endpoint answers for a
  // configured dashboard, where this screen's range is a property of the
  // kpi/get-execution-details response it already has in hand. Asking would
  // only introduce a second, possibly disagreeing, answer — and a window the
  // data does not actually fill.
  private setDataPeriodRange(cycle: string): void {
    const anchor = this.dataMax ? moment(this.dataMax) : moment();
    let from: moment.Moment;
    switch (cycle) {
      case "W":
        from = anchor.clone().subtract(52, "weeks");
        break;
      case "M":
      case "Q":
      case "Y":
        from = anchor.clone().subtract(1, "year");
        break;
      case "D":
      default:
        // Daily: default to the full available data period (min .. max day).
        from = this.dataMin
          ? moment(this.dataMin)
          : anchor.clone().subtract(15, "days");
        break;
    }
    this.setWindow(from.toDate(), anchor.toDate(), cycle);
  }

  // Snap a from/to window to the cycle's boundaries and set picker + bounds.
  private setWindow(winFrom: Date, winEnd: Date, cycle: string): void {
    let f = moment(winFrom);
    let t = moment(winEnd);
    if (cycle === "W") {
      f = f.startOf("isoWeek");
      t = t.endOf("isoWeek");
    } else if (cycle === "M") {
      f = f.startOf("month");
      t = t.endOf("month");
    } else if (cycle === "Q") {
      f = f.startOf("quarter");
      t = t.endOf("quarter");
    } else if (cycle === "Y") {
      f = f.startOf("year");
      t = t.endOf("year");
    }
    this.windowStart = f.toDate();
    this.windowEnd = t.toDate();
    this.fromDateObj = this.windowStart;
    this.toDateObj = this.windowEnd;
    this.minFromDate = this.windowStart;
    this.maxFromDate = this.toDateObj;
    this.minToDate = this.fromDateObj;
    this.maxToDate = this.windowEnd;
    if (cycle === "Q") this.buildQuarterOptions();
    this.commitDates();
  }

  private commitDates(): void {
    if (this.fromDateObj)
      this.fromDate = moment(this.fromDateObj).format("YYYY-MM-DD");
    if (this.toDateObj) this.toDate = moment(this.toDateObj).format("YYYY-MM-DD");
    this.applyFilters();
    this.saveFilterState(); // no-op until the initial load has finished
  }

  // ---- picker change handlers (D/W day, M month, Y year) -----------------
  onFromPicked(d: Date | null): void {
    if (!d) return;
    let f = moment(d);
    if (this.selectedCycle === "W") f = f.startOf("isoWeek");
    else if (this.selectedCycle === "M") f = f.startOf("month");
    else if (this.selectedCycle === "Y") f = f.startOf("year");
    this.fromDateObj = f.toDate();
    this.minToDate = this.fromDateObj;
    this.commitDates();
  }
  onToPicked(d: Date | null): void {
    if (!d) return;
    let t = moment(d);
    if (this.selectedCycle === "W") t = t.endOf("isoWeek");
    else if (this.selectedCycle === "M") t = t.endOf("month");
    else if (this.selectedCycle === "Y") t = t.endOf("year");
    this.toDateObj = t.toDate();
    this.maxFromDate = this.toDateObj;
    this.commitDates();
  }
  onMonthFrom(d: Date, dp: { close?: () => void }): void {
    this.onFromPicked(d);
    dp?.close?.();
  }
  onMonthTo(d: Date, dp: { close?: () => void }): void {
    this.onToPicked(d);
    dp?.close?.();
  }

  // ---- quarter (Q) --------------------------------------------------------
  private quarterOpt(q: string, year: number): any {
    const range: any = {
      Q1: ["01-01", "03-31", "Jan - Mar"],
      Q2: ["04-01", "06-30", "Apr - Jun"],
      Q3: ["07-01", "09-30", "Jul - Sep"],
      Q4: ["10-01", "12-31", "Oct - Dec"],
    };
    return {
      value: q,
      year,
      months: range[q][2],
      label: `${q} ${year}`,
      from_date: `${year}-${range[q][0]}`,
      to_date: `${year}-${range[q][1]}`,
    };
  }
  private buildQuarterOptions(): void {
    const sy = moment(this.windowStart).year();
    const ey = moment(this.windowEnd).year();
    const opts: any[] = [];
    for (let y = sy; y <= ey; y++) {
      ["Q1", "Q2", "Q3", "Q4"].forEach((q) => opts.push(this.quarterOpt(q, y)));
    }
    this.fromOption = opts;
    this.toOption = [...opts];
    const fq = "Q" + (Math.floor(moment(this.windowStart).month() / 3) + 1);
    const tq = "Q" + (Math.floor(moment(this.windowEnd).month() / 3) + 1);
    this.selectedFromOption =
      opts.find((o) => o.value === fq && o.year === sy) || opts[0];
    this.selectedToOption =
      opts.find((o) => o.value === tq && o.year === ey) ||
      opts[opts.length - 1];
  }
  applyQuarter(): void {
    if (this.selectedFromOption)
      this.fromDateObj = moment(this.selectedFromOption.from_date).toDate();
    if (this.selectedToOption)
      this.toDateObj = moment(this.selectedToOption.to_date).toDate();
    this.commitDates();
  }

  // ---- date badge -> app-calendar popup ----------------------------------
  openDateMenu(ev: MouseEvent): void {
    ev.stopPropagation();
    if (this.dateMenuOpen) {
      this.dateMenuOpen = false;
      return;
    }
    this.closeDropdown(); // don't leave a badge dropdown open behind it
    this.buildDateCalendar();
    const el = ev.currentTarget as HTMLElement;
    if (el) {
      const r = el.getBoundingClientRect();
      const width = 580; // dual calendar (from + to) is wide
      this.datePos = {
        left: Math.max(8, Math.min(r.left, window.innerWidth - width - 12)),
        top: r.bottom + 4,
      };
    }
    this.dateMenuOpen = true;
  }
  closeDateMenu(): void {
    this.dateMenuOpen = false;
  }

  private buildDateCalendar(): void {
    this.dateConfig = {
      selectedType: "D",
      option: {
        minDate: this.dataMin ? new Date(this.dataMin) : new Date(),
        maxDate: this.dataMax ? new Date(this.dataMax) : new Date(),
        isRestriction: "no",
        compulsaryFilter: true,
      },
    };
    this.dateSelectedData = {
      from_date: this.fromDateObj || new Date(this.fromDate),
      to_date: this.toDateObj || new Date(this.toDate),
    };
  }

  onCalendarApply(ev: { from_date: Date; to_date: Date }): void {
    if (ev?.from_date) this.fromDateObj = new Date(ev.from_date);
    if (ev?.to_date) this.toDateObj = new Date(ev.to_date);
    this.dateMenuOpen = false;
    this.commitDates();
  }

  // ---------------------------------------------------------------- helpers
  private n(v: any): string {
    return (v == null ? "" : v).toString().trim();
  }
  private num(v: any): number {
    return v == null ? 0 : Number(v) || 0;
  }
  private day(r: any): string {
    // `cycle_dt`, not DPM's `cal_day` — the extract feed's date column, and the one
    // dem/config.ts's xAccessor parses (`timeParser(record.cycle_dt)`).
    //
    // Sliced rather than parsed: the API sends "2026-07-21T00:00:00" with no zone,
    // so `new Date(...)` would read it as local time and a machine behind UTC would
    // move every record to the day before.
    return this.n(r.cycle_dt).slice(0, 10);
  }
  private distinct(key: string): string[] {
    return this.distinctIn(this.data, key);
  }

  /** distinct() over an arbitrary set of rows — what a cascaded chip's options
   *  are drawn from. See cascadeFilters(). */
  private distinctIn(rows: any[], key: string): string[] {
    return sortFilterValues([
      ...new Set(rows.map((r) => this.n(r[key])).filter((x) => x && x !== "null"))
    ]);
  }

  /**
   * Does one record survive one chip?
   *
   * Lifted out of applyFilters(), where it was inline, so the cascade and the
   * filtering cannot answer differently: the rows a chip's options are computed
   * from have to be exactly the rows its parents let through, or the list would
   * offer a value that selects nothing.
   */
  private rowPasses(f: FilterDef, r: any): boolean {
    // A filter with no values of its own has nothing to say about a row — an
    // unloaded chip's empty `applied` set would otherwise match nothing and
    // blank the whole screen.
    if (!this.hasOptions(f)) return true;
    const v = this.n(r[f.key]);
    // rows with no value for a dimension are never filtered out by it
    if (!v || v === "null") return true;
    return f.applied.has(v);
  }

  /**
   * Narrow each chip's values to what its PARENTS still allow.
   *
   * The filter list is a hierarchy read top to bottom — on DPM, Brand, Source
   * Type, Source Group, Source, File Type, then Status — and that declared
   * sequence IS the parent relation. So a chip offers the values that actually
   * exist under the chips above it and no others: pick one Source Type and the
   * Source list holds that type's sources, rather than every source in the brand
   * with most of them selecting nothing.
   *
   * Parents only, never children. Narrowing a chip by the ones BELOW it as well
   * is the classic cascade trap: having picked a Source, the Source Type list
   * would collapse to that source's own type, and there would be no way back up
   * without a Reset. Downward only, so every level can still be widened.
   *
   * Nothing here reads the date range or the cross-filter selections either, and
   * deliberately: those are ways of looking at the data, not of scoping what the
   * data contains. A chip whose values came and went as the date moved would
   * drop a selection the user made, silently, for a reason the strip does not
   * show.
   *
   * Selections are repaired as it goes, because a shrunken list can strand one.
   * What survives the intersection is kept — re-picking a Source Type the user
   * has already narrowed within should not throw that narrowing away — and a
   * selection with nothing left falls back to the chip's default, which for a
   * multi-select is everything now on offer. `defaultApplied` moves with the
   * options for the same reason: it is what isPartial() measures "off default"
   * against, so leaving it at the full list would light the Reset chip for a
   * narrowing the user never made.
   */
  private cascadeFilters(): void {
    let rows = this.data;
    this.filters.forEach((f) => {
      // A dynamic chip's values come from its own API and are already scoped by
      // the filters it depends on (DpmDynamicFilterDef.dependsOn), server side.
      // Its selection still narrows the rows the chips BELOW it see, so it takes
      // part in the walk — it just does not have its own list rebuilt here.
      if (!this.isDynamicFilter(f)) {
        const options = this.distinctIn(rows, f.key);
        const defaultApplied = f.single ? options.slice(0, 1) : options;

        // Was this chip expressing a choice, or just saying "everything"?
        // Measured against its OLD default, before that is overwritten below.
        //
        // A chip on its default is not a narrowing to preserve, and preserving
        // it anyway is wrong in both directions. Widen the parent and the child
        // would stay pinned to yesterday's shorter list — Source stuck at the
        // three SFTP sources after Source Type went back to all of them, showing
        // 3/4 and an amber count for a narrowing the user never made. Reset has
        // the same shape: it puts every chip back on the default it had at that
        // moment, which for a child is its CASCADED default, so intersecting
        // would hold the reset chips at exactly the values Reset was pressed to
        // get rid of.
        //
        // So only an explicit narrowing survives, and it survives only as far as
        // the new list allows: what is left of it after the intersection, or the
        // new default when the parent change stranded all of it.
        const onDefault = this.sameSelection([...f.applied], f.defaultApplied);
        const kept = onDefault
          ? []
          : [...f.applied].filter((v) => options.includes(v));

        f.options = options;
        f.defaultApplied = [...defaultApplied];
        f.applied = new Set(kept.length ? kept : defaultApplied);
        f.checked = [...f.applied];
      }
      rows = rows.filter((r) => this.rowPasses(f, r));
    });
  }

  /**
   * A record's status, in the legend's spelling. Every read of file_status goes
   * through here — the JSON writes "To be Processed" where the legend, the
   * priority table and the no-count list all say "To Be Processed", and a raw
   * read leaves that status matching none of them.
   */
  private status(r: any): string {
    return canonicalStatus(this.n(r.file_status));
  }

  private cellStatus(rows: any[]): string {
    if (!rows.length) return "Expected";
    let best = "Expected";
    let bp = -1;
    rows.forEach((r) => {
      const s = this.status(r);
      const p = STATUS_PRIORITY[s] ?? 1;
      if (p > bp) {
        bp = p;
        best = s;
      }
    });
    return best;
  }

  // Resolved from STATUS_LEGEND, so a cell fill can never disagree with the
  // legend swatch that is supposed to explain it.
  private statusColor(status: string): string {
    return statusFill(status);
  }

  /**
   * The counts every panel on this screen is built from — the KPI row, the donut
   * and the extract grid's rows all read this one shape, so a figure can never
   * disagree with itself between them.
   *
   * All five are FILE counts, not record counts: one row of the response is one
   * expected file for one cycle. `expected` is therefore simply how many rows
   * there are, and `sent` how many of them reached that status.
   *
   * `delayed` and `interim` are flags rather than statuses, so they cut across the
   * other three: a delayed file is also a sent one. That is why they are counted
   * separately instead of being folded into the status tally, and why the KPI row
   * can show 2 Delayed alongside 81 Sent without the two having to add up.
   */
  private aggregate(rows: any[]) {
    const expected = rows.length;
    const sent = rows.filter((r) => this.status(r) === "Sent").length;
    const failed = rows.filter((r) => this.status(r) === "Failed").length;
    const inProcess = rows.filter((r) => this.status(r) === "In Process").length;
    const notStarted = rows.filter(
      (r) => this.status(r) === "Not Started"
    ).length;
    const delayed = rows.filter((r) => isY(r.delay_ind)).length;
    const interim = rows.filter((r) => isY(r.interim_extract_flg)).length;
    // Sent over expected — the figure the reference prints in the middle of its
    // ring, and the one the grid's Processing Status column grades.
    const pct = expected ? +((sent / expected) * 100).toFixed(2) : 0;
    return {
      expected,
      sent,
      failed,
      inProcess,
      notStarted,
      delayed,
      interim,
      pct,
    };
  }

  // ---------------------------------------------------------------- filters
  /**
   * The filter chips, in strip order. A chip backed by a dynamic filter takes
   * its options and its applied selection from what that filter resolved to
   * (the API answered, and pickDefaultSelection() chose); every other chip is
   * still derived from the distinct values of the loaded records.
   */
  private buildFilters(): void {
    this.filters = DEM_FILTER_DEFS.map((d) => {
      const dynamic = this.dynamicFilterStates.find((s) => s.def.key === d.key);
      // Ascending either way — distinct() sorts what it finds in the records, and
      // an API-backed chip's labels are sorted here rather than in the state they
      // come from: DpmDynamicFilterState.options keeps the endpoint's own order,
      // because defaultDynamicLabels() reads its FIRST entry as the filter's
      // default. Sorting the display list and leaving that order alone is what
      // keeps the two from being the same decision.
      const options = dynamic
        ? sortFilterValues(dynamic.options.map((o) => o.label))
        : this.distinct(d.key);
      const single = dynamic ? dynamic.def.single : !!d.single;
      // A dynamic chip's default is the one the portal is already on; a data-backed
      // chip defaults to every value it found, so it hides nothing — unless it is
      // single-select, which can only ever hold one value and so opens on the first.
      let defaultApplied: string[];
      if (dynamic) defaultApplied = this.defaultDynamicLabels(dynamic);
      else if (single) defaultApplied = options.slice(0, 1);
      else defaultApplied = options;
      // A dynamic chip opens on what the load actually resolved (which may be a
      // bookmark's or the last visit's choice, not the default); everything else
      // opens on its default.
      const applied = dynamic ? this.selectedLabels(d.key) : defaultApplied;
      return {
        label: d.label,
        key: d.key,
        single,
        options,
        applied: new Set(applied),
        checked: [...applied],
        search: "",
        addCurrent: false,
        defaultApplied: [...defaultApplied],
      };
    });
  }

  /**
   * What a dynamic chip would open on if nothing had been touched: the selection
   * the rest of the portal is on (Brand is chosen once per module), else the first
   * option the API offered.
   *
   * These are steps 3 and 4 of pickDefaultSelection() and deliberately not steps 1
   * and 2 — the bookmarked and the persisted selections are exactly what Reset
   * exists to undo, so treating either as the default would make Reset a no-op on
   * the visit that restored it. It is also why this can be recomputed on every
   * load instead of being captured once: the two sources it reads do not move
   * while the screen is open (the static screens only ever read the portal's brand;
   * dashboard.component is what sets it).
   */
  private defaultDynamicLabels(state: DpmDynamicFilterState): string[] {
    const shared = this.dpmService.sharedSelection(state.def);
    if (shared !== null && shared !== undefined) {
      const fromShared = state.options.filter(
        (o) => String(o.key) === String(shared)
      );
      if (fromShared.length) return fromShared.map((o) => o.label);
    }
    return state.options.length ? [state.options[0].label] : [];
  }

  /**
   * The filter chips before anything has been fetched: every name from
   * DEM_FILTER_DEFS, with no options yet.
   *
   * Structural rather than visual now. A valueless chip draws nothing — the
   * strip and the panel both render a filter only once it has values — so this
   * is not what puts the filters on screen; it is what gives the rest of the
   * screen a filter list to work against before any of them can be filled.
   * applyDynamicFilterOptions() looks its chips up in here, applyFilters()
   * walks it on every aggregation, and restoreFilterState() writes into it, all
   * of which would otherwise have to guard against a list that does not exist
   * yet.
   *
   * So the chips arrive one source at a time and each as soon as it can: the
   * dynamic ones when their own call lands, the data-backed ones with the
   * records. A filter whose API never answers never appears, which is the honest
   * answer — there is nothing behind it to pick from.
   */
  private buildFilterSkeleton(): void {
    this.filters = DEM_FILTER_DEFS.map((d) => ({
      label: d.label,
      key: d.key,
      single: !!d.single,
      options: [],
      applied: new Set<string>(),
      checked: [],
      search: "",
      addCurrent: false,
      // A chip with no values cannot be off its default, and isPartial() answers
      // for it on hasOptions() before it reads this.
      defaultApplied: [],
    }));
  }

  /**
   * Fill the API-backed chips in the moment their own call has answered, instead of
   * making them wait for the dashboard data.
   *
   * buildFilters() is what normally gives a chip its options, and it cannot run any
   * earlier than the records: the data-backed chips ARE the distinct values of those
   * records. The dynamic chips owe the data nothing, though — their options and their
   * selection are settled by the time this is called — so leaving them at the
   * skeleton's valueless state until the much slower execution call lands means the
   * Brand chip cannot be opened while the screen is loading for that very brand.
   *
   * So each dynamic chip is filled here, and every other chip stays valueless until
   * onDataLoaded() -> buildFilters() reaches it. Nothing is duplicated: buildFilters()
   * rebuilds the whole list from these same states a moment later and remains the one
   * definition of what a chip holds.
   */
  private applyDynamicFilterOptions(): void {
    this.dynamicFilterStates.forEach((state) => {
      const f = this.filters.find((x) => x.key === state.def.key);
      if (!f) return;
      const applied = this.selectedLabels(state.def.key);
      f.single = state.def.single;
      f.options = sortFilterValues(state.options.map((o) => o.label));
      f.defaultApplied = this.defaultDynamicLabels(state);
      f.applied = new Set(applied);
      f.checked = [...applied];
    });
    this.cdr.detectChanges();
    // A chip that had no values a moment ago was not drawn at all, so this does
    // not just fill one in — it puts one on the strip. Re-measure, or the scroll
    // arrows keep the answer they were given for a strip that was one chip
    // shorter. applyFilters() measures again when the records land and the rest
    // of the chips arrive with them.
    this.updateBadgeOverflow();
  }

  /** A filter with values to pick from. Everything that would act on a
   *  selection is gated on this: a valueless chip can only show its name. */
  hasOptions(f: FilterDef): boolean {
    return f.options.length > 0;
  }

  /** What the panel's select prompts with.
   *
   *  It used to answer two more states — "Loading…" while the load was out and
   *  "No values" once it had come back empty. Both are gone with the field
   *  itself: a filter with no values is no longer rendered at all, in the panel
   *  or in the strip, so there is no empty select left to caption. */
  filterPlaceholder(f: FilterDef): string {
    return "Select " + f.label;
  }

  /** True for a chip whose options come from an API rather than from the data.
   *  Those chips refetch when applied, and are not restored from storage — the
   *  load sequence has already settled their selection. */
  private isDynamicFilter(f: FilterDef): boolean {
    return this.dynamicFilterStates.some((s) => s.def.key === f.key);
  }

  /**
   * Commit a chip's new selection. A data-backed chip only re-aggregates what
   * is already loaded; a dynamic one whose selection actually changed scopes
   * the data itself, so it refetches instead.
   */
  private onFilterApplied(f: FilterDef): void {
    const state = this.dynamicFilterStates.find((s) => s.def.key === f.key);
    let keys = state
      ? state.options.filter((o) => f.applied.has(o.label)).map((o) => o.key)
      : [];

    // Only one value of a single-select filter is ever sent, so pin the chip to
    // the one that will be — otherwise the badge could claim a selection the
    // data behind it was never fetched for.
    if (state?.def.single && keys.length > 1) {
      keys = keys.slice(0, 1);
      const label = state.options.find((o) => o.key === keys[0])?.label;
      f.applied = new Set(label ? [label] : []);
      f.checked = [...f.applied];
    }

    if (!state || this.sameSelection(keys, state.selected)) {
      // The chip that just changed is a parent to everything under it, so its
      // children's lists are rebuilt before the page is re-aggregated — see
      // cascadeFilters(). Ahead of applyFilters(), because repairing a stranded
      // child selection changes what applyFilters() is about to filter by.
      this.cascadeFilters();
      this.applyFilters();
      this.saveFilterState();
      return;
    }

    state.selected = keys;
    // A new brand is a new dataset: its sources, file types and date period are
    // its own, so carrying the previous brand's selections over would silently
    // hide part of what was just fetched (or restore values that no longer
    // exist). Everything goes back to select-all over the new data, and the
    // date range back to its data period — so the saved state is dropped
    // outright rather than filtered.
    this.clearFilterState();
    this.loadExecutionData();
  }

  private sameSelection(a: any[], b: any[]): boolean {
    if (a.length !== b.length) return false;
    const bs = new Set(b.map((v) => String(v)));
    return a.every((v) => bs.has(String(v)));
  }

  /**
   * ng-select header search: filter the ng-select's own item list, and record the
   * term on the chip.
   *
   * The term is recorded because the panel's Select All and "Add current selection
   * to filter" have to mean what they mean in the reference multi-select, where
   * both act on the SEARCH RESULTS rather than on the whole list. ng-select filters
   * its own dropdown internally and tells us nothing about it, so `f.search` is how
   * filteredOptions() — the one list all three of them read — knows what is on
   * screen. It is cleared with every apply and every close (commitFilter,
   * onSelectClose), so it never outlives the dropdown it was typed into.
   */
  onFilterText(f: FilterDef, ev: Event): string {
    const value = (ev.target as HTMLInputElement).value;
    f.search = value;
    return value;
  }

  /**
   * The filter panel's single-select ng-select, picked from.
   *
   * Direct selection, exactly as the badge dropdown's list is (toggleOption):
   * the value applies on the pick and the ng-select closes itself
   * (closeOnSelect), so the footer of buttons that used to carry Include alone
   * is gone with it.
   *
   * The empty case is still only a model write. [clearable]="false" means it
   * cannot arrive from the UI, but a filter applied with no value would match no
   * record and blank the screen, so it is not worth committing on the chance it
   * arrives from somewhere else.
   */
  onSingleModel(f: FilterDef, val: string | null): void {
    f.checked = val ? [val] : [];
    if (!val) return;
    f.applied = new Set([val]);
    this.commitFilter(f);
  }

  // Filter the full dataset by the date range, every badge's applied selection
  // and every committed cross-filter, then re-aggregate.
  applyFilters(): void {
    this.buildDateColumns(); // reflect the current from/to date
    // Folded once, outside the row loop: the predicate is the same for every
    // record, and the path kind memoises per record inside its own closure.
    const crossFilter = combineSelections(this.selections, (s) =>
      this.selectionTest(s)
    );
    this.viewData = this.data.filter((r) => {
      const d = this.day(r);
      if (this.fromDate && d < this.fromDate) return false;
      if (this.toDate && d > this.toDate) return false;
      if (!crossFilter(r)) return false;
      return this.filters.every((f) => this.rowPasses(f, r));
    });
    // Before the builds: the summary widgets aggregate from the preview whenever
    // a selection is open, so it has to describe the view they are about to read.
    this.buildPreview();
    this.buildKpis();
    this.buildDonut();
    this.buildHeatmap();
    this.buildGrid();
    // After the builds, which replace every cell, node and grid row: the
    // highlight's dim flags live on those objects, so they have to be re-stamped
    // onto the new ones or a highlight would survive a filter change in the chips
    // but stop being drawn.
    this.applyHighlight();
    this.cdr.detectChanges();
    this.updateBadgeOverflow(); // reset chip may change the badge width
    // Every widget on this screen reads the one dataset, so the page reports its
    // empty state once instead of a dozen times. Reported from here rather than
    // from the load, so narrowing the chips or the date range down to nothing
    // notifies too — see the noData getter.
    if (this.noData) this.noDataNotifier.report("dem-static");
  }

  // ======================================================== cross-filtering
  // Ctrl/⌘+clicking a visual narrows the page, the way a Qlik sheet or a Power BI
  // report does. The modifier click opens a selection that is LIVE — the summary
  // widgets re-aggregate against it at once and the objects that list values grey
  // out what it excludes — and every further ctrl/cmd+click adds to that selection
  // or takes something back out of it. The popup's Apply is what makes it
  // permanent, by routing it into a filter chip; nothing else does.
  //
  // A plain click selects nothing at all, and — just as importantly — does nothing
  // to a selection that is already open. That is the point of the modifier: on a
  // screen this dense you have to be able to point at a figure, read it, fold a
  // branch and open a menu without either filtering the page or losing the picks
  // you were part-way through making. See onDocumentClick() for the rule that used
  // to get this wrong, the block comment above StaticSelection in
  // dpm-static.service.ts, and `previewData` above for what "live" means here.

  /**
   * Recompute `previewData` for the currently open selection.
   *
   * Folded exactly the way a committed set is (OR within a field, AND across
   * fields — combineSelections), so the preview cannot disagree with what Apply will
   * produce. With nothing open it IS viewData, by reference: the widgets read one
   * array or the other and there is no point copying.
   */
  private buildPreview(): void {
    this.assocCache.clear();
    this.previewDays = null;
    const items = this.pending?.items || [];
    if (!items.length) {
      this.previewData = this.viewData;
      return;
    }
    const test = combineSelections(items, (s) => this.selectionTest(s));
    this.previewData = this.viewData.filter(test);
  }

  /**
   * The rows a widget should aggregate from.
   *
   * Every widget gets the preview EXCEPT the one the popup is anchored to — the
   * widget the most recent pick landed in — which keeps the full view. That
   * exception is the whole of Qlik's rule: the object you are selecting in has to
   * go on showing the values you did not pick, or there is nothing left to add to
   * the selection with a second click. Every other object answers the question the
   * selection just asked.
   *
   * A set built across two widgets therefore leaves the earlier one reading the
   * preview, so it dims. That is a deliberate simplification, not an oversight:
   * the anchor follows whatever you just clicked, so the widget you are actually
   * working in is always the one showing its full list, and going back to the
   * earlier widget with another ctrl+click moves the anchor back.
   */
  private widgetRows(widget: string): any[] {
    return !this.pending || this.pending.widget === widget
      ? this.viewData
      : this.previewData;
  }

  /**
   * Re-aggregate the widgets whose numbers depend on the open selection, and
   * re-stamp the dim flags on the ones that list values.
   *
   * Called on every pick and every cancel — the two things that move the preview
   * without going through applyFilters().
   *
   * Every panel answers a pick with its own numbers now, not just a highlight. The
   * summary widgets re-aggregate outright; the grid and the heat map narrow their
   * figures to the preview and drop the branches it empties (refreshGridFigures,
   * refreshHeatFigures) while keeping their trees, expansion and sort exactly as they
   * were. Dimming alone was not enough for either — a row went pale and went on
   * stating the number it stated before the pick, which is a row that is faded and
   * wrong at the same time.
   *
   * The one panel that does NOT narrow is whichever the popup is anchored to. That is
   * Qlik's rule and widgetRows() is where it lives: the object you are selecting in
   * has to go on showing what you did not pick, or a second pick is impossible. So
   * picking in the grid narrows the heat map, picking in the heat map narrows the
   * grid, and picking in a KPI card or the donut narrows both.
   */
  private refreshSelectionPreview(): void {
    this.applyHighlight(); // rebuilds previewData, then stamps the dim flags
    this.buildKpis();
    this.buildDonut();
    this.refreshGridFigures();
    this.refreshHeatFigures();
  }

  /**
   * A selection's row predicate. The one place this screen's column names are
   * attached to the shared selection kinds.
   *
   * `path` memoises inside its own closure rather than recomputing recordPath()
   * per row: the predicate is built once per fold (see applyFilters) and then run
   * across the whole dataset, so the same record is asked repeatedly only when
   * several path selections are live, and each keeps its own cache. A WeakMap so
   * a dropped dataset is still collectable.
   */
  private selectionTest(sel: StaticSelection): (r: any) => boolean {
    switch (sel.kind) {
      case "status":
        return (r) => this.status(r) === sel.value;
      // Unused on this screen — every figure it offers is a status, a flag or a
      // path. Resolved anyway so both static screens answer the shared kinds
      // identically, and a selection carried over in a shared save cannot mean
      // one thing here and another there.
      case "notStatus":
        return (r) => this.status(r) !== sel.value;
      case "flag":
        return (r) => isY(r[sel.value]);
      case "count":
        return (r) => isSet(r[sel.value]);
      case "day":
        return (r) => this.day(r) === sel.value;
      case "path": {
        const cache = new WeakMap<object, string>();
        return (r) => {
          let path = cache.get(r);
          if (path === undefined) {
            path = this.recordPath(r)
              .map((p) => p.label)
              .join("/");
            cache.set(r, path);
          }
          return pathMatches(sel.value, path);
        };
      }
      default:
        // An unrecognised kind matches everything rather than nothing: a
        // selection restored from an older save should be inert, not a filter
        // that silently empties the screen.
        return () => true;
    }
  }

  /**
   * A tree node as a selection — the heat map's rows, the History tree and the
   * grid's rows all produce this, so clicking any of the three means the same
   * branch and lights up all of them.
   *
   * Grouped by the node's DIMENSION rather than by kind: two file types are two
   * values of one field, so picking both widens to either, where a destination
   * and a file type are different fields and picking both narrows. That is Qlik's
   * associative rule, and `dim` is what says which field a node belongs to (a
   * node's depth cannot — blank levels are dropped from a path, so the same
   * dimension sits at different depths in different branches).
   */
  private pathSel(nodeId: string, label: string, dim: string): StaticSelection {
    return {
      id: `path:${nodeId}`,
      group: `path:${dim}`,
      kind: "path",
      value: nodeId,
      // The tree level's own column, which is also the key of the chip that
      // filters by it — so committing a tree row moves that badge. Only a level
      // with no chip (none on this screen; DPM has one) falls back to a
      // standalone chip and to the path predicate below.
      field: dim,
      label,
      color: COLORS.inProcess,
    };
  }

  /** One day as a selection — a date column header, or a calendar tile. */
  private daySel(iso: string): StaticSelection {
    return {
      id: `day:${iso}`,
      group: "day",
      kind: "day",
      value: iso,
      // Not a dimension chip's column: the date badge is what covers a day, and
      // confirmPending()'s day bucket is what drives it.
      field: "",
      label: iso,
      color: COLORS.expected,
    };
  }

  /**
   * The one entry point every clickable visual routes through.
   *
   *   ctrl / cmd + click -> pick, into the selection open on `widget` (Qlik's
   *                   model): the pick joins a pending set, the page previews it,
   *                   and nothing is filtered until the popup's Apply is pressed.
   *                   The same modifier click on something already picked takes it
   *                   back out — so ONE gesture covers both single and multi
   *                   select, and there is no second gesture to learn for the
   *                   second pick.
   *   plain click  -> not a selection gesture at all. Whatever the element
   *                   already owned keeps it (a fold, a comment popup, a calendar
   *                   node), and where it owns nothing the click does nothing.
   *
   * The modifier IS the gesture now, where it used to be a shortcut that committed
   * one item straight to a filter chip. Two reasons. Reading a figure and selecting
   * by it were the same click, so every stray click on a KPI number opened a
   * selection nobody asked for and then the next click away applied it. And the
   * History view could never offer the plain-click version anyway — selectCalNode()
   * owns that click there — so the screen was already teaching two gestures for one
   * idea, with the modifier meaning "pick" in one view and "commit" everywhere
   * else. It means "pick" everywhere now, that view included.
   *
   * What went with it is the one-click commit: Apply on the selection popup is the
   * only thing that turns picks into filters. That is the trade — a second click to
   * confirm, in exchange for a gesture that cannot fire by accident.
   *
   * stopPropagation on the branch that acts, and only that one. It used to be
   * load-bearing — the document handler confirmed an open selection, so without it
   * a pick was applied by its own click — and it stays because that same handler
   * also closes the open filter-badge dropdown and the date popup, neither of which
   * a pick is a reason to close. A plain click is let through untouched: it is
   * ordinary page interaction and there is no longer anything for it to trigger.
   */
  onVisualClick(
    sel: StaticSelection | null,
    ev: MouseEvent,
    widget: string
  ): void {
    if (!sel) return;
    if (!ev.ctrlKey && !ev.metaKey) return;
    ev.stopPropagation();
    this.pick(sel, widget);
  }

  /**
   * Add a pick to the open selection, or take it back out.
   *
   * A pick in a DIFFERENT widget JOINS the selection already open and moves the
   * popup to the widget just clicked. It used to confirm the open one instead —
   * Qlik's rule, on the grounds that the popup should belong to exactly one object
   * — and that was the other half of the accidental-apply bug: picking in the KPI
   * row and then in the tree turned the KPI picks into a filter chip nobody had
   * asked to apply. Nothing commits without Apply now, and that has to hold for
   * every route into pick(), not just for clicking on the background.
   *
   * Merging rather than discarding, because the picks cost the user a modifier
   * click each and throwing them away silently is the same class of surprise in
   * the other direction. They are all listed as chips in the popup, so a set built
   * across two widgets is never a set the screen is hiding — and confirmPending()
   * buckets by DESTINATION, not by source widget, so a mixed set applies correctly
   * with no further work.
   *
   * The popup follows the newest pick so it stays next to whatever was just
   * clicked; `widget` is therefore "where the popup is anchored", which is also
   * the widget that keeps its full value list (see widgetRows).
   *
   * Un-picking the last item closes the mode outright, so an object is never left
   * outlined with an empty popup over it.
   */
  private pick(sel: StaticSelection, widget: string): void {
    const items = this.pending?.items || [];
    // A selection opens at its widget's edge however the last one was left: a
    // hand-placed pill belongs to the selection that was dragged, not to the
    // screen. This is the one place a selection can start, so it is the only
    // place the flag has to be cleared.
    if (!items.length) this.toolbarDragged = false;
    const next = items.some((s) => s.id === sel.id)
      ? items.filter((s) => s.id !== sel.id)
      : [...items, sel];

    this.pending = next.length ? { widget, items: next } : null;
    // The selection is live from this click, not from the one that applies it:
    // re-aggregate the summary widgets and re-stamp the dims in one pass. Also
    // covers un-picking the last item, where `pending` just went null and the
    // numbers have to go back to the unfiltered view.
    this.refreshSelectionPreview();
    this.positionToolbar();
    this.cdr.detectChanges();
  }

  /**
   * Put the selection toolbar against the top edge of the widget that owns the
   * open selection, centred on it.
   *
   * Measured rather than laid out by CSS because the pill is fixed-positioned —
   * see toolbarPos for why it has to be. The widget is found by the
   * `data-xf-widget` attribute its container carries, so the key in `pending` is
   * the only thing tying the two together.
   *
   * The result is clamped to the scrolling region: scroll a panel far enough and
   * its toolbar would otherwise ride up over the filter bar, or off the bottom of
   * the page, pointing at a widget no longer on screen. Pinned to the edge it is
   * leaving, it stays reachable and stays obviously attached to something above
   * or below.
   */
  private positionToolbar(): void {
    if (!this.pending) return;
    // Hand-placed beats anchored: once the pill has been dragged it stays where
    // it was dropped for the rest of this selection, so a pick in another widget
    // — or a scroll — no longer drags it back over the thing the user moved it
    // off. Re-clamped rather than left alone, so a resize cannot strand it half
    // off screen. See onToolbarDragStart().
    if (this.toolbarDragged) {
      this.toolbarPos = this.clampToolbar(
        this.toolbarPos.left,
        this.toolbarPos.top
      );
      return;
    }
    const host = this.host?.nativeElement;
    const el = host?.querySelector(
      `[data-xf-widget="${this.pending.widget}"]`
    ) as HTMLElement | null;
    if (!el) return;

    const r = el.getBoundingClientRect();
    // The scrolling region, which is what the pill must stay inside. Falls back
    // to the viewport if the layout has collapsed to the single-scroll mobile
    // form, where .dem-scroll is not the scroller.
    const scroller = host?.querySelector(".dem-scroll") as HTMLElement | null;
    const bounds = scroller
      ? scroller.getBoundingClientRect()
      : { top: 0, bottom: window.innerHeight };

    // Half the popup's height, so it straddles the edge rather than sitting on
    // it — the CSS translates it by -50% in both axes.
    //
    // A constant, and it can only stay one because the popup is a fixed two rows:
    // the count line and the chip line. That is what PENDING_CHIP_LIMIT exists for
    // — the chips are capped and the remainder collapses into a "+N" — so a
    // twelve-pick selection is exactly as tall as a one-pick selection and neither
    // grows off the anchor this clamps against.
    const HALF = this.TOOLBAR_HALF_H;
    const top = Math.min(
      Math.max(r.top, bounds.top + HALF),
      bounds.bottom - HALF
    );
    // Keep the whole popup on screen horizontally too; it is ~340px wide.
    const HALF_W = this.TOOLBAR_HALF_W;
    const left = Math.min(
      Math.max(r.left + r.width / 2, HALF_W),
      window.innerWidth - HALF_W
    );

    this.toolbarPos = { left, top };
  }

  /** Follow the widget when the page scrolls under a fixed-positioned pill. */
  onPageScroll(): void {
    if (!this.pending) return;
    this.positionToolbar();
  }

  // ---- moving the selection popup by hand ----------------------------------
  // The pill places itself against the top edge of the widget the selection
  // belongs to, and that default is worth keeping: it is what says WHICH object
  // is holding the picks. But it is also a 340px fixed-position card sitting over
  // the page, and what the user wants to read next is sometimes exactly what it
  // covers — the tree rows under a panel's top edge, the figures on the card it
  // straddles. So it can be dragged out of the way, and stays where it is put
  // until the selection ends.
  //
  // Mouse only, as every other pointer gesture on this screen is.

  /** Half the pill's height and width. It is centred on the point it is given
   *  (the CSS translates it -50% in both axes), so these are the margins that
   *  keep it whole on screen — read by positionToolbar() when it anchors the pill
   *  and by clampToolbar() when the pointer is carrying it. */
  private readonly TOOLBAR_HALF_H = 34;
  private readonly TOOLBAR_HALF_W = 172;

  /**
   * The grab in progress: the offset from the pointer to the pill's own centre
   * point — so it travels with the pointer rather than jumping its centre under
   * it — and the element being carried. Null when nothing is being dragged.
   */
  private toolbarGrab: { dx: number; dy: number; el: HTMLElement } | null = null;

  /** The pill has been moved by hand, so positionToolbar() stops re-anchoring it.
   *  Cleared when the next selection opens — see pick(). */
  private toolbarDragged = false;

  /** Is a drag in progress? Drives the pill's cursor. */
  get toolbarDragging(): boolean {
    return !!this.toolbarGrab;
  }

  /**
   * Take hold of the pill, unless the press belongs to something else.
   *
   * Clear, Apply and the pick chips keep their own clicks — they are the point of
   * the popup, and a press that lands on one is that control's, not a grab.
   * Everything else on it is the handle: the count, the "selected", the widget
   * label and the space around them.
   *
   * preventDefault so dragging across the label does not paint a text selection
   * on the way.
   */
  onToolbarDragStart(ev: MouseEvent): void {
    if (ev.button !== 0) return;
    const target = ev.target as HTMLElement | null;
    if (target && target.closest(".sel-btn, .sel-pick")) return;
    ev.preventDefault();
    this.toolbarGrab = {
      dx: ev.clientX - this.toolbarPos.left,
      dy: ev.clientY - this.toolbarPos.top,
      el: ev.currentTarget as HTMLElement,
    };
    // Outside Angular deliberately. A window mousemove registered in the zone
    // runs a change-detection pass per event, and one pass over this screen is the
    // whole heat lattice — hundreds of bound tiles — which is the difference
    // between a pill that follows the pointer and one that stutters behind it. The
    // move handler writes the position itself; the single pass that settles it
    // runs on mouseup.
    this.zone.runOutsideAngular(() => {
      window.addEventListener("mousemove", this.onToolbarDragMove);
      window.addEventListener("mouseup", this.onToolbarDragEnd);
    });
  }

  /**
   * Follow the pointer.
   *
   * A bound field rather than a method, so the same function object can be handed
   * back to removeEventListener and `this` is the component without a closure per
   * drag.
   *
   * The position is written onto the element AND into the model: onto the element
   * because nothing is running change detection while the drag lasts, and into the
   * model because something else on the page still might — a pass that read a
   * stale toolbarPos would snap the pill back to where the drag started until the
   * next mousemove moved it again.
   */
  private readonly onToolbarDragMove = (ev: MouseEvent): void => {
    const grab = this.toolbarGrab;
    if (!grab) return;
    // Set here rather than on mousedown, so a press that never moved stays a
    // click on the pill's chrome and does not pin it off its anchor.
    this.toolbarDragged = true;
    const pos = this.clampToolbar(ev.clientX - grab.dx, ev.clientY - grab.dy);
    this.toolbarPos = pos;
    grab.el.style.left = `${pos.left}px`;
    grab.el.style.top = `${pos.top}px`;
  };

  /** Let go: stop listening, then run the one pass that settles the dragged
   *  position and takes the grabbing cursor back off. Also the cleanup ngOnDestroy
   *  calls, so the window listeners cannot outlive the screen. */
  private readonly onToolbarDragEnd = (): void => {
    if (!this.toolbarGrab) return;
    this.toolbarGrab = null;
    window.removeEventListener("mousemove", this.onToolbarDragMove);
    window.removeEventListener("mouseup", this.onToolbarDragEnd);
    if (this.destroyed) return;
    this.zone.run(() => this.cdr.detectChanges());
  };

  /** Keep the whole pill on screen, wherever it is dropped. */
  private clampToolbar(
    left: number,
    top: number
  ): { left: number; top: number } {
    return {
      left: Math.min(
        Math.max(left, this.TOOLBAR_HALF_W),
        window.innerWidth - this.TOOLBAR_HALF_W
      ),
      top: Math.min(
        Math.max(top, this.TOOLBAR_HALF_H),
        window.innerHeight - this.TOOLBAR_HALF_H
      ),
    };
  }

  /**
   * The dimension chip that already covers this selection's field, if there is
   * one with values loaded.
   *
   * An empty chip is deliberately no match: its options never arrived, so
   * applyFilters() ignores it entirely (see hasOptions there) and routing a
   * selection into it would set a filter that does nothing.
   */
  private chipFor(sel: StaticSelection): FilterDef | undefined {
    if (!sel.field) return undefined;
    return this.filters.find(
      (f) => f.key === sel.field && this.hasOptions(f)
    );
  }

  /**
   * The chip option matching a value, or undefined.
   *
   * Compared case- and space-insensitively rather than by identity, because the
   * two sides do not always spell a status the same way: a chip's options are the
   * RAW `file_status` values out of the response ("To be Processed"), while a
   * status selection carries the legend's canonical spelling ("To Be Processed").
   * An exact match would silently fail on exactly those statuses and fall through
   * to a standalone chip — the bug this method exists to prevent.
   */
  private chipOption(f: FilterDef, value: string): string | undefined {
    const want = value.trim().toLowerCase();
    return f.options.find((o) => o.trim().toLowerCase() === want);
  }

  /** The chip values this selection narrows to, already resolved to real
   *  options. Empty when the chip cannot express it, which sends the caller
   *  back to a standalone chip rather than applying an empty filter. */
  private chipValues(f: FilterDef, sel: StaticSelection): string[] {
    // "Everything but one status" is a real chip state: every option except
    // that one. It is the only kind that widens rather than picking a value.
    if (sel.kind === "notStatus") {
      const drop = this.chipOption(f, sel.value);
      return drop ? f.options.filter((o) => o !== drop) : [];
    }
    // A path names its node by label; everything else matches on its value.
    const hit = this.chipOption(f, sel.kind === "path" ? sel.label : sel.value);
    return hit ? [hit] : [];
  }


  /** Drop one chip. */
  removeSelection(sel: StaticSelection, ev?: Event): void {
    ev?.stopPropagation();
    this.selections = this.selections.filter((s) => s.id !== sel.id);
    this.saveFilterState();
    this.applyFilters();
  }

  // ---- confirm / cancel ----------------------------------------------------
  /**
   * Apply everything picked in the open selection, then close it.
   *
   * This is now the ONLY way picks become filters — the ctrl+click that used to
   * commit one item on the spot is the pick gesture itself, so nothing reaches the
   * chips except through here or through a chip's own controls.
   *
   * Picks are grouped by DESTINATION before anything is applied, and that grouping
   * is the whole reason this is not a commit-one-at-a-time loop: three statuses
   * picked in the KPI row all belong to the one Status badge, and applying them
   * one at a time would leave the badge holding only the last. Grouped, they
   * become a single three-value selection — which is what the popup's count and
   * its three chips promised.
   *
   * Confirm SETS rather than toggles, unlike the ctrl+click path: a set that was
   * built up deliberately and then confirmed should land as it stands, not cancel
   * itself out against whatever the badge happened to hold.
   */
  confirmPending(ev?: Event): void {
    ev?.stopPropagation();
    const open = this.pending;
    this.pending = null;
    if (!open || !open.items.length) {
      this.refreshSelectionPreview();
      return;
    }

    // Destination buckets. A chip can collect from several picks; days collapse
    // to one range; the rest fall through to standalone chips.
    //
    // Bucketed by chip AND BY GROUP, because that is how the picks have been read
    // all along: combineSelections() ORs within a group and ANDs across groups, and
    // that predicate IS the preview the user has been watching since the first
    // pick. One set per chip could only union, so a selection whose groups meant
    // AND applied as something wider than it previewed — Received (every status but
    // Missing) plus Processed landed as plain Received, putting Failed files on a
    // screen where neither pick named Failed and the donut had faded it out. Folded
    // below, so Apply lands exactly the set the preview drew.
    const chipGroups = new Map<FilterDef, Map<string, Set<string>>>();
    const days: string[] = [];
    const standalone: StaticSelection[] = [];

    open.items.forEach((sel) => {
      const chip = this.chipFor(sel);
      const values = chip ? this.chipValues(chip, sel) : [];
      if (chip && values.length) {
        const groups = chipGroups.get(chip) || new Map<string, Set<string>>();
        const bucket = groups.get(sel.group) || new Set<string>();
        values.forEach((v) => bucket.add(v));
        groups.set(sel.group, bucket);
        chipGroups.set(chip, groups);
        return;
      }
      if (sel.kind === "day") {
        days.push(sel.value);
        return;
      }
      standalone.push(sel);
    });

    chipGroups.forEach((groups, f) => {
      // The union of each group's values, intersected: OR within, AND across. A
      // chip that collected from one group only comes out of this as that group's
      // union, which is every chip on the screen bar one — the Status badge, which
      // is where a status pick and the Received pick meet.
      //
      // An empty intersection is a contradiction the user built — Received and
      // Missing, say — and it lands as one: the chip holds nothing and the screen
      // empties, which is what the preview was already showing them.
      const values = [...groups.values()].reduce((acc, set) => {
        return new Set([...acc].filter((v) => set.has(v)));
      });
      f.applied = new Set(values);
      f.checked = [...f.applied];
    });

    // Several days become the span they cover rather than a list: the date badge
    // holds a range, so picking the 3rd and the 7th means the 3rd to the 7th.
    // That is the honest reading of a range control, and it is what the badge
    // will say afterwards.
    if (days.length) {
      const sorted = [...days].sort();
      this.fromDateObj = new Date(sorted[0]);
      this.toDateObj = new Date(sorted[sorted.length - 1]);
    }

    if (standalone.length) {
      const fresh = standalone.filter(
        (s) => !this.selections.some((x) => x.id === s.id)
      );
      if (fresh.length) this.selections = [...this.selections, ...fresh];
    }

    // One commit for the whole set, rather than one per destination — each would
    // otherwise rebuild the entire screen. commitDates() is the date path's own
    // (it stamps fromDate/toDate before re-aggregating), so it stands in for the
    // plain applyFilters() when a day was picked.
    //
    // A dynamic chip would need a refetch instead (onFilterApplied), but none is
    // reachable from a visual: the only one on this screen is Brand, and nothing
    // clickable selects a brand.
    if (days.length) this.commitDates();
    else {
      this.applyFilters();
      this.saveFilterState();
    }
  }

  /** Close the open selection without applying any of it. */
  cancelPending(ev?: Event): void {
    ev?.stopPropagation();
    if (!this.pending) return;
    this.pending = null;
    // Puts the KPI cards, the Extracts figures and the donut back to the
    // committed view as well as clearing the dims — the preview they were
    // reading is gone with the pick.
    this.refreshSelectionPreview();
    this.cdr.detectChanges();
  }

  /**
   * Stamp the current highlight's dim flags onto every rendered object.
   *
   * Stamped rather than tested from the template for the same reason
   * applyLegendVisibility() stamps its own: a long date range is thousands of
   * tiles, and a predicate in a binding would re-run across all of them on every
   * change-detection pass.
   *
   * Only things that HOLD records dim. An empty tile has no ink to take away, and
   * dimming it would wash out the whole strip for a highlight that is about
   * something else.
   */
  private applyHighlight(): void {
    // Every pick in the open selection at once, folded the same way a committed
    // set is (OR within a field, AND across fields) — so the preview the page
    // shows while the popup is up is the very set Apply would commit.
    // Rebuilt here as well, so the rows the summary widgets aggregate and the
    // dim flags stamped below are always the same fold of the same picks — a
    // selection can never end up half-applied across the page.
    this.buildPreview();
    const items = this.pending?.items || [];
    const test = items.length
      ? combineSelections(items, (s) => this.selectionTest(s))
      : null;
    const dims = (rows: any[]): boolean =>
      !!test && rows.length > 0 && !rows.some(test);

    const walkHeat = (nodes: HeatRow[]) => {
      nodes.forEach((n) => {
        n.dimmed = dims(n.rows);
        n.cells.forEach((c) => {
          c.dim = dims(c.rows);
          // Not `dims(s.rows)` on its own: a band sits INSIDE the tile, so two
          // opacities would multiply — 0.28 of 0.28 is 0.08, which is invisible
          // rather than dimmed. A tile that is dimmed as a whole already says
          // everything about its bands, so they only dim when the tile is not:
          // the mixed case, where some of the statuses on this day match and the
          // rest should fade behind them.
          c.segments.forEach((s) => (s.dim = !c.dim && dims(s.rows)));
        });
        if (n.children.length) walkHeat(n.children);
      });
    };
    walkHeat(this.heatRows);

    const walkGrid = (rows: GridRow[]) => {
      rows.forEach((r) => {
        r.dimmed = dims(r.rows);
        if (r.children.length) walkGrid(r.children);
      });
    };
    walkGrid(this.gridRows);

    // ag-Grid re-evaluates gridRowClassRules when its row data is re-set, and
    // mutating `dimmed` in place leaves the array reference untouched — so the
    // rows would keep whatever classes they had. A fresh array is the cheapest
    // way to say "look again"; the row objects themselves are the same ones, so
    // nothing is rebuilt.
    this.visibleGridRows = [...this.visibleGridRows];

    // The KPI cards and the donut are few enough to answer from a binding
    // (isDimmed), so they need no stamping.
  }

  // ---- committed state: deliberately not drawn -----------------------------
  // There is no isSelected() any more, and no .xf-on binding for it to feed.
  //
  // Applying a filter used to leave a mark on everything the filter touched — the
  // KPI figure it came from, its donut band and legend row, the tree row, the grid
  // row, the date column. It read as an open selection that could not be closed:
  // the picking was over, the popup was gone, and the page was still outlined.
  // What is filtering is the filter strip's job to say, and it says it in chips
  // with counts and a Reset beside them.
  //
  // What remains is the state of a selection being BUILT: .xf-lit on each pick,
  // .xf-dim on what the picks exclude, and the popup. All of it goes when Apply or
  // Clear does. If a committed mark is ever wanted back, it wants a new home
  // rather than these three methods back: they answered "is this value in a chip",
  // which is exactly the question the chip itself already answers on screen.

  // ---- selection mode ------------------------------------------------------
  /** Is the open selection anchored to this widget — i.e. did the newest pick land
   *  here? Drives the widget's outline and the popup's placement. */
  isSelecting(widget: string): boolean {
    return this.pending?.widget === widget;
  }

  /** How many picks are in the open selection — the popup's count badge. */
  get pendingCount(): number {
    return this.pending?.items.length || 0;
  }

  // ---- the selection popup's read-model ------------------------------------
  // The popup lists the picked VALUES, where it used to say only "3 selections".
  // That count was the honest minimum and not much more: three ctrl+clicks into a
  // dimmed page, a figure misread, and there was nothing on screen that said which
  // three the Apply button was about to commit. The chips below say it, and each
  // one is its own undo.

  /**
   * How many picks the popup names before it collapses the rest into a "+N".
   *
   * Three, and the cap is structural rather than cosmetic: the popup straddles the
   * owning widget's top edge at a height positionToolbar() hard-codes, so a list
   * that grew with the picks would walk off its own anchor and, at the bottom of
   * the scroller, off the page. Three is what one row fits at the width a chip is
   * allowed to reach. Nothing is lost to the cap — pendingExtraTitle names what it
   * left out.
   */
  private readonly PENDING_CHIP_LIMIT = 3;

  /** The picks the popup draws as removable chips. */
  get pendingChips(): StaticSelection[] {
    return (this.pending?.items || []).slice(0, this.PENDING_CHIP_LIMIT);
  }

  /** How many picks the chips left out. 0 when they all fit. */
  get pendingExtra(): number {
    return Math.max(0, this.pendingCount - this.PENDING_CHIP_LIMIT);
  }

  /** The left-out picks, named — the "+N" chip's tooltip, so a selection is never
   *  holding something the screen refuses to spell out. */
  get pendingExtraTitle(): string {
    return (this.pending?.items || [])
      .slice(this.PENDING_CHIP_LIMIT)
      .map((s) => s.label)
      .join(", ");
  }

  /**
   * Which widget the popup is anchored to, in words.
   *
   * The outline says it already, and the popup says it again on purpose: the ring
   * is 2px on a page where every other widget has just dimmed, and with four cards
   * sitting edge to edge it is easy to read the popup as belonging to the neighbour
   * it overlaps. The two panels answer with their real headings — the same strings
   * their icon-chart headers carry — so the popup names the thing being read rather
   * than an internal widget key.
   *
   * The ANCHOR, not "where the picks came from": picks can span widgets (see
   * pick()), and this names the one the newest pick landed in. Hence the dot
   * separator the template renders it behind rather than an "in" — see .sel-src.
   * The chips name the picks themselves, whichever widget each came from.
   */
  get pendingWidgetLabel(): string {
    switch (this.pending?.widget) {
      case "kpi":
        return "Summary cards";
      case "donut":
        return "Donut chart";
      case "heat":
        return this.heatTitle;
      case "grid":
        return this.gridTitle;
      default:
        return "";
    }
  }

  /**
   * Take one pick back out of the open selection, from its chip in the popup.
   *
   * Routed through pick() rather than splicing `items`, so removing the last chip
   * closes the selection exactly the way un-picking the last visual does — the
   * popup is never left hanging over an empty set, and the preview goes back to the
   * committed view in the same pass.
   *
   * stopPropagation for the reason everything inside the popup stops: the document
   * handler treats a click it sees as clicking away, and would confirm the very set
   * being edited.
   */
  removePick(sel: StaticSelection, ev?: Event): void {
    ev?.stopPropagation();
    if (!this.pending) return;
    this.pick(sel, this.pending.widget);
  }

  /** Is this item one of the open selection's picks? */
  isPicked(sel: StaticSelection | null | undefined): boolean {
    return !!sel && !!this.pending?.items.some((s) => s.id === sel.id);
  }

  /** Is this day picked? Compared by value so a date header need not build a
   *  selection object per change-detection pass just to ask. */
  isDayPicked(iso: string): boolean {
    return !!this.pending?.items.some(
      (s) => s.kind === "day" && s.value === iso
    );
  }

  /** Is this tree node picked? Same reasoning as isDayPicked. */
  isPathPicked(nodeId: string): boolean {
    return !!this.pending?.items.some(
      (s) => s.kind === "path" && s.value === nodeId
    );
  }

  /**
   * Should this visual dim? Qlik's three selection states, in one rule.
   *
   *   SELECTED        a pick in the open selection. Never dims — it takes
   *                   .xf-lit from isPicked() instead, which is the ring that
   *                   says so.
   *   ASSOCIATED      not picked, but the selection still leaves records behind
   *                   it. Stays at full strength, because its FIGURE has already
   *                   re-aggregated to the selection (see widgetRows) and a
   *                   greyed-out card reporting a live number is a contradiction.
   *   NON-ASSOCIATED  the selection excludes it entirely. Dims — the muted state
   *                   Qlik greys an excluded value with.
   *
   * `widget` is which object the visual belongs to, and the exception it buys is
   * the important half: INSIDE the object being selected in, everything unpicked
   * dims whether it is associated or not. That object is the one place the user
   * is choosing from, so "what I picked" has to read louder there than "what is
   * still available" — every other object on the page is already saying the
   * latter, by its numbers.
   *
   * Omit `widget` and the visual is treated as belonging to the selecting object.
   * Nothing relies on that today; it is there so a call site that was never
   * migrated fails safe (dimmed) instead of silently claiming to be associated.
   */
  isDimmed(sel: StaticSelection | null | undefined, widget?: string): boolean {
    if (!this.pending) return false;
    if (this.isPicked(sel)) return false;
    if (!widget || this.pending.widget === widget) return true;
    return !this.isAssociated(sel);
  }

  /**
   * Does the open selection leave any record behind this visual?
   *
   * A null selection is a figure standing for the whole view rather than for a
   * value of its own (the Extracts card's Expected column) — that is associated
   * exactly when the selection left anything at all.
   *
   * Memoised per selection id, because this answers a template binding and so is
   * asked again on every change-detection pass while the answer is a scan of
   * previewData. buildPreview() clears the cache, and rebuilding the preview is
   * the only event that can change any entry in it.
   */
  private isAssociated(sel: StaticSelection | null | undefined): boolean {
    if (!sel) return this.previewData.length > 0;
    const memo = this.assocCache.get(sel.id);
    if (memo !== undefined) return memo;
    const test = this.selectionTest(sel);
    const hit = this.previewData.some(test);
    this.assocCache.set(sel.id, hit);
    return hit;
  }

  /**
   * Should this date column dim? The same three states, for a heat-map column.
   *
   * Always the associative rule with no selecting-widget exception: the date
   * header belongs to the heat map, and a day the selection excludes is exactly
   * what its own tiles have already faded out (applyHighlight), so the header
   * agreeing with the column under it is the only reading that makes sense.
   *
   * Answered from a Set built once per preview rather than by scanning: this is
   * one binding per column, and a History range is hundreds of them.
   */
  isDayDimmed(iso: string): boolean {
    if (!this.pending) return false;
    if (this.isDayPicked(iso)) return false;
    if (!this.previewDays) {
      this.previewDays = new Set(this.previewData.map((r) => this.day(r)));
    }
    return !this.previewDays.has(iso);
  }


  /**
   * The donut legend's selection for a series.
   *
   * A prebuilt map rather than a factory call, because this is read from a
   * binding: returning a fresh object each pass would allocate per legend entry
   * per change-detection cycle for no gain (every reader compares by id).
   *
   * Expected answers null — it is the whole, so there is nothing to narrow to;
   * see DONUT_SEL.
   */
  legendSel(name: string): StaticSelection | null {
    return DONUT_LEGEND_SEL[name] || null;
  }

  // ---- tooltips ------------------------------------------------------------
  /**
   * What a clickable visual's tooltip says. Spelled out because a visual that only
   * answers a MODIFIER click cannot advertise that on its own — nothing about a KPI
   * figure says a bare click will be ignored — and the filter-bar hint that does
   * say it retires as soon as the first filter is applied.
   */
  private readonly XF_HINT = "Ctrl/⌘+click to select · repeat to add more";

  /** The tooltip for a visual that is always a filter source. */
  xfTitleFor(label: string): string {
    return `${label} — ${this.XF_HINT}`;
  }

  /**
   * The same, for a visual that is only sometimes one.
   *
   * A non-source answers null rather than its own label: Angular drops a null
   * `title` attribute entirely, so the element gets no tooltip at all — where
   * echoing the label back would put a hover panel over a figure that already
   * says the same word, and imply an interaction it does not have.
   */
  xfTitle(label: string, sel: StaticSelection | null | undefined): string | null {
    return sel ? this.xfTitleFor(label) : null;
  }

  /** Appended to a tooltip a visual already composes for itself — the donut's
   *  arcs, which name their own series and value. '' for a non-source. */
  xfSuffix(sel: StaticSelection | null | undefined): string {
    return sel ? ` — ${this.XF_HINT}` : "";
  }

  // ---- click sources -------------------------------------------------------
  /**
   * A tree row in the Live strip. Nothing else claims the modifier click here —
   * only the fold button claims a plain one, and it stops propagation of its own.
   */
  onHeatRowClick(row: HeatRow, ev: MouseEvent): void {
    this.onVisualClick(this.pathSel(row.id, row.label, row.dim), ev, "heat");
  }

  /**
   * A tree row in the History view, where the plain click is already taken:
   * selectCalNode() picks which node the month calendars describe, and that is
   * this view's primary interaction — it is how you look at anything at all here.
   *
   * So the plain click stays exactly what it was, and ctrl/cmd+click selects. This
   * row used to be the odd one out for that — the one place where the modifier
   * meant "pick" while it meant "commit on the spot" everywhere else. It is no
   * longer odd: the modifier means "pick" on every widget now, so this row is just
   * the ordinary gesture sitting next to a plain click that already had a job. Same
   * heat-panel selection, same popup, same Apply.
   *
   * The two used to be layered onto the one click, which was a mistake: the
   * pick set is a multi-select and the calendars can only ever show ONE node, so
   * clicking three rows left three picks and the months of whichever was last.
   * They are separate gestures now because they are separate ideas.
   */
  onCalTreeRowClick(row: HeatRow, ev: MouseEvent): void {
    ev.stopPropagation();
    if (ev.ctrlKey || ev.metaKey) {
      this.pick(this.pathSel(row.id, row.label, row.dim), "heat");
      return;
    }
    this.selectCalNode(row);
  }

  /**
   * The History tree row's tooltip.
   *
   * Not xfTitleFor()'s wording, because this row's plain click does something
   * rather than nothing — it moves the calendars — and that is worth naming on the
   * one row where it is true.
   */
  calTreeTitle(label: string): string {
    return `${label} — click to show its calendar · Ctrl/⌘+click to select`;
  }

  /** A date column header in the Live strip. */
  onDateHeaderClick(iso: string, ev: MouseEvent): void {
    this.onVisualClick(this.daySel(iso), ev, "heat");
  }

  /**
   * A grid row. The fold keeps the plain click it already had — that is what the
   * tree column's +/- is for, and nothing competes for it now that selecting takes
   * a modifier.
   *
   * Everywhere else in the row behaves like the heat tree above, but under the
   * grid panel's own selection: the two panels are separate widgets, so picking
   * in one confirms whatever was open in the other.
   */
  onGridRowClick(row: GridRow, ev: MouseEvent): void {
    this.onVisualClick(
      this.pathSel(row.nodeId, row.label, row.dim),
      ev,
      "grid"
    );
  }

  toggleDropdown(f: FilterDef, ev: MouseEvent): void {
    ev.stopPropagation();
    // Nothing loaded yet (or the filter's API never answered): the chip is
    // there to name the filter, and there is no list to open behind it.
    if (!this.hasOptions(f)) return;
    if (this.openKey === f.key) {
      this.openKey = null;
      return;
    }
    // Seed the working selection from what is currently applied.
    f.checked = [...f.applied];
    f.search = "";
    // Same as the reference, which clears isAddChecked as the list is opened
    // (filter-badges.selectedValues): the applied selection is already seeded
    // above, so the box starts unticked and means "and keep what is applied even
    // though the search has hidden it".
    f.addCurrent = false;
    // Position a fixed menu under the trigger so it isn't clipped by the
    // horizontally-scrolling badge strip.
    const el = (ev.currentTarget as HTMLElement) || null;
    if (el) {
      const r = el.getBoundingClientRect();
      const width = 300;
      this.menuPos = {
        left: Math.min(r.left, window.innerWidth - width - 12),
        top: r.bottom + 4,
      };
    }
    this.openKey = f.key;
  }
  closeDropdown(): void {
    this.openKey = null;
  }
  get openFilter(): FilterDef | undefined {
    return this.filters.find((f) => f.key === this.openKey);
  }

  filteredOptions(f: FilterDef): string[] {
    const s = f.search.trim().toLowerCase();
    return s ? f.options.filter((o) => o.toLowerCase().includes(s)) : f.options;
  }

  // ---- working selection (checkboxes / ng-select) --------------------------
  isChecked(f: FilterDef, opt: string): boolean {
    return f.checked.includes(opt);
  }

  toggleOption(f: FilterDef, opt: string, ev?: Event): void {
    ev?.stopPropagation();
    // A single-select badge (Brand) replaces its value rather than adding to
    // it, and cannot be emptied — it is what the data was fetched for.
    //
    // So the click IS the filter, and it applies here rather than parking a tick
    // for an Include button to commit. That button used to be the whole of the
    // dropdown's header on a single-select, with nothing to decide: one value in,
    // one value out, and no "everything except" to offer. Direct selection drops
    // the step; commitFilter closes the dropdown on the way out.
    //
    // Re-picking the value already applied is not special-cased. It still runs
    // through commitFilter, and onFilterApplied's own sameSelection() test sees
    // that nothing moved — so it re-applies the local filters and never refetches.
    if (f.single) {
      f.checked = [opt];
      f.applied = new Set([opt]);
      this.commitFilter(f);
      return;
    }
    f.checked = f.checked.includes(opt)
      ? f.checked.filter((x) => x !== opt)
      : [...f.checked, opt];
  }

  allChecked(f: FilterDef): boolean {
    const visible = this.filteredOptions(f);
    return visible.length > 0 && visible.every((o) => f.checked.includes(o));
  }

  toggleSelectAll(f: FilterDef, ev?: Event): void {
    ev?.stopPropagation();
    const visible = this.filteredOptions(f);
    if (this.allChecked(f)) {
      f.checked = f.checked.filter((o) => !visible.includes(o));
    } else {
      f.checked = [...new Set([...f.checked, ...visible])];
    }
  }

  /**
   * "Add current selection to filter" — filter-badges.selectAddClick() and
   * multi-select.selectAddClick(), which do the same thing to their own working
   * list: ticking it folds what is already applied into the selection, unticking
   * it takes those values back out again.
   *
   * Untick is scoped to the values on screen, exactly as the reference is: its
   * `optionsData` IS the search-filtered list, so with no search running this puts
   * the working selection back to the ticks the user made and with one it drops the
   * applied values the search is not showing.
   */
  toggleAddCurrent(f: FilterDef, ev?: Event): void {
    ev?.stopPropagation();
    f.addCurrent = !f.addCurrent;
    if (f.addCurrent) {
      f.checked = [...new Set([...f.applied, ...f.checked])];
      return;
    }
    const visible = this.filteredOptions(f);
    f.checked = f.checked.filter((o) => visible.includes(o));
  }

  // ---- apply include / exclude / clear --------------------------------------
  /**
   * What Include would actually apply.
   *
   * The ticks still on screen, or every tick plus the applied selection while "Add
   * current selection to filter" is on — the reference's own rule
   * (filter-badges.applyFilter('include'), which intersects with `optionsData`
   * unless isAddChecked). Off a search the two are the same list, so this only
   * differs where it is meant to.
   */
  private includeValues(f: FilterDef): string[] {
    if (f.addCurrent) return [...new Set([...f.applied, ...f.checked])];
    const visible = this.filteredOptions(f);
    return f.checked.filter((o) => visible.includes(o));
  }

  // Gated on what would be applied rather than on the raw tick count, so Include is
  // dead exactly when it has nothing to send — a filter applied with an empty set
  // matches no record and would blank the screen.
  canInclude(f: FilterDef): boolean {
    return this.includeValues(f).length > 0;
  }
  canExclude(f: FilterDef): boolean {
    return f.checked.length > 0 && f.checked.length < f.options.length;
  }
  /**
   * Clear selection is enabled while the filter is narrowing anything — the
   * reference disables it on `optionsData.length == selectedValue.length`, i.e.
   * once everything is picked, because that is already "no filter".
   */
  canClear(f: FilterDef): boolean {
    return this.hasOptions(f) && f.checked.length < f.options.length;
  }

  applyInclude(f: FilterDef, sc?: { close?: () => void }): void {
    // show only the checked values
    f.applied = new Set(this.includeValues(f));
    this.commitFilter(f, sc);
  }

  applyExclude(f: FilterDef, sc?: { close?: () => void }): void {
    // show everything except the checked values
    f.applied = new Set(f.options.filter((o) => !f.checked.includes(o)));
    this.commitFilter(f, sc);
  }

  /**
   * Clear selection: back to every value, applied.
   *
   * A real apply, not just a wipe of the ticks — same as the reference, where the
   * badge dropdown's button is `toggleSelectAll($event, true, true)` (select all,
   * then filter) and the multi-select's is `applyIncludeExcludeFilter('clear')`
   * (`selectedItems = [...all]`, then apply). "Clear" means clear the FILTER, so
   * the screen comes back to everything rather than sitting on a set of empty
   * checkboxes the user then has to apply.
   */
  applyClear(f: FilterDef, sc?: { close?: () => void }): void {
    f.checked = [...f.options];
    f.applied = new Set(f.options);
    this.commitFilter(f, sc);
  }

  /** Commit a chip's new `applied` set and close whatever it was applied from.
   *  The search and the add-current tick belong to the interaction that just
   *  finished, so both are dropped with it. */
  private commitFilter(f: FilterDef, sc?: { close?: () => void }): void {
    f.search = "";
    f.addCurrent = false;
    this.openKey = null;
    this.onFilterApplied(f);
    sc?.close?.();
  }

  // Closing the ng-select without applying reverts the working selection to
  // what is currently applied (same as the reference multi-select onClose).
  onSelectClose(f: FilterDef): void {
    f.checked = [...f.applied];
    f.search = "";
    f.addCurrent = false;
  }

  /**
   * Is this chip off its default? Drives the chip's count colour and, through
   * anyPartial, the Reset chip.
   *
   * Measured against the chip's own default rather than against "every option",
   * which is what lets a single-select chip take part at all: Brand is always on
   * exactly one of its values, so counting values would either read as narrowed
   * forever or (the previous rule) have to be excluded from Reset altogether.
   * Comparing to defaultApplied asks the question that actually matters — is this
   * the value the screen opens on — and asks it the same way for one value or ten.
   */
  isPartial(f: FilterDef): boolean {
    // Nothing loaded yet (or a filter whose API never answered): no selection, so
    // nothing to be off default about.
    if (!this.hasOptions(f)) return false;
    return !this.sameSelection([...f.applied], f.defaultApplied);
  }

  /**
   * Is the screen off its defaults? Drives the Reset chip in both the filter strip
   * and the side panel, so the chip appears exactly when there is something for it
   * to undo — any chip off its default (isPartial), including the single-select
   * Brand, and the date range moved off the loaded data period (initTimeCycle's
   * defaultFrom/defaultTo).
   *
   * Which is also why a visit that arrives from the dashboard card starts with the
   * chip absent: that load drops the persisted selection, so nothing is off default
   * until the user changes something. See loadDashboard().
   */
  get anyPartial(): boolean {
    return (
      this.filters.some((f) => this.isPartial(f)) ||
      this.dateChanged ||
      // A committed cross-filter narrows the screen exactly as a chip does, so
      // Reset has to offer to undo it — otherwise a page filtered only by a
      // clicked visual shows no way back to everything.
      this.selections.length > 0
    );
  }

  /**
   * Back to the state a fresh visit opens on: every chip on its default (see
   * FilterDef.defaultApplied) and the date range on the loaded data period.
   *
   * A dynamic chip is included — it is the whole reason this takes two paths. Its
   * selection is what the data was fetched FOR, so putting it back is a refetch,
   * not a re-aggregation: the chip's new value goes into the filter's state and the
   * screen reloads for it, exactly as changing it by hand does (onFilterApplied).
   * The reload then settles everything else on its own — a different brand's
   * destinations, file types and date period are its own, which is why the data
   * chips are not touched and the date range is left to the load rather than reset
   * here.
   *
   * With no dynamic chip off default, nothing needs refetching and the data chips
   * are reset in place, ending on setDataPeriodRange() to commit + re-aggregate +
   * persist.
   */
  resetFilters(): void {
    let refetch = false;

    // Cross-filters go first and unconditionally, on both paths: they are not
    // tied to a chip, so neither branch below would otherwise clear them, and a
    // refetch would carry every clicked selection over into the new brand's data.
    // An open selection is discarded rather than confirmed — Reset means "back to
    // everything", so applying half-made picks on the way out would be perverse.
    this.selections = [];
    this.pending = null;

    this.filters.forEach((f) => {
      if (!this.isPartial(f)) return; // already on its default
      f.applied = new Set(f.defaultApplied);
      f.checked = [...f.applied];

      const state = this.dynamicFilterStates.find((s) => s.def.key === f.key);
      if (!state) return;
      state.selected = state.options
        .filter((o) => f.applied.has(o.label))
        .map((o) => o.key);
      refetch = true;
    });

    this.openKey = null;
    this.dateMenuOpen = false;

    if (refetch) {
      // Same pair as a hand-made brand change: the saved selection describes the
      // dataset being replaced, so it is dropped rather than filtered.
      this.clearFilterState();
      this.loadExecutionData();
      return;
    }

    // Every chip is back on its default, so every child is back to the full list
    // its parents now allow. Before the date reset below, which is what commits
    // and re-aggregates.
    this.cascadeFilters();

    // reset the date range back to the data period, which also commits +
    // re-aggregates via applyFilters() and persists (saveFilterState)
    this.setDataPeriodRange(this.selectedCycle);
  }

  // ---- persistence: keep the badge selection + date range across a refresh --
  private saveFilterState(): void {
    if (!this.stateReady) return;
    try {
      const state = {
        // The data-backed chips only. A dynamic filter's selection is user
        // state now (seedFilterState -> setStaticFilters) and a second copy
        // here would outlive the module/dashboard click that clears the first —
        // leaving pickDefaultSelection() two sources that disagree, with the
        // stale one in the store that is never cleared. restoreFilterState()
        // has always skipped these on the way back in; this is the write side
        // of the same rule.
        filters: this.filters.reduce((acc: Record<string, string[]>, f) => {
          if (this.isDynamicFilter(f)) return acc;
          acc[f.key] = [...f.applied];
          return acc;
        }, {}),
        fromDate: this.fromDate,
        toDate: this.toDate,
        selectedCycle: this.selectedCycle,
        // Committed cross-filters ride along with the chips: they are filters in
        // the same sense, so a refresh that keeps one should keep the other.
        // StaticSelection is plain data for exactly this reason — see the note on
        // the interface.
        selections: this.selections,
      };
      localStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch {
      /* storage unavailable (private mode / quota) — ignore */
    }
  }

  /**
   * Leave nothing of the PREVIOUS PAGE behind either.
   *
   * The screen's own state is fresh by construction — the host builds a new instance
   * for every arrival (StaticDashboardHostComponent.mount), so no field, icon flag,
   * expansion or selection can survive a navigation. FilterService cannot rely on
   * that: it is app-scoped, so whatever the last page left on it is still sitting
   * there when this one comes up.
   *
   * These are the fields a page load is expected to settle for itself — the same ones
   * pages.component clears at the top of its own load. A static screen fills none of
   * them (it has no filter widgets, and it builds its own request payloads through
   * DpmStaticService), so the honest value for each is empty rather than whatever the
   * last dynamic dashboard happened to put there.
   */
  private resetSharedFilterState(): void {
    // Badges belong to the dynamic filter widgets. This screen draws its own chips and
    // publishes none of these, so any still here describe the page before it.
    this.filterService.badges = [];
    this.filterService.badgeCheck = false;
    // Extra request parameters a dynamic filter merges into baseQuery. Nothing on this
    // screen reads them, and a leftover one would travel with anything that does.
    this.filterService.duplicateParameter = {};
    this.filterService.previousFilterModel = {};
    this.filterService.hoverBackground = false;
  }

  /** Forget the persisted selection, so the next build defaults everything.
   *  Both the stored copy and the in-memory one, or onDataLoaded() would read
   *  the file back and restore what was just discarded. */
  private clearFilterState(): void {
    this.savedState = null;
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      /* storage unavailable (private mode / quota) — ignore */
    }
  }

  /** The persisted selection for this dashboard, or null. Read on every load
   *  rather than cached once, so a refetch picks up what was just saved. */
  private readSavedState(): any {
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private restoreFilterState(): void {
    const state = this.savedState;
    if (!state) return;

    // Committed cross-filters. Validated field by field rather than trusted:
    // this is parsed localStorage, and a half-shaped entry would reach
    // selectionTest() as a predicate over `undefined` and quietly empty the
    // screen. An unrecognised `kind` is allowed through — selectionTest() answers
    // those with a match-everything predicate, so an older save degrades to an
    // inert chip the user can see and remove rather than to a blank dashboard.
    if (Array.isArray(state.selections)) {
      this.selections = state.selections.filter(
        (s: any) =>
          s &&
          typeof s.id === "string" &&
          typeof s.group === "string" &&
          typeof s.kind === "string" &&
          typeof s.value === "string" &&
          typeof s.label === "string" &&
          // Anything a dimension chip now owns is dropped rather than restored.
          // Status and tree-row selections used to be standalone chips and are
          // saved as such in older state; restoring one now would put a second
          // control on screen for a filter the chip beside it already holds —
          // and the chip beside it is the one that would actually be filtering, so
          // the stale chip could not even be reasoned about. The chip's own saved
          // selection is restored just below and is the one that counts.
          !this.chipFor(s as StaticSelection)
      );
    }

    // Restore each filter's applied set, but only for values that still exist
    // in the current data (options) — "based on the data".
    if (state.filters) {
      this.filters.forEach((f) => {
        // A dynamic chip's selection was already settled by the load sequence
        // (and is what the data was fetched for), so leave it alone.
        if (this.isDynamicFilter(f)) return;
        const saved = state.filters[f.key];
        if (!Array.isArray(saved)) return;
        const valid = saved.filter((v: string) => f.options.includes(v));
        // A single-select chip holds one value however many were saved — a state
        // it cannot be put into by hand, but one an older save (or a chip that has
        // since become single-select) can still be carrying.
        const restored = f.single ? valid.slice(0, 1) : valid;
        // an empty/stale selection falls back to the chip's default, so a
        // multi-select shows everything again rather than hiding data
        f.applied = new Set(restored.length ? restored : f.defaultApplied);
        f.checked = [...f.applied];
      });
    }

    // Restore the date range, clamped to the available data window.
    if (state.selectedCycle && this.timeCycles.includes(state.selectedCycle)) {
      this.selectedCycle = state.selectedCycle;
    }
    if (state.fromDate && state.toDate) {
      let from: string = state.fromDate;
      let to: string = state.toDate;
      if (this.dataMin && from < this.dataMin) from = this.dataMin;
      if (this.dataMax && to > this.dataMax) to = this.dataMax;
      if (from <= to) {
        this.fromDate = from;
        this.toDate = to;
        this.fromDateObj = new Date(from);
        this.toDateObj = new Date(to);
      }
    }
  }

  scrollBadges(dir: number): void {
    this.badgeScroller?.nativeElement?.scrollBy({
      left: dir * 220,
      behavior: "smooth",
    });
  }

  // ---- right-side filter panel ---------------------------------------------
  toggleFilterPanel(): void {
    this.filterPanelOpen = !this.filterPanelOpen;
    this.openKey = null; // don't leave a badge dropdown open behind the panel
    if (this.filterPanelOpen) {
      // seed each ng-select's working selection from what is applied
      this.filters.forEach((f) => (f.checked = [...f.applied]));
    }
  }
  closeFilterPanel(): void {
    this.filterPanelOpen = false;
  }

  // Drag the panel's left edge to resize it (same as app-filter).
  onResizeSide(evt: AngularResizeElementEvent): void {
    this.panelWidth = evt.currentWidthValue;
  }

  // ------------------------------------------------------------ card help icons
  /**
   * Load a card's help text into the shared popover — see cardHelp.
   *
   * Runs on the same click that opens the menu, alongside matMenuTriggerFor rather
   * than instead of it, which is how the real KPI cards do it (card2kpi's
   * `(click)="getInfoDetail()" [matMenuTriggerFor]="info"`). Order between the two
   * does not matter: the popover binds `cardHelp`, so whichever fires second, the
   * content is right by the time it paints.
   */
  showCardHelp(entry: HelpEntry | null): void {
    this.cardHelp = entry;
  }

  /**
   * Restamp the filter bar's note for the current view and range — see noteText.
   *
   * The History wording and its arithmetic are the dynamic screen's, taken from the
   * text-notes widget (components/widget/text-notes), which is what the History
   * dashboards render: same sentence, and the same span inclusive of both ends,
   * hence the +1. A from == to range is one day, not none. Copied rather than
   * reworded so the static screen and the real one can never describe the same
   * range differently.
   *
   * `days` is left undefined until there IS a range, and History then shows no note
   * at all rather than falling back to the Live sentence — a missing statement about
   * the view, instead of a wrong one. The widget hides itself in the same situation
   * (its `*ngIf="noteValue"`), so the blank frame is the reference behaviour too.
   */
  private updateNote(days?: number): void {
    if (!this.isHistory) {
      this.noteText = this.LIVE_NOTE;
      return;
    }
    this.noteText =
      days === undefined
        ? ""
        : `The data shown is from ${days} days from the current date`;
  }

  // ---------------------------------------------------------------- build
  private buildDateColumns(): void {
    this.dateColumns = [];
    if (!this.fromDate || !this.toDate || this.fromDate > this.toDate) {
      // No columns and no span, so nothing for the note to report either.
      this.updateNote();
      return;
    }
    const wd = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const toUTC = (iso: string) => {
      const [y, m, d] = iso.split("-").map(Number);
      return Date.UTC(y, m - 1, d);
    };
    const MS = 86400000;
    const start = toUTC(this.fromDate);
    const end = toUTC(this.toDate);
    // A runaway range would otherwise build a column per day for every node.
    // The cap is per view because the two spend a day very differently: a strip
    // column has to stay wide enough to print its value (so 400 of them is
    // already far past scrolling comfortably), while a calendar day is a cell in
    // a month grid — and History is asked precisely because the span is long, so
    // a 13-month ceiling there would quietly cut the earliest months off.
    const maxDays = this.isHistory ? HISTORY_MAX_DAYS : LIVE_MAX_DAYS;
    for (let t = start; t <= end && this.dateColumns.length < maxDays; t += MS) {
      const d = new Date(t);
      this.dateColumns.push({
        iso: d.toISOString().slice(0, 10),
        dd: String(d.getUTCDate()).padStart(2, "0"),
        mon: MONTH_NAMES[d.getUTCMonth()].slice(0, 3),
        wd: wd[d.getUTCDay()],
      });
    }
    const fmt = (iso: string) => {
      const [y, m, d] = iso.split("-");
      return `${m}/${d}/${y.slice(2)}`;
    };
    this.dateRangeLabel = `${fmt(this.fromDate)} - ${fmt(this.toDate)}`;

    // Counted off `start`/`end` — the very timestamps the columns above were laid
    // out from — rather than recomputed from fromDate/toDate, so the note and the
    // range on screen can only ever describe the same span.
    //
    // In UTC, which is what makes it a whole number: the widget subtracts two
    // local-time Dates, and across a DST boundary that lands on 211.9583… and prints
    // the fraction. UTC midnights are exactly 24h apart.
    //
    // Deliberately the full requested span, NOT dateColumns.length: the two part
    // company once a range exceeds the view's cap (HISTORY_MAX_DAYS), and the note
    // is about the data the screen holds, not how many columns it managed to draw.
    this.updateNote(Math.round((end - start) / MS) + 1);
  }

  /**
   * ngFor identity for the KPI row — see the template, where it is what lets the
   * figures animate instead of being replaced.
   *
   * By `key`, which is also what buildKpis() looks each card's value up by: one
   * name identifies a card everywhere, so a card can never be tracked as one
   * thing and filled as another.
   */
  trackKpi(_: number, k: { key: string }): string {
    return k.key;
  }

  private buildKpis(): void {
    // The preview when a selection is open elsewhere on the page, the committed
    // view when the selection is in this row (or when there is none) — see
    // widgetRows(). The Extracts card is built from the same aggregate below, so
    // its three counts and the two KPI cards can never disagree about which rows
    // they are describing.
    const total = this.aggregate(this.widgetRows("kpi"));
    // Keyed by `key` rather than by position, so reordering KPI_CARDS cannot
    // silently move a number onto the wrong card.
    const values: Record<string, { value: number; subValue: number }> = {
      delayed: { value: total.delayed, subValue: 0 },
      // "Interim: 0" on the sub-line, which is what the reference card carries
      // under its In Process count.
      inProcess: { value: total.inProcess, subValue: total.interim },
    };
    this.kpis = KPI_CARDS.map((c) => ({
      ...c,
      ...(values[c.key] || { value: 0, subValue: 0 }),
    }));

    // The Extracts card is three counts side by side rather than a heading and a
    // number, so it is built here instead of going through KPI_CARDS.
    this.extracts = {
      sent: total.sent,
      expected: total.expected,
      interim: total.interim,
    };
  }

  /**
   * The ring: Sent as a share of Expected.
   *
   * Two concentric bands, the same arrangement DPM's card uses — Sent on the
   * outside wearing its legend colour, Expected inside it as the baseline. The
   * card plots Sent *vs Expected*, so Expected is the whole and Sent is its
   * share: the inner ring closes whenever anything was expected at all, and the
   * outer arc is the headline percentage printed beside it. Both bands are full
   * when every expected file was sent.
   *
   * They used to share one band, with Expected as the track and Sent painted
   * over it — see DonutArc for why that read as an overlap rather than as a ring.
   */
  private buildDonut(): void {
    const a = this.aggregate(this.widgetRows("donut"));
    const raw = a.expected ? (a.sent / a.expected) * 100 : 0;
    // Truncated rather than rounded, so the figure can never read 100% while a
    // file is still outstanding. 0 and 100 lose the decimals entirely.
    const percentage =
      raw === 0 || raw === 100
        ? `${Math.floor(raw)}%`
        : `${(Math.floor(raw * 100) / 100).toFixed(2)}%`;

    const ratio = `(${a.sent}/${a.expected})`;
    const legend = [
      { name: "Sent", value: a.sent, color: COLORS.sent },
      { name: "Expected", value: a.expected, color: COLORS.expected },
      { name: "In Process", value: a.inProcess, color: COLORS.inProcess },
      { name: "Delayed", value: a.delayed, color: COLORS.delayed },
    ];

    // Dynamic type size for the card — see `donutFit`. Both of the card's
    // variable-width columns are measured and the worse of them wins, because
    // they share a line and the type scales as one: the legend's longest row
    // ("In Process: 4" on a quiet day) against the 18 characters that column was
    // laid out for, and the headline with its ratio beside it ("85.71% (12/14)")
    // against 16. Lengths, not pixels, and taken from the values themselves
    // rather than from the DOM — fitTier() has the reasoning.
    this.donutFit = Math.max(
      fitTier(
        Math.max(...legend.map((l) => `${l.name}: ${l.value}`.length)),
        18
      ),
      fitTier(`${percentage} ${ratio}`.length, 16)
    );

    this.donut = {
      percentage,
      ratio,
      arcs: [
        this.donutArc(
          0,
          "Sent",
          COLORS.sent,
          a.expected ? a.sent / a.expected : 0,
          `Sent: ${a.sent}`
        ),
        this.donutArc(
          1,
          "Expected",
          COLORS.expected,
          a.expected ? 1 : 0,
          `Expected: ${a.expected}`
        ),
      ],
      legend,
    };
  }

  /**
   * One ring band: `share` (0..1) of the circle at DONUT_RADII[index].
   *
   * The caller passes the share already worked out, and its own hover label —
   * the two bands are shares of different things, so composing one sentence for
   * both here only made each read like the other.
   *
   * First dasharray value is the drawn length, second the full circumference, so
   * the remainder is the gap the band's track shows through. A full share skips
   * it entirely; see DonutArc.dasharray for why.
   */
  private donutArc(
    index: number,
    name: string,
    color: string,
    share: number,
    tooltip: string
  ): DonutArc {
    const r = DONUT_RADII[index];
    const circumference = 2 * Math.PI * r;
    // Guard the ends: a NaN from an empty dataset would drop the arc's
    // attribute silently and paint a full ring.
    const pct = Number.isFinite(share) ? Math.min(1, Math.max(0, share)) : 0;
    // Rounded, not exact: at 99.95%+ the remaining gap is narrower than the
    // stroke's own round caps, so it cannot be drawn as a gap anyway — it would
    // only render as the seam a closed ring is supposed to be free of.
    const full = pct >= 0.9995;
    return {
      name,
      color,
      r,
      dasharray: full
        ? null
        : `${(pct * circumference).toFixed(2)} ${circumference.toFixed(2)}`,
      tooltip,
      // Looked up by series name rather than passed in: which bands are filter
      // sources is a property of what they plot, and DONUT_SEL is where that is
      // stated. A band with no entry (Expected) simply is not clickable.
      sel: DONUT_SEL[name] || null,
    };
  }

  // A record's tree path — the configured levels, with null/blank levels
  // dropped so branches without (e.g.) a sub-type collapse gracefully. Each
  // segment keeps the dimension it came from, because once blanks are dropped a
  // node's depth no longer indexes HEAT_LEVELS: with source_name blank, a
  // file_type node is the 2nd level in one branch and the first in another. This
  // is the reference widget's `XVariable` (status-overview.ts uses
  // getAccessorsPathkey for exactly the same reason), and it is what lets the
  // grid's Excel put every label in its own column.
  private recordPath(r: any): { dim: string; label: string }[] {
    return this.HEAT_LEVELS.map((dim) => ({ dim, label: this.n(r[dim]) })).filter(
      (p) => p.label && p.label !== "null"
    );
  }

  // Build one node's cells: one per date column, aggregating that node's rows
  // (which include all descendant records) for the day.
  private buildCells(rows: any[]): HeatCell[] {
    // Bucket this node's rows by day once, rather than re-filtering the whole
    // list for every date column — that was O(days x rows) per node, which is
    // the hot path when the tree is deep and the range is long.
    const byDay = new Map<string, any[]>();
    rows.forEach((r) => {
      const iso = this.day(r);
      const bucket = byDay.get(iso);
      if (bucket) bucket.push(r);
      else byDay.set(iso, [r]);
    });

    return this.dateColumns.map((c) => {
      const dayRows = byDay.get(c.iso) || [];
      const status = this.cellStatus(dayRows);
      const count = dayRows.reduce((s, r) => s + this.num(r.sent_records), 0);
      // The bands carry the badges as well as the counts, so the indicators are
      // derived inside cellSegments() from each status's own records.
      const segments = this.cellSegments(dayRows);
      return {
        status,
        color: this.statusColor(status),
        count,
        iso: c.iso,
        rows: dayRows,
        interim: this.cellInterim(dayRows),
        showInterim: false,
        segments,
        // Re-stamped by applyLegendVisibility() straight after the build — along
        // with each band's shownIndicators — so any legend entry already switched
        // off stays off across a rebuild.
        shownSegments: segments,
        hidden: false,
        // Likewise re-stamped by applyHighlight(), which applyFilters() runs
        // after every build for exactly this reason.
        dim: false,
      };
    });
  }

  /**
   * The status bands of one tile: every status present in the cell's records,
   * each carrying its own record count and its own indicator badges.
   *
   * Ordered by the legend rather than by count or priority, so a colour always
   * sits in the same place across a row — the eye can follow one status along
   * the date strip instead of re-reading each tile. Statuses the legend does
   * not name (data can grow one) fall in after those it does, rather than being
   * dropped.
   */
  private cellSegments(rows: any[]): CellSegment[] {
    if (!rows.length) return [];

    // The records per status, not just their sum: a band's badges are derived
    // from its own records, so the bucket has to hold them. One pass, so the
    // grouping costs no more than the counting did.
    const byStatus = new Map<string, any[]>();
    rows.forEach((r) => {
      const status = this.status(r);
      const bucket = byStatus.get(status);
      if (bucket) bucket.push(r);
      else byStatus.set(status, [r]);
    });

    const known = STATUS_LEGEND.map((s) => s.label).filter((s) =>
      byStatus.has(s)
    );
    const unknown = [...byStatus.keys()].filter((s) => !known.includes(s));

    return [...known, ...unknown].map((status) => {
      const statusRows = byStatus.get(status) || [];
      const count = statusRows.reduce(
        (sum, r) => sum + this.num(r.sent_records),
        0
      );
      // Only this status's records are tested, which is the whole point: a DEFECT
      // badge on the Failed band says the failures are what tripped it, where a
      // badge on the tile could as easily have come from a Processed file.
      const indicators = this.cellIndicators(statusRows);
      return {
        status,
        color: this.statusColor(status),
        count,
        countLabel: this.cellCountLabel(status, count),
        onPale: PALE_STATUSES.includes(status),
        indicators,
        // Re-stamped by applyLegendVisibility() straight after the build.
        shownIndicators: indicators,
        // The bucket itself, so a cross-highlight can ask this band what it
        // holds. Shared with byStatus rather than copied — nothing mutates it.
        rows: statusRows,
        // Re-stamped by applyHighlight() straight after the build.
        dim: false,
      };
    });
  }

  // Formatted count for a cell, or '' when the status means "no records
  // arrived" — mirrors the `cellValueAccessor` guard in the real dpm config,
  // which blanks the value for Missing / Not Expected rather than printing 0.
  private cellCountLabel(status: string, count: number): string {
    if (STATUSES_WITHOUT_COUNT.includes(status)) return "";
    // A 0 IS printed, unlike DPM, which blanks it.
    //
    // dem/config.ts's cellValueAccessor returns '' for a null `sent_records`, and
    // its cellValueFormatter is d3.format(','), which coerces that '' to 0 and
    // prints "0". So the reference shows a 0 on exactly the tiles this feed has no
    // count for — every Not Started day — and blanking it here would leave those
    // grey tiles empty where the real screen labels them.
    return count.toLocaleString("en-US");
  }

  // Assemble the nested tree by inserting each record along its full path, then
  // attaching that record to every node on the path so ancestors aggregate
  // their descendants' status + indicators (mirrors aggregate4Ancestors).
  private buildHeatmap(): void {
    const roots: HeatRow[] = [];
    const index = new Map<string, HeatRow>();

    const getNode = (
      parts: { dim: string; label: string }[],
      level: number
    ): HeatRow => {
      const labels = parts.map((p) => p.label);
      const id = labels.join("/");
      let node = index.get(id);
      if (!node) {
        node = {
          id,
          label: labels[labels.length - 1],
          dim: parts[parts.length - 1].dim,
          level,
          // Roots open, deeper levels closed.
          //
          // DPM starts everything collapsed, and dem/config.ts's tree says
          // `collapsedAll: true` — but collapsing the root here leaves the whole
          // screen as a single "Sanofi" row with one strip of aggregate tiles and
          // no counts on any of them. The reference screen opens with its file
          // types on show, so level 0 opens and everything below it waits to be
          // asked for — including a sub_file_type level, which is a narrowing of
          // the file type above it rather than the level the screen leads with.
          expanded: level === 0,
          hasChildren: false,
          parentLabels: labels.slice(0, -1),
          // Filled once the tree is complete, when `hasChildren` is finally known
          // — see the stamp in buildHeatmap() below.
          freq: "",
          cutoff: "",
          rows: [],
          cells: [],
          children: [],
          // Re-stamped by applyHighlight() after the build completes.
          dimmed: false,
          // Re-stamped by refreshHeatFigures(), which runs at the end of the build.
          hidden: false,
        };
        index.set(id, node);
        if (level === 0) {
          roots.push(node);
        } else {
          const parent = getNode(parts.slice(0, -1), level - 1);
          parent.children.push(node);
          parent.hasChildren = true;
        }
      }
      return node;
    };

    this.viewData.forEach((r) => {
      const path = this.recordPath(r);
      if (!path.length) return;
      for (let i = 0; i < path.length; i++) {
        getNode(path.slice(0, i + 1), i).rows.push(r);
      }
    });

    index.forEach((node) => {
      node.cells = this.buildCells(node.rows);
      // The schedule and cutoff pills, now that `hasChildren` is settled. Only a
      // row that IS a file type carries them — on an aggregate the two would have
      // to summarise every schedule beneath it. Read off the first record because
      // both columns are properties of the file type, so every record under a
      // leaf agrees on them.
      if (!node.hasChildren) {
        node.freq = this.n(node.rows[0]?.file_freq_desc);
        node.cutoff = this.n(node.rows[0]?.file_receive_cutoff_time);
      }
    });

    this.heatRows = this.orderNodes(roots);
    // The cells above were built from the full view, so whatever narrowing was on
    // them is gone with them — say so before refreshHeatFigures() works out whether
    // it has anything to do, then let it re-narrow for a selection that is still
    // open. It early-returns when there is none, which is the usual case here.
    this.heatNarrowed = false;
    this.refreshHeatFigures();
    this.refreshVisibleHeat();
    // A rebuild replaces every cell object, so the visibility flags have to be
    // re-stamped for whatever the legends still have switched off.
    this.applyLegendVisibility();
    // Fresh nodes start collapsed, so the header icon has to fall back to
    // "Expand All" instead of describing the tree that was just discarded.
    this.syncHeatExpandIcon();
    // The rebuild just replaced every node, so the History view's selection is
    // pointing at a discarded one — re-find it and redraw the months.
    this.syncCalendarSelection();
  }

  /**
   * Put each level's nodes in the order the backend gave for it, recursively.
   *
   * Without this the rows come out in whatever order the records happened to
   * arrive in, which for this feed is alphabetical by file type and puts HCO above
   * Patient View. The reference leads with the core SP files and lets the rest fall
   * in behind them.
   *
   * Two sources, in this order:
   *   1. what the tree-order report answered with for that level (see
   *      DpmStaticService.getTreeOrder) — the same api_key the dynamic widgets
   *      order their trees by, so this screen follows the configured hierarchy
   *      wherever it is changed;
   *   2. dem/config.ts's ITEMS_ORDER, the reference order this screen shipped
   *      with, for a level the report says nothing about (or if the call fails).
   *
   * Named entries first, in their listed order; everything else after them,
   * alphabetically. Alphabetically rather than in data order because data order is
   * an accident of the response and would shift between loads — a name the list
   * does not mention should still land somewhere predictable.
   */
  private orderNodes(nodes: HeatRow[]): HeatRow[] {
    if (!nodes.length) return nodes;
    const dim = nodes[0].dim;
    const wanted = this.treeOrder[dim] || ITEMS_ORDER[dim] || [];
    const rank = (n: HeatRow) => {
      const i = wanted.indexOf(n.label);
      return i === -1 ? wanted.length : i;
    };
    nodes.sort((a, b) => {
      const ra = rank(a);
      const rb = rank(b);
      return ra !== rb ? ra - rb : a.label.localeCompare(b.label);
    });
    nodes.forEach((n) => {
      if (n.children.length) this.orderNodes(n.children);
    });
    return nodes;
  }

  /**
   * True while the heat map's cells were last built from a NARROWED record set.
   *
   * What lets refreshHeatFigures() do nothing on the common path. Rebuilding every
   * cell is the expensive half of buildHeatmap() — a tile per node per day — and a
   * pick made in the heat map itself narrows nothing, so without this flag every
   * click on the strip would pay for a full rebuild to arrive back where it started.
   * It also has to be remembered rather than recomputed: when a selection is
   * cleared there is nothing left to compare against, and the cells still need
   * putting back.
   */
  private heatNarrowed = false;

  /**
   * Re-derive the heat map's tiles against the open selection, and drop the branches
   * it empties.
   *
   * The mirror of refreshGridFigures(), and it exists for the mirror-image reason:
   * a pick in the GRID used to leave this panel fading rows while its tiles went on
   * counting records the selection excluded. Each panel now narrows when the other
   * one is being picked in.
   *
   * `widgetRows` is what decides whether anything narrows, so the rule stays
   * symmetrical: the panel the popup is anchored to keeps every row and every tile
   * intact — applyHighlight()'s dimming is its feedback — and only the panels the
   * selection did NOT come from narrow. That is what keeps a second pick possible on
   * the strip you are working in.
   *
   * Cells are rebuilt through buildCells() rather than adjusted, because a tile is
   * not a number: it is a stack of status bands, an interim outline and a row of
   * indicator badges, all derived together. Re-deriving from the narrowed records is
   * the only way they can agree with each other. The TREE survives — same nodes,
   * same expansion, same order — so un-picking brings the rows back exactly as they
   * were, which a buildHeatmap() rebuild could not do.
   */
  private refreshHeatFigures(): void {
    const rows = this.widgetRows("heat");
    const keep = rows === this.viewData ? null : new Set(rows);
    // Nothing to narrow, and nothing was narrowed last time: the tiles already say
    // what they should. See heatNarrowed.
    if (!keep && !this.heatNarrowed) return;
    this.heatNarrowed = !!keep;

    const walk = (nodes: HeatRow[]) => {
      nodes.forEach((n) => {
        const mine = keep ? n.rows.filter((x) => keep.has(x)) : n.rows;
        n.cells = this.buildCells(mine);
        n.hidden = !!keep && n.rows.length > 0 && mine.length === 0;
        if (n.children.length) walk(n.children);
      });
    };
    walk(this.heatRows);

    // Every cell object above is a new one, so the two things that live ON cells
    // have to be re-applied — exactly the duty buildHeatmap() discharges after its
    // own build. The legend flags first, then the flat list (whose membership the
    // `hidden` stamps just changed).
    this.applyLegendVisibility();
    this.refreshVisibleHeat();
    // The History view reads its months straight off calNode.cells, which have just
    // been replaced — so the calendar is drawn from the narrowed tiles too, rather
    // than from the ones the strip no longer shows.
    this.buildCalendar();
  }

  // Flatten the tree into the visible, indented row list (respecting expansion).
  private refreshVisibleHeat(): void {
    const out: HeatRow[] = [];
    const walk = (nodes: HeatRow[]) => {
      for (const n of nodes) {
        // A branch the open selection excludes is dropped, subtree and all — see
        // HeatRow.hidden. The recursion is skipped with it, or a hidden parent's
        // children would surface as orphans at the top of the strip.
        if (n.hidden) continue;
        out.push(n);
        if (n.expanded && n.children.length) walk(n.children);
      }
    };
    walk(this.heatRows);
    this.visibleHeatRows = out;
    // Tree width first: the count sizing below budgets the tiles from what is
    // left over, so it has to know how wide the tree column has just become.
    this.updateHeatTreeWidth();
    this.updateHeatCountSizing();
  }

  // ============================================== history (calendar) view
  /**
   * Keep the calendar pointed at a node that still exists, then rebuild it.
   *
   * Called after every heat map rebuild, because a rebuild replaces every node
   * object: holding the old reference would leave the calendar drawing cells
   * from data that has been filtered away. The selection is re-found by `id`
   * (the node's full path), so it survives a refetch or a filter change as long
   * as the branch is still there — and falls back to the first root when it is
   * not, so the view is never blank with a tree beside it. That fallback is also
   * the first load's default: the calendar opens on the top of the tree.
   */
  private syncCalendarSelection(): void {
    if (!this.isHistory) return;
    const wanted = this.calNode?.id;
    let node = wanted ? this.findHeatNode(wanted) : null;

    if (!node) {
      // Open on the tree's FIRST ROOT — the top of the hierarchy (SP), collapsed,
      // exactly as the strip opens.
      //
      // Its months are the totals for everything beneath it, which is the summary
      // this view is entered for. The last level is where the counts and the tile
      // popups live (isCellInteractive), so it is one or two folds away rather
      // than on screen — .cal-note above the months says so, and says which row
      // to pick.
      //
      // No expansion on the way in: a root is visible in the tree by definition,
      // so the selected row is never one the tree beside it is not showing.
      node = this.heatRows[0] || null;
    }

    this.calNode = node;
    this.buildCalendar();
  }

  /**
   * Whether the calendar is on a node that can be read in detail.
   *
   * The template says so out loud when it is not: a tile above the last level
   * showing bands but answering neither a hover nor a click is indistinguishable
   * from a broken one.
   */
  get calNodeIsLastLevel(): boolean {
    return !!this.calNode && !this.calNode.hasChildren;
  }

  /** Depth-first lookup by node id (its full path). */
  private findHeatNode(id: string): HeatRow | null {
    let found: HeatRow | null = null;
    const walk = (nodes: HeatRow[]) => {
      for (const n of nodes) {
        if (found) return;
        if (n.id === id) {
          found = n;
          return;
        }
        if (n.children.length) walk(n.children);
      }
    };
    walk(this.heatRows);
    return found;
  }

  /**
   * The selected node's cells, laid out as one grid per calendar month.
   *
   * Built off the node's existing `cells` rather than re-reading the records:
   * buildCells() already aggregated each day into the status bands, count,
   * indicators and anomaly the tile draws, so a calendar day and a strip tile
   * are guaranteed to be the same cell — the same object, in fact, which is why
   * a legend toggle or a note added from a popup shows up in both views without
   * the calendar knowing either happened.
   *
   * Months run newest first, which is the order the reference view uses: with a
   * long history the month you want is nearly always the most recent one.
   */
  private buildCalendar(): void {
    this.calMonths = [];
    const node = this.calNode;
    if (!node || !this.dateColumns.length) return;

    const byIso = new Map<string, HeatCell>();
    node.cells.forEach((c) => byIso.set(c.iso, c));

    // Which months to draw: every month the loaded range touches, taken from the
    // range's own columns rather than walked from/to, so the calendar can never
    // put up a month the strip has no data for at all.
    const monthKeys = new Set<string>();
    this.dateColumns.forEach((col) => monthKeys.add(col.iso.slice(0, 7))); // "2026-07"

    const months: CalMonth[] = [];
    monthKeys.forEach((key) => {
      const [y, m] = key.split("-").map(Number);
      const pad = (): CalDay => ({
        iso: "",
        dom: "",
        offset: true,
        cell: null,
      });

      const weeks: CalWeek[] = [];
      // The 1st rarely falls on a Sunday, so its week starts with padding — that
      // is what puts every date under its real weekday column.
      let days: CalDay[] = Array.from(
        { length: new Date(Date.UTC(y, m - 1, 1)).getUTCDay() },
        pad
      );
      // The row's date is its first real day, so a week that opens with padding
      // is still labelled by the 1st rather than by a day in the month before.
      let label = "";

      const flush = () => {
        while (days.length < 7) days.push(pad());
        weeks.push({ label, days });
        days = [];
        label = "";
      };

      // Every day of the month, not only the days the range covers: one day of
      // data earns the whole grid. A month clipped to the range came out as a
      // fragment — a February starting on the 12th because that is where the
      // range opened, which reads as a month with no first fortnight rather than
      // as the edge of the data.
      //
      // Day 0 of the next month is the last of this one, so this is that month's
      // real length, leap years included.
      const lastDom = new Date(Date.UTC(y, m, 0)).getUTCDate();
      for (let d = 1; d <= lastDom; d++) {
        const dom = String(d).padStart(2, "0");
        if (!label) label = dom;
        days.push({
          iso: `${key}-${dom}`,
          dom,
          // null outside the range: buildCells() only makes a cell per date
          // column, so a day the range does not reach draws the same outlined
          // empty slot a day with no records does, and stays inert — the
          // calendar handlers all gate on `cell` (onCalDayEnter and friends).
          offset: false,
          cell: byIso.get(`${key}-${dom}`) || null,
        });
        if (days.length === 7) flush();
      }
      if (days.length) flush();

      months.push({ key, label: `${MONTH_NAMES[m - 1]} - ${y}`, weeks });
    });

    // Whichever way the header's Sort icon has the months running — newest first
    // by default, because with a long history the month being looked for is
    // nearly always the most recent one. `key` is "YYYY-MM", so it sorts
    // chronologically as a string and needs no date parsing.
    const dir = this.calSortOrder === "asc" ? -1 : 1;
    this.calMonths = months.sort((a, b) =>
      a.key < b.key ? dir : a.key > b.key ? -dir : 0
    );
  }

  /**
   * Flip the month order — the header's Sort icon, History only.
   *
   * Rebuilds rather than reversing `calMonths` in place, so the order is decided
   * in exactly one place (buildCalendar) and cannot drift from what a rebuild
   * would produce. The grids are re-made from the node's existing cells, so this
   * re-reads no records and the tiles keep the very same HeatCell objects.
   */
  private toggleCalSort(): void {
    this.calSortOrder = this.calSortOrder === "asc" ? "desc" : "asc";
    // The months are about to move out from under any open tile panel.
    this.hideCellTip();
    this.hideBadgePop();
    this.buildCalendar();
    this.cdr.detectChanges();
  }

  // ---- calendar day interaction --------------------------------------------
  // Thin wrappers so the day's handlers read like the strip's: a padding day has
  // no cell to describe and nothing is selected before the first build, and both
  // are this view's concern rather than something the shared handlers should
  // have to know about. The level gate still lives in isCellInteractive().
  //
  // There is deliberately no dblclick wrapper: the control chart popup belongs to
  // the Live strip, so the calendar day leaves the binding off entirely rather
  // than routing to a handler that would refuse — see the template.
  onCalDayEnter(day: CalDay, ev: MouseEvent): void {
    if (!day?.cell || !this.calNode) {
      this.hideCellTip();
      return;
    }
    this.onCellEnter(day.cell, this.calNode, ev);
  }

  /**
   * A calendar tile.
   *
   * Plain click is left exactly as it was — the comment popup, this view's own
   * interaction. ctrl/cmd+click selects the day instead, the same gesture every
   * other visual answers, which is how the History view reaches what the Live strip
   * offers on its date column headers: a month grid has no per-day header to put
   * them on, and its day tiles are the only thing that names a single date.
   *
   * The day selection does not need a cell behind it — an empty tile is still a
   * date, and narrowing to it is a legitimate (if empty) answer. The comment
   * popup does, hence the two guards falling in that order.
   */
  onCalDayClick(day: CalDay, ev?: MouseEvent): void {
    if (!day || day.offset) return;
    if (ev && (ev.ctrlKey || ev.metaKey)) {
      // Picks into the heat panel's selection, which is all the modifier ever does
      // now — see onVisualClick(). This tile reaches pick() directly rather than
      // through it only because its plain click belongs to the comment popup.
      ev.stopPropagation();
      this.pick(this.daySel(day.iso), "heat");
      return;
    }
    if (!day.cell || !this.calNode) return;
    this.onCellClick(day.cell, this.calNode);
  }

  /**
   * Pick the node the calendars describe.
   *
   * Every level is selectable, not just the leaves: a parent's cells already
   * aggregate its whole branch (buildHeatmap attaches a record to every node on
   * its path), so selecting "Commercial" is a real answer — the month totals
   * for everything under it — rather than an empty grid.
   */
  selectCalNode(row: HeatRow, ev?: Event): void {
    ev?.stopPropagation();
    this.hideCellTip();
    this.calNode = row;
    this.buildCalendar();
    this.cdr.detectChanges();
  }

  isCalNode(row: HeatRow): boolean {
    return this.calNode?.id === row.id;
  }

  // =================================================== heat legend interaction
  // Both legends toggle one entry at a time, independently: switching a status
  // off blanks its cells, switching an indicator off removes that badge from
  // every tile. Clicking the same entry again brings it back.

  toggleStatus(label: string): void {
    if (this.hiddenStatuses.has(label)) this.hiddenStatuses.delete(label);
    else this.hiddenStatuses.add(label);
    this.applyLegendVisibility();
  }

  toggleIndicator(name: string): void {
    if (this.hiddenIndicators.has(name)) this.hiddenIndicators.delete(name);
    else this.hiddenIndicators.add(name);
    this.applyLegendVisibility();
  }

  isStatusShown(label: string): boolean {
    return !this.hiddenStatuses.has(label);
  }

  isIndicatorShown(name: string): boolean {
    return !this.hiddenIndicators.has(name);
  }

  // Stamp each cell's visibility once per click — `shownSegments` / `hidden` for
  // the status legend, and each band's `shownIndicators` for the indicator legend.
  // The template then only reads those, so change detection never re-evaluates
  // predicates across thousands of cells, which is what keeps a long date range
  // cheap.
  private applyLegendVisibility(): void {
    const hiddenStatuses = this.hiddenStatuses;
    const hiddenIndicators = this.hiddenIndicators;

    const stamp = (nodes: HeatRow[]) => {
      nodes.forEach((n) => {
        for (const c of n.cells) {
          // Switching a status off removes its band and leaves the rest of the
          // tile standing; only when it was the cell's last band does the whole
          // tile fall back to the empty slot.
          c.shownSegments = hiddenStatuses.size
            ? c.segments.filter((s) => !hiddenStatuses.has(s.status))
            : c.segments;
          c.hidden = !!c.segments.length && !c.shownSegments.length;
          // Badges hang off the bands, so the indicator legend is applied per
          // band. Every segment, not just the shown ones — a status switched back
          // on must come back with its badges already correct.
          //
          // Reuse the same array when nothing is switched off, so the common case
          // allocates nothing and *ngFor keeps its existing DOM.
          for (const s of c.segments) {
            s.shownIndicators = hiddenIndicators.size
              ? s.indicators.filter((i) => !hiddenIndicators.has(i.name))
              : s.indicators;
          }
          // Switching the border indicator off in the legend drops the outline,
          // the reference widget's borderBadgeAccessor consults `marked`. Still
          // per cell: the outline wraps the tile, not a band.
          c.showInterim =
            c.interim && !hiddenIndicators.has(BORDER_INDICATOR);
        }
        if (n.children.length) stamp(n.children);
      });
    };
    // Walks the whole tree, not just visibleHeatRows, so collapsed branches are
    // already correct when they are expanded.
    stamp(this.heatRows);
  }

  // ===================================================== heat tree column width
  // Size the tree column to the tree that is on screen: the widest row wins, and
  // a row needs its indent + its fold toggle + its label. Runs from
  // refreshVisibleHeat(), so every expand, collapse and rebuild re-fits it — that
  // is what makes the tiles give up space as the tree deepens and take it back as
  // it closes. Arithmetic only, no layout read, so it is free to run per toggle.
  private updateHeatTreeWidth(): void {
    // The pill columns first: they are the same width on every row, so they are
    // fitted to the widest value on screen before any row's total is worked out.
    let freq = 0;
    let cutoff = 0;
    for (const row of this.visibleHeatRows) {
      freq = Math.max(freq, this.pillWidth(row.freq));
      cutoff = Math.max(cutoff, this.pillWidth(row.cutoff));
    }
    this.freqColPx = Math.ceil(freq);
    this.cutoffColPx = Math.ceil(cutoff);

    let needed = 0;
    for (const row of this.visibleHeatRows) {
      const w =
        this.HEAT_TREE_PAD_PX +
        row.level * this.HEAT_TREE_INDENT_PX +
        // Every row pays for the fold, whether it has one or not: a leaf renders a
        // .fold-spacer in its place so labels line up per level instead of a
        // childless row starting 26px left of its own parent.
        this.HEAT_FOLD_PX +
        // The LABEL is the part that is allowed to be too long, so the ceiling is
        // applied here rather than to the row's total. A file type with a very
        // long name ellipsizes and answers with a tooltip on hover; the pills
        // beside it are short fixed strings that must be readable in full, so
        // capping the whole row made the two compete and the pills lost.
        Math.min(
          this.measureLabel(row.label, row.level === 0),
          this.HEAT_LABEL_MAX_PX
        ) +
        // The pill columns, at their full measured width. Never negotiable: a
        // clipped "M, T, W, TH , F" says something different from the schedule it
        // is reporting. Both are added for every row, not just the rows that have
        // pills, because the columns exist for the whole tree — that is what keeps
        // them aligned.
        this.freqColPx +
        this.cutoffColPx +
        // Three gaps, not two: .tree-cell's gap sits in front of the label as well
        // as in front of each pill.
        this.PILL_GAP_PX * 3 +
        this.HEAT_TREE_TAIL_PX;
      if (w > needed) needed = w;
    }
    // Floor only. There is no upper clamp on the total any more — everything left
    // in it after the label cap is content that has to be shown at its own size.
    this.heatTreeColPx = Math.round(
      Math.max(this.HEAT_TREE_MIN_PX, needed)
    );
  }

  /** What one pill needs for `text`, or 0 for a row that has none. Measured at
   *  .row-pill's 10px rather than the label's 12px. */
  private pillWidth(text: string): number {
    if (!text) return 0;
    return this.measureLabel(text, false, this.PILL_FONT_PX) + this.PILL_PAD_PX;
  }

  /**
   * A label's rendered width. Measured on a canvas rather than in the DOM: it
   * needs the width a label *would* take, which the ellipsized element can no
   * longer report, and reading it off the page per row would thrash layout on
   * every toggle. The 2d context is created once and kept.
   *
   * Falls back to the same character-ratio estimate the tile counts use where
   * canvas is unavailable; the column is a little loose then, never too tight.
   */
  private measureLabel(
    text: string,
    bold: boolean,
    fontPx: number = this.HEAT_LABEL_FONT_PX
  ): number {
    if (this.labelCtx === undefined) {
      this.labelCtx = document.createElement("canvas").getContext("2d");
    }
    if (!this.labelCtx) {
      return text.length * fontPx * this.HEAT_CHAR_RATIO;
    }
    // Matches .node-label (and its .lvl0 weight) in the stylesheet; `fontPx` is
    // what lets the row pills be measured at their own smaller size.
    this.labelCtx.font = `${bold ? 600 : 400} ${fontPx}px Poppins, sans-serif`;
    return this.labelCtx.measureText(text).width;
  }

  /**
   * Give a tree label a tooltip only when the column had to clip it. The text is
   * already legible otherwise, and a title on every row turns ordinary mouse
   * travel down the tree into a trail of popups.
   *
   * Written straight to the DOM node on hover instead of bound in the template:
   * whether a label is clipped is a rendered fact (scrollWidth vs clientWidth)
   * that no expression can answer, and doing it here costs nothing per row and
   * needs no extra change-detection pass.
   */
  onTreeLabelHover(ev: MouseEvent): void {
    const el = ev.target as HTMLElement | null;
    if (!el) return;
    // 1px of slack: sub-pixel text widths round these two apart on their own.
    if (el.scrollWidth > el.clientWidth + 1) {
      el.setAttribute("title", (el.textContent || "").trim());
    } else {
      el.removeAttribute("title");
    }
  }

  // ==================================================== heat leaf-tile counts
  // Guarantee each date column can print its widest leaf value legibly, then
  // adapt the font to the width the column actually received. Pure arithmetic
  // for the guarantee (no layout read), one measurement for the adaptation.
  private updateHeatCountSizing(): void {
    let chars = 0;
    let segments = 1;
    for (const row of this.visibleHeatRows) {
      if (row.hasChildren) continue; // only leaf tiles print a value
      for (const cell of row.cells) {
        // The bands of one tile share its width, so what the column has to fit
        // is the whole tile's text — every band's count side by side, not the
        // widest single one.
        let cellChars = 0;
        for (const seg of cell.segments) cellChars += seg.countLabel.length;
        if (cellChars > chars) chars = cellChars;
        if (cell.segments.length > segments) segments = cell.segments.length;
      }
    }
    this.heatMaxCountChars = chars;

    const colMin = Math.max(
      26, // preserves the original tile width when values are short
      Math.ceil(chars * this.HEAT_COUNT_MIN_FONT * this.HEAT_CHAR_RATIO) +
        8 +
        // the gutters between the bands are width the counts cannot use
        (segments - 1) * this.HEAT_COL_SPACING_PX
    );
    const cols = this.dateColumns.length;
    this.heatTableMinPx = Math.max(
      760, // the table's original floor
      this.heatTreeColPx + cols * (colMin + this.HEAT_COL_SPACING_PX)
    );

    this.measureHeatCountFont();
    // Against the layout still on screen, so a rebuild's tiles are drawn with the
    // caps they will keep instead of showing the ⋯ disc for one frame and then
    // unfolding into badges. Tile widths do not move on a refilter (the columns
    // are the same), so the outgoing measurement is the incoming one; on the very
    // first build there is nothing to read and scheduleTileMetrics() is what
    // settles it.
    this.measureTileWidth();
  }

  // Fit the font to the rendered column width. Runs after layout, and only on
  // the events that can change it (build, expand, resize, fullscreen).
  private measureHeatCountFont(): void {
    const chars = this.heatMaxCountChars;
    if (!chars) return;

    const cell = this.heatScrollEl?.nativeElement?.querySelector(
      "td.heat-cell"
    ) as HTMLElement | null;
    const width = cell?.getBoundingClientRect().width || 0;
    if (!width) return; // not laid out yet; the scheduled pass will catch it

    const fitted = (width - 6) / (chars * this.HEAT_CHAR_RATIO);
    this.heatCountFontPx = Math.round(
      Math.min(this.HEAT_COUNT_MAX_FONT, Math.max(this.HEAT_COUNT_MIN_FONT, fitted))
    );
  }

  /**
   * The rendered width of one tile, for bandBadgeCap().
   *
   * Not derivable: the strip's columns are sized by updateHeatCountSizing() and
   * the calendar's by its day grid, and both move with the viewport, the tree
   * column's width and fullscreen. Only the view on screen is measured — the two
   * are mutually exclusive (*ngIf on isHistory), so one width serves the tile
   * template they share. Every tile in a view is the same width: the strip's table
   * is table-layout: fixed and the calendar's day columns are all 1fr.
   */
  private measureTileWidth(): void {
    const tile = this.isHistory
      ? (this.host.nativeElement.querySelector(
          ".cal-day"
        ) as HTMLElement | null)
      : (this.heatScrollEl?.nativeElement?.querySelector(
          "td.heat-cell"
        ) as HTMLElement | null);
    const width = tile?.getBoundingClientRect().width || 0;
    if (!width || width === this.tileWidthPx) return; // not laid out yet, or unchanged

    this.tileWidthPx = width;
    this.bandCapCache.clear();
  }

  /**
   * How many badges one band of an `bands`-band tile can carry before the set has
   * to collapse to the ⋯ disc.
   *
   * The badges hang off the band, not the tile, so the room they have is the
   * band's share of the tile: the bands divide it equally (`flex: 1 1 0`) with a
   * gutter between each pair. A band is allowed the 3px .cell-badges already hangs
   * off its right edge and nothing on the left, because a badge crossing the left
   * edge is over the band before it — which is the "no space" this is here to
   * find.
   *
   * Never returns 0: a band too narrow for one badge is too narrow for the disc
   * too, so there is nothing to be gained by collapsing further. On the narrowest
   * multi-band tiles the single badge does overhang its band — accepted, because
   * the alternative is a status whose indicators are not shown at all.
   */
  private bandBadgeCap(bands: number): number {
    if (bands < 1) return 1;
    const cached = this.bandCapCache.get(bands);
    if (cached !== undefined) return cached;

    // No measurement yet — one a band, corrected by the next scheduled pass.
    let cap = 1;
    if (this.tileWidthPx) {
      const bandWidth =
        (this.tileWidthPx - (bands - 1) * this.HEAT_COL_SPACING_PX) / bands;
      // n badges span n*BADGE + (n-1)*GAP, so adding one gap back makes the
      // division exact rather than one badge pessimistic.
      const room = bandWidth + this.BADGE_OVERHANG_PX + this.BADGE_GAP_PX;
      cap = Math.max(1, Math.floor(room / (this.BADGE_PX + this.BADGE_GAP_PX)));
    }
    this.bandCapCache.set(bands, cap);
    return cap;
  }

  // Re-measure after the browser has laid out (fullscreen transition, expand,
  // resize), then flush so the new count font and band badge caps paint in the
  // same frame. Both read rendered geometry, so both belong to this one pass.
  private scheduleTileMetrics(): void {
    setTimeout(() => {
      this.measureHeatCountFont();
      this.measureTileWidth();
      this.cdr.detectChanges();
    });
  }

  // ======================================================= heat widget actions
  // icon-chart owns the fullscreen transition itself (it calls requestFullscreen
  // on the element handed to [expand]) and reports back here — but its report is
  // not authoritative, so this only re-reads the DOM. See
  // syncFullscreenFromDom().
  onHeatFullscreen(): void {
    this.syncFullscreenFromDom();
    this.scheduleTileMetrics();
    // closeFullscreen() clears icon-chart's `expandChart` on the way out, so
    // re-assert what the tree is actually showing.
    this.syncHeatExpandIcon();
  }

  onGridFullscreen(): void {
    this.syncFullscreenFromDom();
    this.syncGridExpandIcon();
  }

  // Fullscreen can also start or end without the header icon being involved —
  // Esc, F11, the browser's own "exit fullscreen" affordance. icon-chart does
  // listen for fullscreenchange, but with @HostListener('fullscreenchange'),
  // i.e. on its own host element, and the element that actually goes fullscreen
  // is the panel's article — its ANCESTOR. The event fires there and bubbles
  // upwards, so it never reaches the icon-chart host.
  @HostListener("document:fullscreenchange")
  @HostListener("document:webkitfullscreenchange")
  @HostListener("document:mozfullscreenchange")
  @HostListener("document:MSFullscreenChange")
  onDocumentFullscreenChange(): void {
    this.syncFullscreenFromDom();
  }

  // The single source of truth for both panels: what the browser has actually
  // promoted. icon-chart's (screenChanges) alone cannot be trusted — its
  // openFullscreen() emits `true` from a 100ms timer regardless of whether
  // requestFullscreen succeeded, and it skips the request entirely when
  // something else is already fullscreen. Taking that flag at face value leaves
  // a panel wearing .is-active (height: 100dvh) as a full-height sheet *inside*
  // the page with its icon stuck on "Exit Full Screen". Reading
  // document.fullscreenElement means each icon can only ever claim what is
  // really on screen, and because this runs on fullscreenchange too, any
  // disagreement corrects itself on the next frame.
  private syncFullscreenFromDom(): void {
    const doc = document as any;
    const fsEl: Element | null =
      document.fullscreenElement ||
      doc.webkitFullscreenElement ||
      doc.mozFullScreenElement ||
      doc.msFullscreenElement ||
      null;
    // Scoped per panel, so whichever article the browser promoted (or neither)
    // leaves the other one reading "not fullscreen" rather than flipping both.
    const heat = !!fsEl && fsEl.classList.contains("heat-fs");
    const grid = !!fsEl && fsEl.classList.contains("grid-fs");

    // Each guard skips the work when this panel is already in the right state.
    if (heat !== this.heatFullscreen) {
      this.heatFullscreen = heat;
      // The tiles are about to be laid out somewhere else entirely, so a tooltip
      // placed against the old layout has nothing left to point at.
      this.hideCellTip();
      this.scheduleTileMetrics();
      this.syncHeatExpandIcon();
    }
    if (grid !== this.gridFullscreen) {
      this.gridFullscreen = grid;
      this.syncGridExpandIcon();
    }
  }

  // icon-chart's gridload_circle emits expand/collapse. On a tree heat map the
  // useful analogue of the grid widgets' expand-all is revealing the whole
  // hierarchy, so that is what it drives here.
  onHeatExpandToggle(expanded: any): void {
    const open = !!expanded;
    const walk = (nodes: HeatRow[]) => {
      nodes.forEach((n) => {
        if (n.hasChildren) n.expanded = open;
        if (n.children.length) walk(n.children);
      });
    };
    walk(this.heatRows);
    this.refreshVisibleHeat();
    this.scheduleTileMetrics();
    // Read the icon's state back off the tree rather than trusting the emitted
    // flag, so a tree with nothing to open can't be left claiming "Collapse All".
    this.syncHeatExpandIcon();
  }

  // Keep the header's expand/collapse icon tied to what the tree is actually
  // showing. icon-chart renders that icon from its own `expandChart` flag and
  // the only way in from outside is filterService.expendTree, keyed by
  // item.config.widgetKey — the same channel action-grid uses when its rows are
  // opened by hand. Called after every route that can change the expansion:
  // the +/- folds, the icon itself, a rebuild (which collapses the tree), and a
  // fullscreen change (icon-chart clears `expandChart` in closeFullscreen(),
  // which would otherwise leave the icon reading "Expand All" over a tree that
  // is still fully open).
  private syncHeatExpandIcon(): void {
    // Matches the grid widgets' convention: any open branch means the useful
    // next action is "Collapse All".
    let anyExpanded = false;
    const walk = (nodes: HeatRow[]) => {
      nodes.forEach((n) => {
        if (n.hasChildren && n.expanded) anyExpanded = true;
        if (n.children.length) walk(n.children);
      });
    };
    walk(this.heatRows);

    this.heatExpanded = anyExpanded;
    // Always pushed, never guarded on a change of `heatExpanded`: icon-chart
    // resets its own flag behind our back, so re-asserting is the point.
    this.filterService.expendTree.next({
      key: this.heatItem.config.widgetKey,
      value: anyExpanded,
    });
  }

  /**
   * Flat record list behind the heat map, for "Export as Excel".
   *
   * The rows the panel is actually drawn from, so an open selection narrows the
   * export the same way it narrows the tiles — an export that disagrees with the
   * screen it was taken from is worse than no export. Still a stable reference
   * either way (viewData or previewData, never a fresh array), which matters: this
   * getter feeds icon-chart's [data], and a new identity every change-detection pass
   * re-fires its ngOnChanges and leaks a resize listener each time.
   */
  get heatExcelRows(): any[] {
    return this.widgetRows("heat");
  }

  // ================================================== grid widget actions
  // The grid carries the heat map's full hierarchy now, so expand-all recurses
  // the whole tree the way onHeatExpandToggle() does — a flat pass over the top
  // rows would only open the first level.
  onGridExpandToggle(expanded: any): void {
    const open = !!expanded;
    const walk = (rows: GridRow[]) => {
      rows.forEach((r) => {
        if (r.hasChildren) r.expanded = open;
        if (r.children.length) walk(r.children);
      });
    };
    walk(this.gridRows);
    this.refreshVisibleGrid();
    // Read the icon's state back off the tree rather than trusting the emitted
    // flag, so a tree with nothing to open can't be left claiming "Collapse All".
    this.syncGridExpandIcon();
  }

  // Same channel and convention as syncHeatExpandIcon(): any open group at any
  // depth means the useful next action is "Collapse All".
  private syncGridExpandIcon(): void {
    let anyExpanded = false;
    const walk = (rows: GridRow[]) => {
      rows.forEach((r) => {
        if (r.hasChildren && r.expanded) anyExpanded = true;
        if (r.children.length) walk(r.children);
      });
    };
    walk(this.gridRows);

    this.gridExpanded = anyExpanded;
    this.filterService.expendTree.next({
      key: this.gridItem.config.widgetKey,
      value: anyExpanded,
    });
  }

  // Grid rows flattened parents-before-children, for "Export as Excel". Every
  // level gets its own column (see gridExcelConfig), filled from the path each
  // row sits on — an indented single column would not survive the trip to a
  // spreadsheet, and a row's own label alone would not say which branch it came
  // from.
  //
  // Built once per grid rebuild rather than exposed as a getter: [data] is an
  // icon-chart input, and a getter would hand it a brand-new array on every
  // change-detection pass. Angular compares inputs by reference, so `data` would
  // read as changed every cycle and fire icon-chart's ngOnChanges every cycle —
  // which registers another window resize listener each time it runs. Those pile
  // up and drag the whole page down.
  private buildGridExcelRows(): void {
    const out: any[] = [];
    // `path` carries the ancestors' labels keyed by their own dimension, so each
    // exported row reads as a full coordinate rather than a bare leaf name.
    const walk = (rows: GridRow[], path: Record<string, string>) => {
      rows.forEach((r) => {
        // Excluded by the open selection, so it is not on screen — and an export
        // that disagrees with the screen it was taken from is worse than no export.
        if (r.hidden) return;
        const here = { ...path, [r.dim]: r.label };
        out.push({
          ...here,
          processing_status: `${r.pct.toFixed(2)}%`,
          expected: r.expected,
          sent: r.sent,
          inProcess: r.inProcess,
          notStarted: r.notStarted,
          delayed: r.delayed,
          interim: r.interim,
          failed: r.failed,
        });
        if (r.children.length) walk(r.children, here);
      });
    };
    walk(this.gridRows, {});
    this.gridExcelRows = out;
  }

  // The grid is the same tree as the heat map, over the same levels, so it is
  // mapped straight off the heat map's nodes instead of re-grouping the records:
  // HEAT_LEVELS is the one place the hierarchy is defined, and the two panels
  // cannot disagree about what a level is or where a branch ends. Every node
  // already carries the records beneath it (buildHeatmap attaches each record to
  // every node on its path), so a level's totals are just aggregate(node.rows).
  //
  // Runs after buildHeatmap() — applyFilters() calls them in that order.
  private buildGrid(): void {
    const toGridRow = (n: HeatRow, i: number): GridRow => ({
      label: n.label,
      // The DIMENSION, not the heat node's dim FLAG — the two share a spelling
      // here for historical reasons and mean different things. See GridRow.dimmed
      // for the highlight flag's own field.
      dim: n.dim,
      level: n.level,
      expanded: false,
      hasChildren: n.hasChildren,
      // Carried so a click in this panel produces the same `path:` selection a
      // click on the heat tree does, and both light up together.
      nodeId: n.id,
      rows: n.rows,
      dimmed: false,
      hidden: false,
      // The order the tree order (or the data) put this node in, so clearing the
      // grid's sort can restore it — see GridRow.ord.
      ord: i,
      ...this.aggregate(n.rows),
      children: n.children.map(toGridRow),
    });

    this.gridRows = this.heatRows.map(toGridRow);
    // A filter change rebuilds the tree, so the column the user sorted by has to be
    // re-applied — nothing emits sortChanged for a rebuild.
    this.sortGridTree();
    this.refreshVisibleGrid();
    // The figures toGridRow() just stamped are the whole branch's. An open selection
    // narrows them and this rebuild has thrown that narrowing away, so re-apply it —
    // and it is refreshGridFigures() that derives the export rows, so they are
    // always taken from the numbers actually on screen. With nothing open it does
    // the export and nothing else, which is the usual case for a filter change.
    this.refreshGridFigures();
    // Fresh groups start collapsed, so the header icon falls back to
    // "Expand All" instead of describing the rows that were just discarded.
    this.syncGridExpandIcon();
  }

  /**
   * Re-aggregate every grid row against the open selection, and rebuild the export
   * rows from the result.
   *
   * This is the grid's answer to a pick, and it is a stronger answer than the heat
   * map's. Both panels fade a branch the selection excludes (applyHighlight), but
   * fading was all the grid did: ctrl+click "In Process" on a KPI card and a row
   * still read 40 files when only 6 of them were in process. Pale and wrong at once,
   * and the numbers are the entire reason to look at this panel.
   *
   * `widgetRows` decides whether the figures narrow at all, which is where Qlik's
   * rule lands here: the panel the popup is anchored to keeps its FULL numbers.
   * Without that, picking one grid row would zero every other row and there would be
   * nothing left to build a second pick out of.
   *
   * A branch with nothing left in it is dropped outright (`hidden`), not left
   * sitting at zero: the panel is being asked "what does this narrow to", and a page
   * of greyed zero rows is an answer nobody reads. That is the difference between
   * filtering and highlighting, and highlighting alone was the complaint.
   *
   * The tree ITSELF is deliberately untouched — same nodes, same expansion, same
   * sort, only the membership of the flat list changes. Rebuilding it is
   * buildGrid()'s job and it collapses every group; doing that on a pick would shut
   * a tree the user had just opened, once per click through a multi-select.
   * Re-sorting is out for the same reason: the figures move, so a sort on one of
   * them would shuffle rows under the cursor mid-selection. And because the nodes
   * survive, un-picking brings the rows straight back with their expansion intact —
   * which a rebuild could not do.
   */
  private refreshGridFigures(): void {
    const rows = this.widgetRows("grid");
    // Identity membership, built once for the whole tree: every node's records are a
    // subset of viewData, so narrowing one is a lookup per record instead of another
    // run of the selection predicate over it. Null when the grid keeps its full
    // numbers, which skips the filtering altogether.
    const keep = rows === this.viewData ? null : new Set(rows);
    const walk = (gridRows: GridRow[]) => {
      gridRows.forEach((r) => {
        const mine = keep ? r.rows.filter((x) => keep.has(x)) : r.rows;
        // Only the count fields are replaced — aggregate() returns nothing else, so
        // `rows`, `children`, `expanded` and the highlight flags survive untouched.
        Object.assign(r, this.aggregate(mine));
        // Nothing under this branch survives the selection, so it leaves the grid
        // rather than sitting there at zero. Only ever true while the selection
        // belongs to another panel: `keep` is null when this one is the anchor, and
        // then every row stays and applyHighlight()'s dimming is the feedback
        // instead.
        r.hidden = !!keep && r.rows.length > 0 && mine.length === 0;
        if (r.children.length) walk(r.children);
      });
    };
    walk(this.gridRows);
    // The tree is unchanged but its MEMBERSHIP is not, so the flat list has to be
    // rebuilt — and the export after it, off the rows that survived.
    this.refreshVisibleGrid();
    this.buildGridExcelRows();
  }

  // Flatten the grid tree into the visible, indented row list (respecting
  // expansion) — the same shape refreshVisibleHeat() produces, which is what
  // lets one *ngFor render a tree of any depth.
  private refreshVisibleGrid(): void {
    const out: GridRow[] = [];
    const walk = (rows: GridRow[]) => {
      rows.forEach((r) => {
        // A branch the open selection excludes is dropped, subtree and all —
        // refreshGridFigures() sets the flag, and only ever for a selection made in
        // some OTHER panel. Skipping the recursion with it matters: a hidden parent
        // whose children were left in the list would put orphans at the top level.
        if (r.hidden) return;
        out.push(r);
        if (r.expanded && r.children.length) walk(r.children);
      });
    };
    walk(this.gridRows);
    this.visibleGridRows = out;
  }

  // ---- grid sorting --------------------------------------------------------
  /**
   * The column the grid is sorted by, or null for the order the tree was built in.
   *
   * Held here rather than read back off ag-Grid each time, because a filter change
   * rebuilds the tree without any sort event to hang the re-apply on (see
   * buildGrid()).
   */
  private gridSort: { field: keyof GridRow; dir: "asc" | "desc" } | null = null;

  /**
   * A header click, applied to the TREE rather than to the flat row list.
   *
   * Every node's children are reordered inside their own branch, so "Sent,
   * descending" reads as "biggest first at every level" and a child never leaves the
   * parent it is indented under. ag-Grid's own sort is a no-op that preserves the
   * order we hand it — see gridDefaultColDef.comparator.
   */
  onGridSortChanged(ev: any): void {
    const state: any[] = ev?.columnApi?.getColumnState?.() || [];
    const sorted = state.find((c) => !!c.sort);
    // The hierarchy column holds node labels; every other colId IS its field.
    const field = sorted
      ? sorted.colId === "tree"
        ? "label"
        : sorted.colId
      : "";
    this.gridSort =
      sorted && field ? { field, dir: sorted.sort === "desc" ? "desc" : "asc" } : null;
    this.sortGridTree();
    this.refreshVisibleGrid();
    // "Export as Excel" should hand over the order that is on screen, so the export
    // rows are rebuilt with it rather than keeping the order of the last rebuild.
    this.buildGridExcelRows();
    this.cdr.detectChanges();
  }

  /**
   * Reorder every branch by the current sort, in place.
   *
   * With no sort it puts each node's children back in build order (GridRow.ord),
   * which is what makes un-sorting a column free — no rebuild, so nothing the user
   * had open is closed. Labels compare as text, counts as numbers.
   */
  private sortGridTree(): void {
    const sort = this.gridSort;
    const sign = sort?.dir === "desc" ? -1 : 1;
    const compare = (a: GridRow, b: GridRow): number => {
      if (!sort) return a.ord - b.ord;
      const av: any = a[sort.field];
      const bv: any = b[sort.field];
      if (typeof av === "number" && typeof bv === "number") {
        return sign * (av - bv);
      }
      return sign * String(av ?? "").localeCompare(String(bv ?? ""));
    };
    const walk = (rows: GridRow[]) => {
      rows.sort(compare);
      rows.forEach((r) => walk(r.children));
    };
    walk(this.gridRows);
  }

  // ---------------------------------------------------------------- template
  /**
   * The Processing Status column's colour, from PCT_COLOR_BANDS.
   *
   * A band lookup rather than a chain of thresholds because the source is a
   * band list — matching its shape means a change to the production config is a
   * change to the array and nothing else. Out-of-range values (a rounding
   * artefact just over 100, a negative from bad data) take the nearest band
   * rather than falling through to no colour at all.
   */
  pctColor(pct: number): string {
    const v = Number(pct) || 0;
    const band = PCT_COLOR_BANDS.find((b) => v >= b.start && v <= b.end);
    if (band) return band.color;
    return v > PCT_COLOR_BANDS[PCT_COLOR_BANDS.length - 1].end
      ? PCT_COLOR_BANDS[PCT_COLOR_BANDS.length - 1].color
      : PCT_COLOR_BANDS[0].color;
  }

  toggleHeat(row: HeatRow): void {
    row.expanded = !row.expanded;
    this.refreshVisibleHeat();
    // A fold changes the tree column's width, so the tiles are about to be
    // re-laid out and their count font has to re-fit — measured after the browser
    // has applied the new width, not against the old one.
    this.scheduleTileMetrics();
    // Opening or closing a branch by hand moves the header icon with it.
    this.syncHeatExpandIcon();
  }
  toggleGrid(row: GridRow): void {
    row.expanded = !row.expanded;
    this.refreshVisibleGrid();
    // Opening or closing a group by hand moves the header icon with it.
    this.syncGridExpandIcon();
  }

  // ------------------------------------------------- ag-Grid render + events
  /**
   * The tree column's cell: the row indented to its level, behind the same +/-
   * fold the heat map uses.
   *
   * A renderer function (HTML) rather than an Angular component so the markup
   * stays beside the table markup it replaces. That HTML is set as innerHTML by
   * ag-Grid, so `label` — which is record data — is escaped rather than parsed.
   *
   * The label ellipsizes at the column's width (.dpm-tree-cell > span) and carries
   * its full text as a `title`, so a name the column had to clip is still readable
   * on hover. Unconditional here, unlike the heat map's onTreeLabelHover(): this
   * cell is innerHTML rather than a template, so there is no element to hang a
   * mouseenter on that could test scrollWidth first — and a tooltip repeating a
   * label that happens to fit costs nothing, where a clipped name with no tooltip
   * is unreadable.
   */
  private gridTreeCell(row: GridRow): string {
    if (!row) return "";
    // A childless row holds the fold's place rather than closing the gap — the same
    // reason the heat map's tree does it: without the spacer a leaf's label starts
    // 26px left of its own parent's and the levels stop reading as nested.
    const fold = row.hasChildren
      ? `<button type="button" class="fold${
          row.expanded ? "" : " collapsed"
        }">${row.expanded ? "–" : "+"}</button>`
      : `<span class="fold-spacer"></span>`;
    const label = this.escapeHtml(row.label);
    return (
      `<span class="dpm-tree-cell" style="padding-left:${
        10 + row.level * 18
      }px">${fold}` +
      `<span class="${
        row.level === 0 ? "lvl0" : ""
      }" title="${label}">${label}</span></span>`
    );
  }

  /**
   * ag-Grid's stock header with this screen's status dot in front of the label,
   * so a count column still wears the colour of the status it counts.
   *
   * The full set of `ref` elements is kept even though this grid neither sorts
   * nor filters — they are what the default header component binds to, and a
   * template missing them is a template it cannot wire up.
   */
  private dotHeaderTemplate(color: string): string {
    return `
      <div class="ag-cell-label-container" role="presentation">
        <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button" aria-hidden="true"></span>
        <div ref="eLabel" class="ag-header-cell-label" role="presentation">
          <span class="hd" style="background:${color}"></span>
          <span ref="eText" class="ag-header-cell-text"></span>
          <span ref="eFilter" class="ag-header-icon ag-header-label-icon ag-filter-icon" aria-hidden="true"></span>
          <span ref="eSortOrder" class="ag-header-icon ag-header-label-icon ag-sort-order" aria-hidden="true"></span>
          <span ref="eSortAsc" class="ag-header-icon ag-header-label-icon ag-sort-ascending-icon" aria-hidden="true"></span>
          <span ref="eSortDesc" class="ag-header-icon ag-header-label-icon ag-sort-descending-icon" aria-hidden="true"></span>
          <span ref="eSortNone" class="ag-header-icon ag-header-label-icon ag-sort-none-icon" aria-hidden="true"></span>
        </div>
      </div>`;
  }

  /** Record text on its way into a cell renderer's HTML. */
  private escapeHtml(v: any): string {
    return this.n(v)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  /**
   * The fold folds; everything else in the row cross-filters that branch.
   *
   * The fold keeps the plain click it has always had — it is the panel's only way
   * to open a branch. Anywhere else in the row behaves exactly like the heat map's
   * tree above it: ctrl/cmd+click picks that branch into the grid panel's
   * selection, a plain click does nothing. Both panels are the same tree over the
   * same levels, so both produce the same selection.
   */
  onGridCellClicked(ev: CellClickedEvent): void {
    const target = ev.event?.target as HTMLElement | null;
    const row = ev.data as GridRow;
    if (target && target.closest(".fold")) {
      // Kept stopping propagation for the same reason the heat tree's fold does:
      // the document handler closes the open filter-badge dropdown and the date
      // popup, and folding a branch is not a reason to close either. It no longer
      // has an open SELECTION to protect — onDocumentClick stopped confirming — but
      // the dropdown half of that handler is still live.
      (ev.event as MouseEvent | null)?.stopPropagation();
      this.toggleGrid(row);
      // The fold happened inside an ag-Grid event, so nothing has told Angular
      // the row list changed — [rowData] needs a pass to reach the grid.
      this.cdr.detectChanges();
      return;
    }
    if (!row) return;
    // ag-Grid hands the raw DOM event through, so the modifier reads the same
    // way it does on a plain Angular (click).
    this.onGridRowClick(row, (ev.event as MouseEvent) || new MouseEvent("click"));
  }

  // ---------------------------------------------------- heat cell interaction
  // Which indicator badges a set of records lights. Called per status band from
  // cellSegments() with that status's records only, so a badge names the status it
  // is drawn on. INTERIM is deliberately excluded — it is drawn as the tile
  // outline, so cellInterim() picks it up instead of it appearing twice.
  private cellIndicators(rows: any[]): CellBadge[] {
    if (!rows?.length) return [];
    // LEGEND_INDICATORS, not INDICATORS: a badge a tile can draw has to be one the
    // legend names, or it cannot be explained and cannot be switched off.
    //
    // The four timing indicators ARE in that list, and the extract feed sets one of
    // them on nearly every record — so a band routinely carries a timing badge
    // beside its trend. That is what the legend now promises, and it is affordable
    // because badgesCollapsed() folds a set the band has no room for into the single
    // ⋯ disc rather than letting the icons bury the count.
    return LEGEND_INDICATORS.filter(
      (ind) => ind.name !== BORDER_INDICATOR && rows.some((r) => ind.test(r))
    ).map((ind) => ({ name: ind.name, url: ind.url, label: ind.label }));
  }

  /** True when any record in the cell trips the border indicator's test. */
  private cellInterim(rows: any[]): boolean {
    if (!rows?.length || !this.BORDER_DEF) return false;
    const test = this.BORDER_DEF.test;
    return rows.some((r) => test(r));
  }

  /**
   * Whether a tile responds at all — to hover, to a single click and to a double
   * click alike. All three are gated together, and on the same level, because
   * that is how the reference widget is configured: itemAccessorsMouseover,
   * itemAccessorsSingleClick and itemAccessorsDoubleClick are one and the same
   * list of file-type accessors (dpm/config.ts, lines ~38-42), so an aggregate
   * row appears in none of them.
   *
   * `hasChildren` is this tree's reading of that level. In the library the three
   * interactions all work off a cell's `details` map, and only the row built from
   * a record's full accessor path is given one: aggregate4Ancestors() fills
   * `details` for a parent solely when itemAccessorsMouseover names that parent's
   * dimension (status-overview.ts line ~374), with no `|| recordAccessor`
   * fallback like the leaf branch has (line ~263) — and DPM leaves that config
   * unset. So parents hold no details, and a row with no children is exactly this
   * tree's full-path row, including where a branch ends early because a level was
   * blank.
   *
   * The rest is the plain "is there anything here" test: an empty slot, or a cell
   * whose status the legend has switched off, has nothing to show.
   */
  // Both views gate on the last level, and on nothing else: a tile above it is
  // an aggregate of the levels underneath, so a popup, a hover panel or a
  // printed count there would report a number that belongs to the rows below
  // rather than to the tile it is drawn on. Selecting a parent in the History
  // tree therefore shows its bands without any of the three — pick a last-level
  // node to read the detail.
  private isCellInteractive(cell: HeatCell, node: HeatRow): boolean {
    return !!node && !node.hasChildren && !!cell?.rows?.length && !cell.hidden;
  }

  // ---- hover tooltip -------------------------------------------------------
  // Hovering an interactive tile opens the reused Status Overview cell panel
  // against that tile.
  onCellEnter(cell: HeatCell, node: HeatRow, ev: MouseEvent): void {
    if (!this.isCellInteractive(cell, node)) {
      this.hideCellTip();
      return;
    }
    // The cell element is the anchor, exactly as updateTooltip() hands
    // duc-tooltip `anchor: e.target`. Captured before the render pass below,
    // since currentTarget is only meaningful during dispatch.
    const anchor = ev.currentTarget as HTMLElement;
    this.tipData = this.buildCellPayload(cell, node);
    // Render off-screen first so the panel can be measured before it is placed —
    // its height depends on how many files the cell holds, and every placement
    // decision needs it. duc-tooltip does the same thing, parking itself at
    // -1000/-1000 for one change-detection pass. Both passes run inside this one
    // event handler, so the browser never paints the parked position.
    this.tipPos = { left: -9999, top: -9999 };
    this.cdr.detectChanges();
    this.placeCellTip(anchor);
  }

  hideCellTip(): void {
    if (!this.tipData) return;
    this.tipData = null;
    this.cdr.detectChanges();
  }

  // ---- indicator overflow --------------------------------------------------
  /**
   * True when a band has more badges than it has room for, and shows the ⋯ disc
   * in their place.
   *
   * Space, not a count: a band carries every badge it can fit side by side, and
   * only collapses when it cannot. The room is its share of the measured tile (see
   * bandBadgeCap), so a one-status tile in a wide strip column shows three badges
   * where the same status sharing a narrow calendar day with two others shows the
   * disc — and the answer moves with the viewport rather than being fixed.
   *
   * All-or-nothing on purpose: a band that cannot hold the set shows the disc
   * alone rather than as many badges as fit plus a disc. Spending one of the few
   * slots on the disc leaves so little room for real badges that the remainder is
   * not a useful summary, and "some of them, then a marker" reads as if the
   * visible ones were the important ones.
   */
  badgesCollapsed(seg: CellSegment, cell: HeatCell): boolean {
    return (
      seg.shownIndicators.length > this.bandBadgeCap(cell.shownSegments.length)
    );
  }

  /**
   * Unstack the ⋯ disc: the band's indicators as a column of icons rising out of
   * the badge.
   *
   * Icons only, in the same white discs the tile wears, so the popover reads as
   * the badges the disc stands in for rather than as a list about them — the
   * indicator legend above the heat map is where an icon is named, and repeating
   * the names here made a tile's overflow the wordiest thing on the screen.
   *
   * Placed off the disc's own rect rather than the band's and centred on it, so
   * it stays with the badge it belongs to wherever on the page that tile is.
   * Kept simpler than placeCellTip(): one axis, and it only has to drop below the
   * disc when the column would not clear the top of the viewport.
   */
  showBadgePop(seg: CellSegment, ev: MouseEvent): void {
    const el = ev.currentTarget as HTMLElement;
    if (!el || !seg.shownIndicators.length) return;
    const r = el.getBoundingClientRect();
    const EDGE = 8;
    // One disc per indicator, stacked — so the column is a badge wide and as tall
    // as the set, plus .badge-pop's own padding and gaps.
    const n = seg.shownIndicators.length;
    const w = this.BADGE_PX + this.BADGE_POP_PAD_PX * 2;
    const h =
      n * this.BADGE_PX +
      (n - 1) * this.BADGE_POP_GAP_PX +
      this.BADGE_POP_PAD_PX * 2;

    // Upwards out of the disc; below it only when there is no room above.
    let top = r.top - h - this.BADGE_POP_OFFSET_PX;
    if (top < EDGE) {
      top = Math.min(
        r.bottom + this.BADGE_POP_OFFSET_PX,
        window.innerHeight - h - EDGE
      );
    }

    this.badgePop = {
      indicators: seg.shownIndicators,
      left: Math.max(
        EDGE,
        Math.min(r.left + r.width / 2 - w / 2, window.innerWidth - w - EDGE)
      ),
      top: Math.max(EDGE, top),
    };
    this.cdr.detectChanges();
  }

  hideBadgePop(): void {
    if (!this.badgePop) return;
    this.badgePop = null;
    this.cdr.detectChanges();
  }

  /**
   * Place the panel against the tile, mirroring ducharts' duc-tooltip.render()
   * (node_modules/@dataunveil/ducharts, components/tooltip) — the geometry the
   * reference StatusOverview gets for free by handing it `container` + `anchor`:
   *
   *  - anchored to the cell box, not the cursor;
   *  - vertical priority below → above → centred, horizontal right → left →
   *    centred, so the resting placement is below-right of the tile;
   *  - each axis falls back to the library's fitLinearSegment() when no
   *    placement fits, which slides the panel along that axis to stay inside
   *    the container;
   *  - whichever corner ends up on the tile is squared off, so the panel reads
   *    as hanging off that tile (duc-tooltip's caret corner).
   *
   * Placed once per tile entry rather than tracked on mousemove — the panel
   * belongs to the tile, and following the pointer would run change detection
   * over the whole heat map on every mouse event.
   */
  private placeCellTip(anchor: HTMLElement): void {
    const el = this.cellTipEl?.nativeElement;
    if (!el || !anchor) return;

    const c = this.tipContainer(anchor);
    const a = anchor.getBoundingClientRect();
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const m = this.TIP_MARGIN;

    // Same tests as the library's canFit, read in viewport coordinates because
    // .cell-tip is position: fixed. The two "centre" tests measure from the
    // anchor's top / left edge rather than its middle — a library quirk kept as
    // is, so a placement decision here matches one over there.
    const fits = {
      below: a.bottom + h + m < c.bottom,
      above: a.top - h - m > c.top,
      vCenter: a.top - h / 2 > c.top && a.top + h / 2 < c.bottom,
      right: a.right + w + m < c.right,
      left: a.left - w - m > c.left,
      hCenter: a.left - w / 2 > c.left && a.left + w / 2 < c.right,
    };
    const vertical = fits.below
      ? "below"
      : fits.above
      ? "above"
      : fits.vCenter
      ? "center"
      : "";
    const horizontal = fits.right
      ? "right"
      : fits.left
      ? "left"
      : fits.hCenter
      ? "center"
      : "";

    let top: number;
    if (vertical === "below") top = a.bottom + m;
    else if (vertical === "above") top = a.top - h - m;
    else if (vertical === "center") top = a.top - h / 2;
    else
      top =
        c.top +
        this.fitSegment(c.bottom - c.top, h, a.top + a.height / 2 - c.top, m);

    let left: number;
    if (horizontal === "right") left = a.right + m;
    else if (horizontal === "left") left = a.left - w - m;
    else if (horizontal === "center") left = a.left - w / 2;
    else
      left =
        c.left +
        this.fitSegment(c.right - c.left, w, a.left + a.width / 2 - c.left, m);

    this.tipPos = { left, top };
    this.tipRadius = this.caretRadius(vertical, horizontal);
    this.cdr.detectChanges();
  }

  // The box the panel is kept inside. duc-tooltip clamps to the chart element,
  // which here is the widget's own article — reached from the tile rather than
  // through a second @ViewChild, so it resolves to whichever article the browser
  // has promoted when the panel is fullscreen. Intersected with the window
  // because that article grows with the tree inside .dpm-scroll and can run well
  // past the fold: clamping to a box that is off screen would place the panel
  // where it cannot be read.
  private tipContainer(anchor: HTMLElement): {
    left: number;
    top: number;
    right: number;
    bottom: number;
  } {
    const host = anchor.closest(".heat-fs") as HTMLElement | null;
    const r = host?.getBoundingClientRect();
    const EDGE = 8;
    return {
      left: Math.max(r?.left ?? 0, EDGE),
      top: Math.max(r?.top ?? 0, EDGE),
      right: Math.min(r?.right ?? window.innerWidth, window.innerWidth - EDGE),
      bottom: Math.min(
        r?.bottom ?? window.innerHeight,
        window.innerHeight - EDGE
      ),
    };
  }

  // ducharts' fitLinearSegment(): centre `segment` on `center` inside a
  // container that starts at 0, giving up the centring rather than the fit.
  private fitSegment(
    container: number,
    segment: number,
    center: number,
    margin: number
  ): number {
    if (center - segment / 2 - margin > 0 && center + segment / 2 + margin < container)
      return center - segment / 2;
    if (center - segment / 2 < 0) return 0;
    if (center + segment / 2 > container) return container - segment;
    return center - segment / 2;
  }

  // duc-tooltip's caretStyle(): square off the one corner that sits on the
  // anchor. A centred (or unplaceable) axis is not in the list, and then the
  // library leaves all four corners rounded — nothing is touching the tile for
  // the corner to point at.
  private caretRadius(vertical: string, horizontal: string): string {
    const corners = ["below-right", "below-left", "above-left", "above-right"];
    const radii = [
      this.TIP_RADIUS,
      this.TIP_RADIUS,
      this.TIP_RADIUS,
      this.TIP_RADIUS,
    ];
    const caret = corners.indexOf(`${vertical}-${horizontal}`);
    if (caret !== -1) radii[caret] = 0;
    return radii.map((r) => `${r}px`).join(" ");
  }

  // Single click -> comment popup. Delayed so a double click can cancel it.
  // Last level only, same gate as the hover panel — see isCellInteractive().
  onCellClick(cell: HeatCell, node: HeatRow): void {
    if (!this.isCellInteractive(cell, node)) return;
    // A dialog covering the cursor does not reliably fire mouseleave on the tile,
    // so retire the tooltip on the way into either popup.
    this.hideCellTip();
    if (this.clickTimer) clearTimeout(this.clickTimer);
    this.clickTimer = setTimeout(() => {
      this.clickTimer = null;
      this.openCommentModal(cell, node);
    }, 250);
  }

  // Double click -> control chart popup. Last level only, same gate as the hover
  // panel and the single click — see isCellInteractive(). The pending
  // single-click timer is cleared before that test, not after, so a double click
  // can never leave one behind to fire.
  onCellDblClick(cell: HeatCell, node: HeatRow): void {
    this.hideCellTip();
    if (this.clickTimer) {
      clearTimeout(this.clickTimer);
      this.clickTimer = null;
    }
    if (!this.isCellInteractive(cell, node)) return;
    this.openControlChart(cell, node);
  }

  /**
   * Build the ducharts-style cell object the reused popups expect: a `details`
   * map keyed by file name, plus xKey / status / colorScale.
   *
   * Each detail carries the whole record the API returned for it, not a chosen
   * handful of columns. Two reasons it has to be the whole thing:
   *
   *   - the comments component resolves its `conversation_key` by looking up
   *     config-named columns straight off the topic
   *     (comments.ts -> notes_selected_key[].api_resp_column_nm). Any column
   *     outside a fixed list silently fell back to `file_type`, so a note could
   *     be filed under the wrong key purely because the field was not copied
   *     across;
   *   - neither popup spreads a detail into a request — both read named fields
   *     and build their own payloads — so carrying every column costs a wider
   *     object and nothing else.
   *
   * The derived fields are layered on top of the spread and win, so everything
   * either popup reads by name keeps the normalised value it always had:
   * canonical statuses, a DD-MM-YY `period`, and the file_type_dim_key /
   * file_type_nm aliases that have no equivalent in the raw record.
   */
  private buildCellPayload(cell: HeatCell, node: HeatRow): any {
    const details: any = {};
    (cell.rows || []).forEach((r) => {
      const fileNm = this.n(r.file_nm) || `${node.label} ${this.n(r.file_type)}`;
      if (!details[fileNm]) {
        const idx = (Object.keys(details).length + 1).toString();
        details[fileNm] = {
          // Every column kpi/get-execution-details sent for this record.
          ...r,
          index: `File ${idx.padStart(3, "0")}`,
          // Canonical, so the tooltip's swatch resolves a fill for this status
          // the same way the tile behind it did.
          status: this.status(r),
          // NOT canonicalised, unlike `status` above — this is one of the fields a
          // note is keyed by, so it has to stay the spelling the row was stored
          // with. Filing under the legend's spelling instead would put these notes
          // in a bucket the production DEM widget — which keys off the raw value —
          // can never read, and vice versa. The swatch needs the canonical form;
          // the note key needs the stored one.
          file_status: this.n(r.file_status),
          file_type_key: r.file_type_key,
          file_type_dim_key: r.file_type_key,
          source_key: r.source_key,
          period: moment(this.day(r)).format("DD-MM-YY"),
          source_name: this.n(r.source_name),
          // dem/cell-info.ts and dem/modal.ts read `source_nm`; the extract
          // config's detailsAccessorDuplicates sets both spellings, so this does
          // too rather than betting on which one a popup happens to use.
          source_nm: this.n(r.source_name),
          file_type: this.n(r.file_type),
          file_type_nm: this.n(r.file_type),
          file_key: r.file_key,
          file_nm: this.n(r.file_nm),
          // The trend the tooltip prints its own arrow from — the derived column,
          // not a flag, so it has to be carried across with the record. See
          // stampTrends().
          sent_records_trend: this.n(r.sent_records_trend),
          on_hold_trend: this.n(r.on_hold_trend),
          earlier_time: r.earlier_time || undefined,
          // Falsy -> null, and refresh_no is 0 on most records. The comments
          // component posts `refresh_no: topic.refresh_no || undefined`, so a 0
          // is dropped from the insert and the row stores NULL; on the way back
          // it filters with `comment.refresh_no == topic.refresh_no`, and
          // `null == 0` is false in JS. That is the whole bug: a note added on
          // a refresh_no: 0 tile shows straight away (the insert response is
          // applied unfiltered) and is gone after a reload, because the filter
          // can never match it again. Normalising here makes the value we
          // compare with the value that was actually stored.
          //
          // The fix belongs on this side, not in comments.ts: making the insert
          // send a real 0 would match new notes and orphan every note already
          // stored against NULL.
          refresh_no: r.refresh_no || 0,
          // The record's own answer, defaulted only when it has none — the
          // reference reads it the same way (status-overview.detailNav /
          // controlChartPagePopup, `record.show_grid_flg || 'Y'`). It is not
          // cosmetic: it becomes the detail popup's hd_flg, and 'N' is what drops
          // that page's hidden widgets from the tab strip (page-popup ->
          // buildTabs). A hardcoded 'Y' answered for the record instead of asking
          // it, so every tile claimed the full tab set.
          show_grid_flg: r.show_grid_flg || "Y",
          // Several records can share one file name — the counts below are
          // their sum, and this is what they were summed from. Kept so a popup
          // reading the detail is never limited to the first record's columns.
          records: [],
          // Exactly the keys dem/cell-info.ts reads off `count`, and exactly the
          // ones dem/config.ts's detailsAccessorDuplicates builds. The four
          // summable columns start at 0 and are accumulated below; the timing
          // fields are single values off the record, and stay `undefined` rather
          // than 0 or '' because the tooltip shows each of those rows only
          // `*ngIf` the value is truthy — a 0 would print "EARLY: 0" on every
          // file that was not early.
          count: {
            sent_records: 0,
            failures: 0,
            errors: 0,
            on_hold: 0,
            early: r.early || undefined,
            delay: r.delay || undefined,
            time_sent: r.time_sent || undefined,
            earlier_time: r.earlier_time || undefined,
            delay_time: r.delay_time || undefined,
            on_time: r.on_time || undefined,
          },
        };
      }
      const f = details[fileNm];
      f.records.push(r);
      f.count.sent_records += this.num(r.sent_records);
      f.count.failures += this.num(r.failures);
      f.count.errors += this.num(r.errors);
      f.count.on_hold += this.num(r.on_hold);
    });

    return {
      xKey: new Date(cell.iso + "T00:00:00"),
      yKey: node.id,
      status: cell.status,
      colorScale: (s: string) => this.statusColor(s) || COLORS.expected,
      status_details: { [cell.status]: { details } },
      details,
      // Everything behind the tile, ungrouped and untouched — `details` is the
      // per-file rollup of exactly these records.
      records: cell.rows || [],
    };
  }

  private openCommentModal(cell: HeatCell, node: HeatRow): void {
    const payload = this.buildCellPayload(cell, node);
    const fileNames = Object.keys(payload.details || {});
    if (!fileNames.length) return;
    this.seedFilterState();
    // The modal keys its notes off the first detail entry only (modal.ts builds
    // its topic from Object.entries(details)[0]), so those are the only records
    // whose notes this popup can add to or remove — and the only ones whose
    // NOTES badge may change when it closes.
    const affected: any[] = payload.details[fileNames[0]].records || [];
    const ref = this.dialog.open(DemCommentModal, {
      disableClose: true,
      autoFocus: false,
      panelClass: "status-comment-dialog",
      data: { data: payload, item: this.commentItem, dateFormat: "DD-MM-YY" },
    });
    ref.afterClosed().subscribe(() => this.refreshCellNotes(affected));
  }

  /**
   * Double click -> the tile's DETAIL PAGE, rendered inside a popup.
   *
   * Mirrors status-overview's `popup_page` navigation
   * (dbltoggleModal -> controlChartPagePopup), not its `popup` one: the popup is
   * PagePopupComponent, which fetches the configured page and mounts each of its
   * content widgets as a tab (Control Chart / File Details / ...), rather than
   * the single ControlChartDialogComponent. So the screen shows the same detail
   * page the production DEM widget shows, with the same widgets and the same
   * per-widget requests, instead of a re-implemented chart.
   *
   * PagePopupComponent runs as a fully ISOLATED page session — it re-provides
   * FilterService / WidgetService / DashboardService / PageDataService in its own
   * injector — so the state it builds cannot reach this screen's services. That
   * is what `parentFilterState` is for: a copy of the context this screen fetched
   * its own data with, which the popup seeds its private FilterService from. It
   * overrides page_key and dashboard_key with the ones the page it loaded
   * actually has (buildPopupBaseQuery), so this screen's minted static page key
   * never reaches the detail page's requests.
   *
   * Nothing is written to sessionStorage, exactly as in the reference: all of it
   * travels through MAT_DIALOG_DATA.
   */
  private openControlChart(cell: HeatCell, node: HeatRow): void {
    const payload = this.buildCellPayload(cell, node);
    if (!payload.details || !Object.keys(payload.details).length) return;
    // No page pointer, nothing to open. A static screen has no widget config to
    // read one from, so it is stated in DEM_DETAIL_PAGE — see that constant for
    // the fields and where to copy them from.
    if (!isDetailPageConfigured(DEM_DETAIL_PAGE)) {
      console.warn(
        "DEM-Static: no detail page configured — set DEM_DETAIL_PAGE (dpm-static.service.ts) " +
          "to the production DEM widget's nav_configuration values to enable the control-chart popup."
      );
      return;
    }
    this.seedFilterState();

    const navData = this.buildCcNavData(payload);

    // The page-config pointer, in the shape PagePopupComponent reads it
    // (`navDetailInfo`) — the reference builds the identical object out of
    // nav_configuration.controls[0].
    const nav = {
      dbJSON: DEM_DETAIL_PAGE.dbJSON,
      s3JSON: DEM_DETAIL_PAGE.s3JSON,
      pageName: DEM_DETAIL_PAGE.pageName,
      isDashboardLevel: DEM_DETAIL_PAGE.isDashboardLevel,
      isNavSelected: DEM_DETAIL_PAGE.isNavSelected,
      navpageName: DEM_DETAIL_PAGE.navpageName,
      navpageNameLeft: DEM_DETAIL_PAGE.navpageNameLeft,
      navpageNameRight: DEM_DETAIL_PAGE.navpageNameRight,
    };

    // Label parts the popup composes its title from when the page is
    // nav-selected, and the `f_q` label an "open the full page" jump would carry.
    // Trailing separators included, because that is how they are concatenated
    // (page-popup -> loadPageConfig / navigateToDetailPage). node.label is the
    // deepest tree label, which is what the reference takes off record.yKey.
    const header = {
      cycle_dt: navData.cycle_dt
        ? moment(navData.cycle_dt, "DD-MM-YY").format("DD MMM ") + " "
        : "",
      file_type_nm: (node.label || "") + " - ",
      source_name: (navData.source_name || "") + " - ",
    };

    // The clicked tile, as request parameters. Unlike the control-chart dialog's
    // payload, this one reaches EVERY widget on the detail page: the popup merges
    // it into its private baseQuery and keeps it there (filterService
    // extraNavPayload), so it is the whole of what tells that page which tile was
    // opened. Hence the tile's identity in full — the source and file type by
    // key, and the day — where the dialog needed only what its own chart request
    // took.
    //
    // hd_flg IS here, and is not a filter: PagePopupComponent reads it to decide
    // whether the page's hidden widgets get tabs (buildTabs), the same thing
    // page navigation uses it for in the URL.
    const extraPayload: any = {
      day_of_the_week: navData.day_of_the_week,
      source_key: navData.source_key,
      refresh_no: navData.refresh_no||0,
      // Both spellings of the source, because the detail page is sent both: the
      // key scopes the query, the name is what its header and its own
      // source-scoped widgets read.
      source_nm: navData.source_nm || navData.source_name,
      file_type_dim_key: navData.file_type_dim_key,
      // The file itself, by key and by name. file_key is the FIRST file's — the
      // reference has the same asymmetry, because navData spreads detail #1 while
      // file_nm is the comma-joined list of every file under the tile — so a
      // multi-file tile names them all and keys the first. Left as the reference
      // has it rather than "fixed" here: the detail page has always been given
      // this pair.
      file_key: navData.file_key,
      file_nm: navData.file_nm,
      // Re-formatted, not passed through. This screen holds `period` as DD-MM-YY
      // — the notes popup and the tooltip are given that spelling and key off it
      // — but the production DEM detail holds it as yyyy-MM-DD (its cycle_dt is
      // formatted with widgetConfigs.dem.dateFormat before ducharts copies it
      // into period), and yyyy-MM-DD is therefore the shape the detail page's
      // widgets have always been sent. Converting here rather than changing
      // `period` keeps the notes keys as they are.
      cycle_dt: navData.cycle_dt
        ? moment(navData.cycle_dt, "DD-MM-YY").format("YYYY-MM-DD")
        : navData.cycle_dt,
      hd_flg: navData.show_grid_flg || "Y",
    };

    const heading = this.buildCcHeading(node, navData);

    // Lazy import, for the reason the reference gives: PagePopupComponent pulls
    // in WidgetModule, and importing it statically from a dashboard component
    // drags the whole widget graph into this chunk.
    import("../../../widget/status-overview/page-popup/page-popup.component")
      .then(({ PagePopupComponent }) => {
        this.dialog.open(PagePopupComponent, {
          disableClose: false,
          closeOnNavigation: false,
          panelClass: "page-popup-dialog",
          width: "92vw",
          maxWidth: "95vw",
          height: "88vh",
          data: {
            heading,
            navDetailInfo: nav,
            navHeaderData: header,
            navConfig: DEM_DETAIL_PAGE,
            selectConfig: sessionStorage.getItem("selectConfig") || "S3",
            extraPayload,
            parentFilterState: this.popupFilterState(navData),
            keys: {
              clientKey: this.clientKey,
              moduleKey: this.moduleKey,
              dashboardKey: this.dashboardKey,
              moduleName: this.moduleName,
            },
          },
        });
      })
      .catch((err) => console.error("DEM-Static: failed to open page popup", err));
  }

  /**
   * A copy of this screen's filter context for the popup's isolated
   * FilterService to start from — the static equivalent of the reference's
   * `parentFilterState`.
   *
   * Copied, never shared: the popup mutates what it is given (it rebuilds
   * baseQuery, sets day_of_the_week from its own Timecycle dropdown), and a
   * shared reference would let that reach the tiles behind it. The reference
   * clones with lodash; nothing under static/ imports lodash, and every value
   * here is JSON-safe (strings, numbers, arrays of those — see
   * seedFilterState), so a JSON round trip is the same guarantee without the
   * import. It drops undefined-valued keys, which is what a request body does
   * with them anyway.
   *
   * The slices with nothing behind them on this screen are stated as empty
   * rather than left out: seedIsolatedFilterState() assigns each one, so an
   * absent key would put `undefined` where the popup's widgets expect a
   * collection.
   */
  private popupFilterState(navData: any): any {
    const fs: any = this.filterService;
    const suffix = this.brandTableSuffix();
    return {
      baseQuery: {
        ...JSON.parse(JSON.stringify(fs.baseQuery || {})),
        // Two fields the detail page has always been sent that seedFilterState()
        // does not put in this screen's own baseQuery, because on a dynamic page
        // they come from the filter widget (FilterService.setFilterValue), and
        // the popup renders no filter widget — it emits this baseQuery once and
        // that is the whole context its widgets ever see. Stated here rather than
        // in seedFilterState() so this screen's own execution call, which works
        // without them, keeps the payload it has today.
        time_cycle: this.selectedCycle,
        // Only when the portal actually has one: an explicit undefined would
        // still be a key, and JSON.stringify would drop it anyway — but an empty
        // string would not, and would read as "no suffix" rather than "unset".
        ...(suffix ? { tbl_name_suffix: suffix } : {}),
      },
      // No filter widget on this screen, so there are no badges and no filter
      // model to hand over — the popup does not render one either.
      badges: [],
      model: {},
      previousModel: {},
      duplicateParameter: {},
      // The popup assigns both onto its private FilterService, and its widgets
      // read them there as well as out of baseQuery, so they have to agree with
      // what baseQuery above says.
      reportType: fs.report_type || DPM_REPORT_TYP.live,
      timeCycle: this.selectedCycle,
      dayOfWeek: navData.day_of_the_week,
      bufferDays: fs.buffer_days,
      startDate: this.fromDate,
      endDate: this.toDate,
      sourceKey: navData.source_key,
    };
  }

  /**
   * The selected brand's table suffix, read exactly as FilterService reads it
   * (setFilterValue -> the two `tbl_name_suffix` session entries).
   *
   * It is in every dynamic page's baseQuery and therefore in every request the
   * detail page makes, but it reaches that baseQuery through the filter widget,
   * which this screen does not have. Both entries hold the brand-selection object
   * the dashboard wrote (dashboard.component.gettingDatafromPopup), not the
   * suffix itself, so the field has to be read out of it.
   */
  private brandTableSuffix(): any {
    const read = (key: string): any => {
      try {
        const raw = sessionStorage.getItem(key);
        return raw ? JSON.parse(raw)?.tbl_name_suffix : undefined;
      } catch {
        // A malformed entry is a missing suffix, not a broken popup.
        return undefined;
      }
    };
    // FilterService's precedence: it reads the non-FRM entry first and then lets
    // the brand-selection one overwrite it, so that one wins where both exist.
    return read("tbl_name_suffix") ?? read("tbl_name_suffix_non_frm");
  }

  /**
   * Mirrors status-overview.buildNavData: the clicked tile as the popup's
   * builders want it. Everything openControlChart sends — extraPayload, the
   * heading, the navHeaderData labels — is read off this.
   *
   * `file_type_nm` is a LABEL here and nothing else: the detail page is scoped by
   * file_type_dim_key, and the production popup could not send the name even if
   * the page wanted it (a ducharts DEM detail carries `file_type`, never
   * `file_type_nm` — status-overview/dem/config.ts -> detailsAccessorDuplicates).
   * Keep it out of extraPayload, which is a request body.
   */
  private buildCcNavData(record: any): any {
    const keys = Object.keys(record.details);
    const detail: any = Object.values(record.details)[0];
    return {
      ...detail,
      file_type_dim_key: detail.file_type_key,
      cycle_dt: detail.period,
      file_type_nm: detail.file_type,
      file_nm: (keys.length > 1 ? keys.join(",") : keys[0]) || detail.file_nm,
      day_of_the_week: String(moment(detail.period, "DD-MM-YY").isoWeekday()),
    };
  }

  private buildCcHeading(node: HeatRow, navData: any): string {
    const source =
      navData.source_name || node.parentLabels[0] || node.label || "";
    const label = node.label || "";
    const date = navData.cycle_dt
      ? moment(navData.cycle_dt, "DD-MM-YY").format("DD MMM YYYY")
      : "";
    return [source, label, date].filter(Boolean).join(" • ");
  }

  // The reused comment / chart components read live context off FilterService +
  // DashboardService. This static screen has no filter widget, so seed the
  // minimum they need (a truthy baseQuery, the date range and the keys).
  private seedFilterState(): void {
    this.dashboardService.dashboardKey = this.dashboardKey;
    // Notes are this dashboard's own, not a widget's. pages.component sets this
    // per page, and the notes panel reads it to re-point itself at a different
    // dashboard's notes and narrow them to a card id — so anything a previously
    // visited dynamic page left behind would answer the Notes icon here.
    // Written rather than left alone for a second reason too: the panel reads it
    // with a bare Object.keys(), which throws on the undefined it starts as if
    // this screen is the first page of the session.
    this.dashboardService.dashboardDetailsOfNotes = {};
    if (!this.filterService.report_type) this.filterService.report_type = "D";
    if (!this.filterService.model) this.filterService.model = {};
    // The same context and selections the screen's own data was fetched with,
    // so a popup can never describe a cell under a different brand than the
    // screen behind it.
    const params = this.dynamicFilterParams();
    this.filterService.baseQuery = Object.assign(
      {},
      this.filterService.baseQuery || {},
      this.apiContext,
      params,
      {
        brand_key: params["brand_key"] ?? this.data?.[0]?.brand_dim_key,
        from_date: this.fromDate,
        to_date: this.toDate,
      }
    );
    // What a bookmark filed from this screen carries. The Bookmarks panel reads it
    // the moment the user clicks Add, so it is refreshed here — this method already
    // runs on every load and before every popup, i.e. everywhere the panel's other
    // context is settled. Published as its own thing rather than folded into
    // baseQuery above, because baseQuery is a request payload: everything in it is
    // posted to the backend by the popups that read it.
    this.dpmService.publishBookmarkState(this.dynamicFilterStates);
    // And remember them as user state, from the same snapshot. This method runs
    // on every load — and a dynamic filter change IS a load, since changing one
    // refetches (onFilterApplied) — so the state is written at exactly the
    // moments the selection can have moved, with no separate hook to keep in
    // step with it.
    this.userFilterState = dynamicFilterSnapshot(this.dynamicFilterStates);
    this.userActivityService.setStaticFilters(
      this.dashboardKey,
      this.userFilterState
    );
  }

  /**
   * After the notes popup closes, move the NOTES badge to match what it left
   * behind — without a reload.
   *
   * `records` is the set the popup was actually filing notes against, not every
   * record under the tile. A tile can hold several files, each with its own
   * notes, and the popup only ever edits the first; flagging them all meant
   * deleting that one note cleared the badge for files whose notes were never
   * touched (and adding one claimed notes they do not have).
   *
   * The re-derive then has to be tree-wide. buildHeatmap() attaches a record to
   * every node on its path, so the record just re-flagged is in an ancestor's
   * cell as well as the leaf's, and every band caches its own `indicators`
   * array. Re-deriving only the clicked cell left every tile above it drawing
   * the badge state from before the popup opened, until the next data load.
   *
   * Rebuilding each cell's bands rather than patching their badge arrays: the
   * badges are derived per status inside cellSegments(), so that function is the
   * one place the mapping lives, and re-running it cannot drift from the build.
   * It re-derives the counts too, to the same values — one pass over the same
   * records the badge test already needs.
   */
  private refreshCellNotes(records: any[]): void {
    if (!records?.length) return;
    const msgs = this.filterService.currentMsgOnDM;
    // The comments component parks its message list here when there are any and
    // the bare topic object when there are none — so "an array with something
    // in it" is the test for whether notes survived the popup.
    const hasComments = Array.isArray(msgs) && msgs.length > 0;
    records.forEach((r) => (r.comment_flg = hasComments ? "Y" : "N"));

    const walk = (nodes: HeatRow[]) => {
      nodes.forEach((n) => {
        n.cells.forEach((c) => (c.segments = this.cellSegments(c.rows)));
        if (n.children.length) walk(n.children);
      });
    };
    walk(this.heatRows);
    // Re-stamp shownSegments / shownIndicators from the new bands, so a NOTES
    // badge added here still respects the indicator legend if NOTES is switched
    // off — and so the tiles are pointed at the new segment objects at all.
    this.applyLegendVisibility();
    this.cdr.detectChanges();
  }

  previousPage(): void {
    this.navigationService.goBack();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.reqSubcription.forEach((sub) => sub.unsubscribe());
    // A drag in progress holds two window listeners; the guard above means this
    // only unhooks them and returns.
    this.onToolbarDragEnd();
    // Stop describing this screen to the Bookmarks panel — the next page is not it.
    this.dpmService.clearBookmarkState();
  }
}
